import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import theFoxMini from '../../TheFoxMini.png';
import algoIcon from '../../algo.webp';
import antifoxSmall from '../../antifox-small.png';
import foxMaskFallback from '../../foxmask.png';
import orheartImage from '../../orheart.webp';
import friendsImage from '../../friends.webp';
import voguImage from '../../vogu.webp';
import forbsImage from '../../forbs.webp';
import vanitImage from '../../vanit.webp';
import scissorsImage from '../../scissors.webp';
import { seedFromString } from '../../utils/seedFromString';
import { allBrands, takeInitialBrandSample } from '../../data/brandSamples';
import { activityEmojis as allActivityEmojis } from '../../data/activityEmojis';
import { OPPS_EMOJI_LIBRARY, getNextOppsEmojiBatch } from '../../data/oppsEmojis';
import { allPlaces, getVariedPlacesSample } from '../../data/placesSamples';
import { searchActivityPlusEmojisByQuery } from '../../utils/activityEmojiKeywordSearch';
import {
  HELLO_LOCAL_GIFS,
  MOOD_LOCAL_GIFS,
  getShuffledMoodAndHelloGifsAll,
} from '../../data/localGifLibraries';
import { SAMPLE_NICKNAMES } from '../../data/sampleNicknames';
import {
  searchBrandImages,
  searchPlaces as searchPlacesApi,
  searchSpiritGifs as searchSpiritGifsApi,
  persistSearchResultImage,
  applyUserPhotoFoxMask,
  assignPreviewAuraColor,
  previewAuraColor as previewAuraColorApi,
  attachFoxChatPosterApi,
} from '../../services/firebaseApiService';
import { storage, db, auth } from '../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  doc,
  updateDoc,
  setDoc,
  arrayUnion,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import {
  persistCollageImageBundle,
  persistImageUrlToStorage,
  buildPosterDoc,
  writeSharedPosterMirror,
  sanitizeForFirestore,
  stripUnmaskedPhotoForPublicShare,
} from '../../utils/collagePersistence';
import { appendCollageForFriendContact } from '../../utils/collageForFriendFirestore';
import { shareDailyVibeCheckGifWithPartners } from '../../services/messagingService';
import {
  computeMyNewMatches as computeMyNewMatchesApi,
  initiateMyVvtfMatch as initiateMyVvtfMatchApi,
} from '../../services/firebaseApiService';
import { applyFoxMaskToSavedPoster } from '../../utils/applyFoxMaskToSavedPoster';
import {
  readEmailSignupAttachSession,
  clearEmailSignupAttachSession,
} from '../../utils/emailSignupCollageAttach';
import { ensureBrowserDisplayablePhotoFile } from '../../utils/ensureBrowserDisplayablePhotoFile';
import { referFriendEmailValidationError } from '../../utils/berkeleyEmailAccess';
import {
  saveDailyVibeCheck,
  loadDailyVibeCheckForPeriod,
  loadDailyVibeChecksForMonth,
  readDailyVibeChatCache,
  readDailyVibeGreetingSeenAt,
  recordDailyVibeGreetingSeenAt,
  readDailyVibeMatchFound,
  readDailyVibeMatchIds,
  syncDailyVibeMatchIds,
  recordDailyVibeMatchFound,
  clearDailyVibeMatchFound,
  readDailyVibeCheckOutcome,
  recordDailyVibeCheckOutcome,
  clearDailyVibeCheckOutcome,
  readDailyVibeMatchEngineRan,
  recordDailyVibeMatchEngineRan,
  writeDailyVibeChatCache,
  getDailyVibePeriodKey,
  getNextDailyVibePeriodStart,
  resolveVibeCheckStreakDisplay,
  VIBE_CHECK_STREAK_MAX,
} from '../../utils/dailyVibeFirestore';
import AgeWheel, { AGE_WHEEL_DEFAULT } from '../AgeWheel';
import '../InlineSurvey.css';
import '../PersonalityChart.css';
import '../../styles/Messaging.css';
import '../../styles/FoxChat.css';
import { DEFAULT_FOX_CHAT_COPY } from './foxChatConfig';
import { FeedNicknameTile } from '../feed/FeedNicknameTile';
import { CollageDetailsLuvLegend } from '../feed/CollageDetailsLuvLegend';
import { resolveAuraRgb } from '../../utils/auraColor';
import { countCollageTileChanges, hasCollageTileChanges } from '../../utils/collageTileChanges';

/** Fallback vibe cloud when the user has no server-assigned `auraColor` yet. */
const FOX_CHAT_DEFAULT_AURA = { r: 227, g: 160, b: 27 };
/** Hold “recomputing” in place of the vibe ball after a color drift. */
const FOX_CHAT_AURA_RECOMPUTE_MS = 3000;
const FOX_CHAT_SAVE_REDIRECT_MS = 700;
/** Coalesce unselect + select (a typical tile swap) into one drift. */
const FOX_CHAT_AURA_DRIFT_DEBOUNCE_MS = 400;

/** Same tile slots `countCollageTileChanges` uses on the user-page SAVE path. */
function foxChatAuraTileSnapshot(answers, photoKey) {
  const acts = Array.isArray(answers?.activities) ? answers.activities : [];
  const slot = (i) => {
    const a = acts[i];
    if (a == null) return '';
    return typeof a === 'string' ? a.trim() : String(a.name || a.emoji || '').trim();
  };
  const gif = answers?.spirit_gif;
  const gifKey = gif
    ? String(gif.id || gif.gifUrl || gif.fullUrl || gif.stillUrl || '').trim()
    : '';
  const photo = typeof photoKey === 'string' ? photoKey : '';
  return {
    swags: (answers?.brands || []).slice(0, 3),
    places: (answers?.places || []).slice(0, 3),
    activityX: slot(0),
    activityY: slot(1),
    activityZ: slot(2),
    heartPhotoUrl: gifKey,
    userPhotoUrl: photo,
    partnerCutout: photo,
  };
}

/** Stable key for a tile snapshot so this session can reuse a color if they undo a swap. */
function foxChatAuraTileKey(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return '';
  const idOf = (tile) => {
    if (tile == null) return '';
    if (typeof tile === 'string') return tile.trim();
    if (typeof tile !== 'object') return String(tile);
    const id = typeof tile.id === 'string' ? tile.id.trim() : '';
    if (id) return id;
    const name = typeof tile.name === 'string' ? tile.name.trim() : '';
    if (name) return name;
    return typeof tile.imageUrl === 'string' ? tile.imageUrl.trim() : '';
  };
  const listKey = (list) => (Array.isArray(list) ? list : []).map(idOf).join('\0');
  return [
    listKey(snapshot.swags),
    listKey(snapshot.places),
    snapshot.activityX || '',
    snapshot.activityY || '',
    snapshot.activityZ || '',
    snapshot.heartPhotoUrl || '',
    snapshot.userPhotoUrl || '',
    snapshot.partnerCutout || '',
  ].join('|');
}

async function copyTextToClipboard(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

const PHOTO_PROCESSING_HINTS = [
  'sharpening scissors...',
  'picking a fox...',
  'warming up glue...',
  'cutting...',
  'slicing...',
  'dicing...',
  'aligning cutouts...',
  'sticking it together...'
];
const MAGAZINE_IMAGES = [voguImage, forbsImage, vanitImage];

const buildMagazinePair = () => {
  const shuffled = [...MAGAZINE_IMAGES].sort(() => Math.random() - 0.5);
  return {
    left: { src: shuffled[0], rot: (Math.random() * 20 - 10).toFixed(1), dx: (Math.random() * 16 - 8).toFixed(1) },
    right: { src: shuffled[1], rot: (Math.random() * 20 - 10).toFixed(1), dx: (Math.random() * 16 - 8).toFixed(1) },
  };
};

const buildScissorsOffset = () => ({
  left: { rot: (Math.random() * 60 - 30).toFixed(1), dx: (Math.random() * 30 - 15).toFixed(1), dy: (Math.random() * 20 - 10).toFixed(1) },
  right: { rot: (Math.random() * 60 - 30).toFixed(1), dx: (Math.random() * 30 - 15).toFixed(1), dy: (Math.random() * 20 - 10).toFixed(1) },
});

const isValidAge = (raw) => {
  const trimmed = String(raw ?? '').trim();
  if (!/^\d+$/.test(trimmed)) return false;
  const n = Number(trimmed);
  return n >= 18 && n <= 120;
};

const GENDER_OPTIONS = [
  { id: 'male', label: 'MALE' },
  { id: 'female', label: 'FEMALE' },
  { id: 'nb', label: 'NB' },
];

/* Religious emojis are excluded from random selection (matches InlineSurvey emoji step). */
const RELIGION_EMOJIS = ['🛐', '🧘', '⚛️', '🕉️', '✡️', '☸️', '☯️', '✝️', '☦️', '☪️', '☮️', '🕎', '🔯', '🪯'];

const getRandomEmojis = (count, exclude = []) => {
  const excludeSet = new Set([...RELIGION_EMOJIS, ...exclude]);
  const pool = allActivityEmojis.filter((e) => !excludeSet.has(e));
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

const getRandomMoodGifs = (count = 9) => {
  const shuffled = [...MOOD_LOCAL_GIFS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

const getRandomNicknames = (count = 3, exclude = []) => {
  const excludeSet = new Set(exclude);
  const pool = SAMPLE_NICKNAMES.filter((n) => !excludeSet.has(n));
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

const formatTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const KLIPY_PICKER_PAGE = 40;

function gifDedupeKey(g) {
  const u = (g && (g.fullUrl || g.gifUrl)) || '';
  return String(u).trim();
}

function mergeLocalThenKlipy(localGifs, remoteGifs) {
  const seen = new Set();
  const out = [];
  const push = (item) => {
    const k = gifDedupeKey(item);
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push(item);
  };
  (localGifs || []).forEach(push);
  (remoteGifs || []).forEach(push);
  return out;
}

function mergeKlipyThenLocal(remoteGifs, localGifs) {
  const seen = new Set();
  const out = [];
  const push = (item) => {
    const k = gifDedupeKey(item);
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push(item);
  };
  (remoteGifs || []).forEach(push);
  (localGifs || []).forEach(push);
  return out;
}

function FoxChatSurveySearchField({
  onSubmit,
  onRefresh,
  refreshMode = false,
  refreshing = false,
  onInputChange,
  onHasTextChange,
  placeholder,
  ariaLabel,
  submitDisabled = false,
  disabled = false,
  autoFocus = false,
}) {
  const inputRef = useRef(null);
  const [hasText, setHasText] = useState(false);
  const onHasTextChangeRef = useRef(onHasTextChange);
  onHasTextChangeRef.current = onHasTextChange;

  useEffect(() => {
    onHasTextChangeRef.current?.(false);
    return () => onHasTextChangeRef.current?.(false);
  }, []);

  useEffect(() => {
    if (disabled) inputRef.current?.blur();
  }, [disabled]);

  const handleInput = (e) => {
    if (disabled) return;
    const nextHasText = !!e.target.value.trim();
    setHasText(nextHasText);
    onHasTextChangeRef.current?.(nextHasText);
    onInputChange?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (disabled || submitDisabled) return;
    if (refreshMode) {
      onRefresh?.();
      return;
    }
    const query = (inputRef.current?.value ?? '').trim();
    if (!query) return;
    onSubmit(query);
  };

  return (
    <form
      className={`fox-chat-nickname-custom-form fox-chat-survey-search-form${disabled ? ' fox-chat-survey-search-form--disabled' : ''}`}
      onSubmit={handleSubmit}
    >
      <div className="fox-chat-input-wrap fox-chat-nickname-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="brand-search-input fox-chat-nickname-custom-input fox-chat-custom-entry-input"
          placeholder={placeholder}
          defaultValue=""
          onInput={handleInput}
          autoFocus={autoFocus && !disabled}
          disabled={disabled}
          aria-label={ariaLabel || placeholder}
        />
        <button
          type="submit"
          className={`messaging-send fox-chat-send${refreshMode ? ' fox-chat-send--refresh' : ''}${refreshMode && refreshing ? ' fox-chat-send--refreshing' : ''}`}
          disabled={(!refreshMode && !hasText) || submitDisabled || disabled}
          aria-label={refreshMode ? 'More results' : 'Search'}
          aria-busy={refreshMode && refreshing ? true : undefined}
        >
          {refreshMode ? (
            <svg
              key="refresh"
              className="fox-chat-send-icon fox-chat-send-icon--refresh"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 14.4A7.2 7.2 0 1 1 16.3 6.9L20 10.4" />
              <path d="M20 5.6v4.8h-4.8" />
            </svg>
          ) : (
            <svg
              key="search"
              className="fox-chat-send-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 19V5" />
              <path d="M5 12l7-7 7 7" />
            </svg>
          )}
        </button>
      </div>
    </form>
  );
}

function FoxChatSurveyCustomEntryField({
  defaultValue = '',
  onSubmit,
  onInputChange,
  placeholder,
  ariaLabel,
  maxLength,
  showActiveBorder = false,
  formClassName = 'fox-chat-nickname-custom-form',
  submitAriaLabel = 'Submit',
}) {
  const inputRef = useRef(null);
  const [submitArmed, setSubmitArmed] = useState(false);
  const [hasText, setHasText] = useState(!!String(defaultValue).trim());
  const [focused, setFocused] = useState(false);
  const activeBorder = showActiveBorder || focused;

  const handleInput = (e) => {
    setHasText(!!e.target.value.trim());
    setSubmitArmed(true);
    onInputChange?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = (inputRef.current?.value ?? '').trim();
    if (!value || !submitArmed) return;
    inputRef.current?.blur();
    setSubmitArmed(false);
    onSubmit(value);
  };

  return (
    <form className={formClassName} onSubmit={handleSubmit}>
      <div className="fox-chat-input-wrap fox-chat-nickname-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className={`brand-search-input fox-chat-nickname-custom-input fox-chat-custom-entry-input${
            activeBorder ? ' fox-chat-custom-entry-input--active' : ''
          }`}
          placeholder={placeholder}
          defaultValue={defaultValue}
          onInput={handleInput}
          onFocus={() => {
            setFocused(true);
            setSubmitArmed(true);
          }}
          onBlur={() => setFocused(false)}
          maxLength={maxLength}
          aria-label={ariaLabel || placeholder}
        />
        <button
          type="submit"
          className="messaging-send fox-chat-send"
          disabled={!hasText || !submitArmed}
          aria-label={submitAriaLabel}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 19V5" />
            <path d="M5 12l7-7 7 7" />
          </svg>
        </button>
      </div>
    </form>
  );
}

const buildFoxDailyCheckOpeningMessage = (messageIdPrefix, greetingText, timestamp) => ({
  id: `${messageIdPrefix}-fox-daily-check-open`,
  sender: 'fox',
  text: greetingText,
  timestamp: timestamp ?? new Date(),
  foxDailyCheckGreeting: true,
});

const resolveDailyCheckGreetingTimestamp = (
  userId,
  existingGreeting,
  { recordIfMissing = false } = {},
) => {
  if (userId) {
    const stored = readDailyVibeGreetingSeenAt(userId);
    if (stored) return new Date(stored);
    if (recordIfMissing) {
      const seenAt = recordDailyVibeGreetingSeenAt(userId);
      return new Date(seenAt);
    }
  }
  if (existingGreeting?.timestamp) {
    const ts = existingGreeting.timestamp instanceof Date
      ? existingGreeting.timestamp
      : new Date(existingGreeting.timestamp);
    if (userId && recordIfMissing) {
      recordDailyVibeGreetingSeenAt(userId, getDailyVibePeriodKey(), ts.getTime());
    }
    return ts;
  }
  return new Date();
};

const buildFoxDailyCheckComputingReplyMessage = (messageIdPrefix, replyText, timestamp) => ({
  id: `${messageIdPrefix}-fox-daily-check-computing`,
  sender: 'fox',
  text: replyText,
  timestamp: timestamp ?? new Date(),
  foxDailyCheckComputingReply: true,
});

const buildFoxDailyCheckMatchFoundMessage = (
  messageIdPrefix,
  matchFoundText,
  conversationId,
  timestamp,
) => ({
  id: `${messageIdPrefix}-fox-daily-check-match-found`,
  sender: 'fox',
  text: matchFoundText,
  timestamp: timestamp ?? new Date(),
  foxDailyCheckMatchFound: true,
  matchConversationId: conversationId || null,
});

const buildFoxDailyCheckGateMessage = (messageIdPrefix, gate, gateText, timestamp = null) => ({
  id: `${messageIdPrefix}-fox-daily-check-gate-${gate}`,
  sender: 'fox',
  text: gateText,
  timestamp: timestamp ?? new Date(),
  foxDailyCheckGate: gate,
});

const isFoxDailyCheckGateMessage = (msg) => Boolean(
  msg?.foxDailyCheckGate
  || (msg?.id && String(msg.id).includes('-fox-daily-check-gate-')),
);

const buildFoxDailyCheckNoMatchesMessage = (messageIdPrefix, text, timestamp = null) => ({
  id: `${messageIdPrefix}-fox-daily-check-no-matches`,
  sender: 'fox',
  text,
  timestamp: timestamp ?? new Date(),
  foxDailyCheckNoMatches: true,
});

const isFoxDailyCheckNoMatchesMessage = (msg) => Boolean(
  msg?.foxDailyCheckNoMatches
  || (msg?.id && String(msg.id).endsWith('-fox-daily-check-no-matches')),
);

/** Match-found info from the period cache, shaped for assembleDailyCheckThread. */
const readDailyCheckMatchFoundForThread = (userId) => {
  const found = userId ? readDailyVibeMatchFound(userId) : null;
  if (!found?.conversationId) return null;
  return {
    conversationId: found.conversationId,
    timestamp: new Date(found.foundAt || Date.now()),
  };
};

/** Terminal gate / no-matches reply from the period cache, shaped for assembleDailyCheckThread. */
const readDailyCheckOutcomeForThread = (userId) => {
  const outcome = userId ? readDailyVibeCheckOutcome(userId) : null;
  if (!outcome?.type) return null;
  return {
    ...outcome,
    timestamp: outcome.at ? new Date(outcome.at) : new Date(),
  };
};

const dailyCheckGifTimestamp = (userGifMessage) => {
  const ts = userGifMessage?.timestamp;
  return ts instanceof Date ? ts : new Date(ts || Date.now());
};

const dailyCheckComputingReplyTimestamp = (userGifMessage, fallbackTimestamp) => {
  if (fallbackTimestamp) {
    return fallbackTimestamp instanceof Date ? fallbackTimestamp : new Date(fallbackTimestamp);
  }
  return new Date(dailyCheckGifTimestamp(userGifMessage).getTime() + 1000);
};

/** Period key for a chat timestamp, or null when the date is missing/invalid. */
const dailyVibePeriodKeyForTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const ts = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(ts.getTime())) return null;
  return getDailyVibePeriodKey(ts);
};

const assembleDailyCheckThread = ({
  messageIdPrefix,
  greetingText,
  computingReplyText,
  userGifMessage,
  greetingTimestamp,
  computingReplyTimestamp,
  matchFoundText = null,
  matchFound = null,
  checkOutcome = null,
  gateTexts = null,
  noMatchesText = null,
}) => {
  const foxGreeting = buildFoxDailyCheckOpeningMessage(
    messageIdPrefix,
    greetingText,
    greetingTimestamp,
  );
  if (!userGifMessage) return [foxGreeting];
  const thread = [
    foxGreeting,
    userGifMessage,
    buildFoxDailyCheckComputingReplyMessage(
      messageIdPrefix,
      computingReplyText,
      dailyCheckComputingReplyTimestamp(userGifMessage, computingReplyTimestamp),
    ),
  ];
  if (matchFound?.conversationId && matchFoundText) {
    thread.push(buildFoxDailyCheckMatchFoundMessage(
      messageIdPrefix,
      matchFoundText,
      matchFound.conversationId,
      matchFound.timestamp,
    ));
  } else if (checkOutcome?.type === 'gate' && checkOutcome.gate === 'verify' && gateTexts?.verify) {
    /* Only the verify gate exists now; a stale cached 'age' outcome from the old
       pilot cap renders nothing so the match check can run again. */
    thread.push(buildFoxDailyCheckGateMessage(
      messageIdPrefix,
      checkOutcome.gate,
      gateTexts.verify,
      checkOutcome.timestamp,
    ));
  } else if (checkOutcome?.type === 'noMatches' && noMatchesText) {
    thread.push(buildFoxDailyCheckNoMatchesMessage(
      messageIdPrefix,
      noMatchesText,
      checkOutcome.timestamp,
    ));
  }
  return thread;
};

const isFoxDailyCheckOpeningMessage = (msg) => Boolean(
  msg?.foxDailyCheckGreeting
  || (msg?.id && String(msg.id).endsWith('-fox-daily-check-open')),
);

const isFoxDailyCheckComputingReplyMessage = (msg) => Boolean(
  msg?.foxDailyCheckComputingReply
  || (msg?.id && String(msg.id).endsWith('-fox-daily-check-computing')),
);

const isFoxDailyCheckMatchFoundMessage = (msg) => Boolean(
  msg?.foxDailyCheckMatchFound
  || (msg?.id && String(msg.id).endsWith('-fox-daily-check-match-found')),
);

const buildSurveyGreetingMessages = (
  initialGreetingId,
  messageIdPrefix,
  firstMessageText,
  firstMessageAlgoIcon = false,
) => {
  const ts = new Date();
  /* Only the greeting bubble is shown up front; the brand-tiles prompt is appended ~250ms later
     by an effect so the first question lands with the same cadence as every later question. */
  return [
    {
      id: initialGreetingId,
      sender: 'fox',
      text: firstMessageText,
      inlineAlgoIcon: firstMessageAlgoIcon,
      timestamp: ts,
      hideTimestamp: true,
    },
  ];
};

/** Trailing "..." on a status sentence, cycling . / .. / ... without shifting the words. */
function AnimatedTrailingDots({ text }) {
  const [dots, setDots] = useState(1);
  const base = String(text || '').replace(/\.+$/, '');

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDots(3);
      return undefined;
    }
    const id = window.setInterval(() => {
      setDots((n) => (n % 3) + 1);
    }, 400);
    return () => window.clearInterval(id);
  }, []);

  return (
    <>
      {base}
      <span className="fox-chat-ellipsis-dots" aria-hidden="true">
        {'.'.repeat(dots)}
      </span>
    </>
  );
}

const renderFoxBubbleText = (msg) => {
  if (!msg.text) return null;
  if (msg.inlineAlgoIcon) {
    return (
      <p className="messaging-bubble-text">
        {msg.text}
        <img src={algoIcon} alt="" className="fox-chat-greeting-algo-icon" aria-hidden />
      </p>
    );
  }
  return <p className="messaging-bubble-text">{msg.text}</p>;
};

/* Resolve once every image URL has finished loading (or errored), with a hard cap so a slow or
   broken image can't stall the dependent UI indefinitely. Used to preload survey tile images
   during the inter-message delay so the tiles bubble appears with everything already decoded. */
const preloadImageUrls = (urls, capMs = 1500) => new Promise((resolve) => {
  const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
  if (
    list.length === 0
    || typeof window === 'undefined'
    || typeof window.Image === 'undefined'
  ) {
    resolve();
    return;
  }
  let remaining = list.length;
  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    resolve();
  };
  const onOne = () => {
    remaining -= 1;
    if (remaining <= 0) finish();
  };
  setTimeout(finish, capMs);
  list.forEach((url) => {
    const img = new window.Image();
    img.onload = onOne;
    img.onerror = onOne;
    img.src = url;
  });
});

/* Custom search picks keep their original URL in `sourceImageUrl`; if the persisted Storage
   copy fails to load, fall back to it once instead of showing a broken image. */
const fallbackToSourceImage = (tile) => (e) => {
  const source = typeof tile?.sourceImageUrl === 'string' ? tile.sourceImageUrl : '';
  const img = e?.currentTarget;
  if (!img || !source || img.dataset.fallbackApplied || img.src === source) return;
  img.dataset.fallbackApplied = '1';
  img.src = source;
};

const isFoxSurveyMessage = (msg) => Boolean(
  msg?.surveyPromptBubble
  || msg?.brandTiles
  || msg?.emojiTiles
  || msg?.placeTiles
  || msg?.gifTiles
  || msg?.nicknameTiles
  || msg?.genderTiles
  || msg?.luvTiles
  || msg?.romanticLookingTiles
  || msg?.ageTiles
  || msg?.locationTiles
  || msg?.photoTiles
  || msg?.collageMessage,
);

const hasFoxSurveyProgress = (answers, posterFields) => (
  answers.brands.length > 0
  || answers.activities.length > 0
  || answers.places.length > 0
  || !!answers.spirit_gif
  || !!answers.user_photo
  || !!posterFields.posterNickname
  || !!posterFields.selfGender
  || posterFields.romanticLuv
  || posterFields.friendLuv
  || posterFields.lookingForGenders.length > 0
  || posterFields.selfAge !== null
  || !!posterFields.homeBase
);

const BRAND_PICK_MAX = 3;
const EMOJI_PICK_MAX = 3;
const PLACE_PICK_MAX = 3;
const SURVEY_SEARCH_PAGE = 9;
const BRAND_SEARCH_FETCH_COUNT = 36;

const tilePickRemainingHint = (picked, max, pluralNoun, singularNoun) => {
  const need = max - picked;
  if (need === 3) return `^ pick 3 ${pluralNoun} ^`;
  if (need === 2) return `^ pick 2 more ${pluralNoun} ^`;
  if (need === 1) return `^ pick 1 more ${singularNoun} ^`;
  return null;
};

const customSearchAddedHint = (step, answers) => {
  let remaining = null;
  if (step === 'brand') {
    remaining = tilePickRemainingHint(answers.brands.length, BRAND_PICK_MAX, 'items', 'item');
  } else if (step === 'emoji') {
    remaining = tilePickRemainingHint(
      answers.activities.length,
      EMOJI_PICK_MAX,
      'emojis',
      'emoji',
    );
  } else if (step === 'place') {
    remaining = tilePickRemainingHint(answers.places.length, PLACE_PICK_MAX, 'places', 'place');
  }
  if (!remaining) return 'Custom result added!';
  const plain = remaining.replace(/^\^ /, '').replace(/ \^$/, '');
  const sentence = plain.charAt(0).toUpperCase() + plain.slice(1);
  return `Custom result added! ${sentence}`;
};

const takeNextSearchPage = (poolRef, pageSize = SURVEY_SEARCH_PAGE) => {
  const pool = Array.isArray(poolRef.current) ? poolRef.current : [];
  const next = pool.slice(0, pageSize);
  poolRef.current = pool.slice(pageSize);
  return next;
};

const HOME_BASE_PRESETS = [
  { id: 'san-francisco', label: 'SAN FRANCISCO', value: 'SAN FRANCISCO' },
  { id: 'berkeley', label: 'BERKELEY', value: 'BERKELEY' },
];

/** Linear survey order for FoxChat — must stay aligned with findFirstUnansweredMsgId. */
const getFoxChatSurveyStep = (msg) => {
  /* Split-out intro prompts (text bubble shown just before each tile bubble) carry their own step
     so the "hide downstream questions when an earlier answer is deselected" logic hides the prompt
     text alongside its tiles. */
  if (typeof msg.surveyPromptStep === 'number') return msg.surveyPromptStep;
  if (msg.brandTiles) return 0;
  if (msg.emojiTiles) return 1;
  if (msg.placeTiles) return 2;
  if (msg.gifTiles) return 3;
  if (msg.nicknameTiles) return 4;
  if (msg.genderTiles) return 5;
  if (msg.ageTiles) return 6;
  if (msg.luvTiles) return 7;
  if (msg.romanticLookingTiles) return 8;
  if (msg.locationTiles) return 9;
  if (msg.photoTiles) return 10;
  if (msg.collageMessage) return 11;
  return null;
};

/** Smallest survey step index that is still incomplete, or null if steps 0–9 are all satisfied. */
const getFirstIncompleteFoxSurveyStepIndex = (
  answers,
  posterFields,
  luvConfirmed,
  romanticLookingConfirmed,
  skipGenderLuvAgeSteps,
) => {
  if (answers.brands.length < BRAND_PICK_MAX) return 0;
  if (answers.activities.length < EMOJI_PICK_MAX) return 1;
  if (answers.places.length < PLACE_PICK_MAX) return 2;
  if (!answers.spirit_gif) return 3;
  const nick = posterFields.posterNickname;
  if (!nick || (typeof nick === 'string' && !nick.trim())) return 4;
  if (skipGenderLuvAgeSteps) return null;
  if (!posterFields.selfGender) return 5;
  /* Age now comes right after gender, before the LUV question. */
  if (posterFields.selfAge === null) return 6;
  /* Match LUV UI: tiles alone are not enough — must tap CONFIRM (or both tiles auto-confirms). */
  if (!luvConfirmed || (!posterFields.romanticLuv && !posterFields.friendLuv)) return 7;
  /* Romantic “who are you looking for?” — must tap CONFIRM after picking. */
  if (
    posterFields.romanticLuv
    && (!romanticLookingConfirmed || posterFields.lookingForGenders.length === 0)
  ) {
    return 8;
  }
  const homeBase = posterFields.homeBase;
  if (!homeBase || (typeof homeBase === 'string' && !homeBase.trim())) return 9;
  return null;
};

const FOX_SETUP_QUESTION_TOTAL = 10;

/** LUV + "who are you looking for?" share one setup question. */
const isFoxSetupLuvQuestionComplete = (posterFields, luvConfirmed, romanticLookingConfirmed) => {
  const luvPicked = !!luvConfirmed && (!!posterFields.romanticLuv || !!posterFields.friendLuv);
  if (!luvPicked) return false;
  if (!posterFields.romanticLuv) return true;
  return !!romanticLookingConfirmed && posterFields.lookingForGenders.length > 0;
};

/** 10 collage-builder questions. LUV + romantic-looking are question 8; photo is 10. */
const getFoxSetupQuestionProgress = (
  answers,
  posterFields,
  luvConfirmed,
  romanticLookingConfirmed,
) => {
  const nick = posterFields.posterNickname;
  const homeBase = posterFields.homeBase;
  const steps = [
    answers.brands.length >= BRAND_PICK_MAX,
    answers.activities.length >= EMOJI_PICK_MAX,
    answers.places.length >= PLACE_PICK_MAX,
    !!answers.spirit_gif,
    !!(nick && (typeof nick !== 'string' || nick.trim())),
    !!posterFields.selfGender,
    posterFields.selfAge !== null,
    isFoxSetupLuvQuestionComplete(posterFields, luvConfirmed, romanticLookingConfirmed),
    !!(homeBase && (typeof homeBase !== 'string' || homeBase.trim())),
    !!answers.user_photo,
  ];
  const completed = steps.filter(Boolean).length;
  const firstIncomplete = steps.findIndex((done) => !done);
  const current = firstIncomplete === -1 ? FOX_SETUP_QUESTION_TOTAL : firstIncomplete + 1;
  return {
    completed,
    current,
    total: FOX_SETUP_QUESTION_TOTAL,
    pct: Math.round((current / FOX_SETUP_QUESTION_TOTAL) * 100),
  };
};

const FoxChat = ({
  launcherVisible,
  isOpen,
  onLauncherClick,
  onClose,
  /* Auth + post-save props passed by HomePage. `currentUser` gates the SAVE button; when null,
     SAVE invokes `onShowSignUp` instead of writing. `signUpPopupOpen` mirrors HomePage's
     signup-popup visibility so we can detect "user closed signup without authing" and drop
     the post-signup auto-save flag. `onCollageSaved` lets HomePage close the popup and route
     the user to "Your collages" after a successful save. */
  currentUser = null,
  onShowSignUp = null,
  signUpPopupOpen = false,
  /** True while LoginPopup is open. Fox Chat is portaled above `.App`, so it must drop behind that popup too. */
  loginPopupOpen = false,
  onCollageSaved = null,
  /** `(hooks) => void` — passes `{ buildPosterForResetEmail, onPosterAttachedViaResetEmail }` for password-reset collage attach. */
  registerFoxChatResetEmailAttachHooks = null,
  foxChatCopy = DEFAULT_FOX_CHAT_COPY,
  /** Renders as the thread panel inside Messages Inbox (no overlay / launcher). */
  embeddedInInbox = false,
  /** Back from embedded inbox thread → conversation list. */
  onInboxBack = null,
  /** Fires once when the embedded daily-check thread is shown. */
  onInboxThreadOpened = null,
  /** Fires when the user sends a daily-check GIF (clear Messages badge). */
  onDailyVibeCheckCompleted = null,
  /** `(conversationId|null) => void` — OPEN MESSAGES under the "Found someone!" reply. */
  onOpenMessages = null,
  /** Opens the school-email confirmation popup (auto after an unverified vibe check). */
  onVerifyProfile = null,
  /** Live `users/{uid}.berkeleyEmailVerified`; flipping true re-runs the daily-check match. */
  profileVerified = false,
  /** HomePage school-email popup is open — drop this overlay behind `.App` so the popup is clickable. */
  verifyPopupOpen = false,
  vibeCheckStreak = 0,
  vibeCheckStreakLastPeriodKey = null,
  /** True when the signed-in user already has a saved collage. Daily vibe checks only apply once a
      collage exists; signed-in users without one get the collage builder (same as signed-out). */
  hasSavedCollage = false,
  /**
   * Incremented when Messages asks for another match. `0` is the normal daily check.
   * A positive token opens this thread and, when today's GIF is already in, searches
   * again instead of restoring the match already shown.
   */
  requestAnotherMatchToken = 0,
}) => {
  const {
    firstMessageText,
    firstMessageAlgoIcon = false,
    brandPromptText,
    emojiPromptText,
    placesPromptText,
    gifPromptText,
    nicknamePromptText,
    genderPromptText,
    luvPromptText,
    romanticLookingPromptText,
    agePromptText,
    locationPromptText,
    locationOtherPlaceholder,
    locationOtherInputAriaLabel,
    photoPromptText,
    photoCollageIntroText,
    deferFoxMaskToSave,
    collageFoxMaskSaveHint,
    messageInputEnabled,
    initialGreetingId,
    domIdPrefix,
    messageIdPrefix,
    analyticsSource,
    tileHintPickNickname,
    tileHintPickGender,
    tileHintConfirmAge,
    tileHintPickLocation,
    tileHintPickPhoto,
    selectedBarAriaLabel,
    saveOpeningCollagesHint,
    emailSignupSavedHint,
    signedOutProfileSaveHint,
    nicknamePlaceholder,
    nicknameInputAriaLabel,
    photoChipAriaLabel,
    photoChipTitle,
    skipGenderLuvAgeSteps,
    collageCtaSignedIn,
    collageCtaSignedOut,
    opsSendToOpsFlow,
    chatHeaderTitle,
    loggedInChatHeaderTitle,
    dailyCheckGreetingText,
    dailyCheckSubtitleText,
    dailyCheckComputingReplyText,
    dailyCheckMatchFoundText,
    dailyCheckMatchFoundCtaText,
    dailyCheckVerifyGateText,
    dailyCheckVerifyGateCtaText,
    dailyCheckNoMatchesText,
    chatLauncherAriaLabel,
  } = foxChatCopy;

  /* Daily-check (vibe) mode requires a signed-in user who already has a collage. A signed-in user
     without a collage falls through to the collage builder, just like a signed-out visitor. */
  const isLoggedInSelfFoxChat = Boolean(currentUser?.uid && !opsSendToOpsFlow && hasSavedCollage);
  const displayChatHeaderTitle = isLoggedInSelfFoxChat
    ? loggedInChatHeaderTitle
    : chatHeaderTitle;

  const [localVibeCheckStreak, setLocalVibeCheckStreak] = useState(vibeCheckStreak);
  const [localVibeCheckStreakLastPeriodKey, setLocalVibeCheckStreakLastPeriodKey] = useState(
    vibeCheckStreakLastPeriodKey,
  );

  useEffect(() => {
    setLocalVibeCheckStreak(vibeCheckStreak);
    setLocalVibeCheckStreakLastPeriodKey(vibeCheckStreakLastPeriodKey);
  }, [vibeCheckStreak, vibeCheckStreakLastPeriodKey]);

  const vibeCheckStreakCount = useMemo(
    () => resolveVibeCheckStreakDisplay(localVibeCheckStreak, localVibeCheckStreakLastPeriodKey),
    [localVibeCheckStreak, localVibeCheckStreakLastPeriodKey],
  );

  const dailyCheckSubtitleWithStreak = `${vibeCheckStreakCount}/${VIBE_CHECK_STREAK_MAX} vibe streak`;

  /** This month's daily-check GIFs keyed by periodKey, for the streak day carousel. */
  const [monthVibeGifs, setMonthVibeGifs] = useState({});
  const streakCarouselRef = useRef(null);
  const [dailyVibePeriodKey, setDailyVibePeriodKey] = useState(() => getDailyVibePeriodKey());

  /** Day cards: 1..today (vibe-period day of the current month). */
  const { streakCarouselDays, streakCarouselMonthLabel, streakCarouselMonthShort } = useMemo(() => {
    const currentKey = dailyVibePeriodKey;
    const monthPrefix = currentKey.slice(0, 8);
    const todayDay = Number(currentKey.slice(8));
    const [year, month] = currentKey.slice(0, 7).split('-').map(Number);
    const monthName = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' });
    const days = Array.from({ length: todayDay }, (_, i) => {
      const day = i + 1;
      return { day, periodKey: `${monthPrefix}${String(day).padStart(2, '0')}` };
    });
    return {
      streakCarouselDays: days,
      streakCarouselMonthLabel: `Your daily vibes in ${monthName}`,
      streakCarouselMonthShort: monthName.slice(0, 3).toUpperCase(),
    };
  }, [dailyVibePeriodKey]);

  const [messages, setMessages] = useState(() => {
    if (isLoggedInSelfFoxChat) {
      const uid = currentUser?.uid;
      const greetingTimestamp = resolveDailyCheckGreetingTimestamp(uid, null);
      const cached = uid ? readDailyVibeChatCache(uid) : null;
      if (cached?.gifUrl) {
        return assembleDailyCheckThread({
          messageIdPrefix,
          greetingText: dailyCheckGreetingText,
          computingReplyText: dailyCheckComputingReplyText,
          userGifMessage: {
            id: `me-gif-${cached.checkId || cached.timestamp}`,
            sender: 'me',
            gifUrl: cached.gifUrl,
            timestamp: new Date(cached.timestamp || Date.now()),
          },
          greetingTimestamp,
          matchFoundText: dailyCheckMatchFoundText,
          matchFound: requestAnotherMatchToken > 0 ? null : readDailyCheckMatchFoundForThread(uid),
          checkOutcome: requestAnotherMatchToken > 0 ? null : readDailyCheckOutcomeForThread(uid),
          gateTexts: { verify: dailyCheckVerifyGateText },
          noMatchesText: dailyCheckNoMatchesText,
        });
      }
      return assembleDailyCheckThread({
        messageIdPrefix,
        greetingText: dailyCheckGreetingText,
        computingReplyText: dailyCheckComputingReplyText,
        userGifMessage: null,
        greetingTimestamp,
      });
    }
    return buildSurveyGreetingMessages(
      initialGreetingId,
      messageIdPrefix,
      firstMessageText,
      firstMessageAlgoIcon,
    );
  });
  const [input, setInput] = useState('');
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [hasVibratedOnce, setHasVibratedOnce] = useState(false);
  /* Canonical survey-answer blob — keyed exactly like InlineSurvey writes it to `survey_responses`
     and HomePage embeds in saved posters as `surveyAnswers`. Keep this in sync with the schema in
     `InlineSurvey.handleAnswer` so the editor on HomePage can hydrate FoxChat-saved posters. */
  const [answers, setAnswers] = useState({
    brands: [],
    activities: [],
    places: [],
    spirit_gif: null,
    vibe_photo: null,
    user_photo: null,
    user_photo_original: null,
  });
  /* Top-level poster fields — keyed exactly like the doc HomePage's `handleSaveClick` writes to
     `users/{uid}.savedWantedPosters`. These are NOT part of `surveyAnswers`; they live alongside it. */
  const [posterFields, setPosterFields] = useState({
    posterNickname: null,
    selfGender: null,
    romanticLuv: false,
    friendLuv: false,
    lookingForGenders: [],
    selfAge: null,
    homeBase: null,
  });
  const foxSurveyInProgress = hasFoxSurveyProgress(answers, posterFields);
  const showPreUnmaskGifChat = isLoggedInSelfFoxChat && !foxSurveyInProgress;
  const suppressFoxCollageSurvey = showPreUnmaskGifChat;
  const dailyCheckGifSent = useMemo(
    () => showPreUnmaskGifChat && messages.some((m) => m.sender === 'me' && m.gifUrl),
    [showPreUnmaskGifChat, messages],
  );
  const dailyCheckGifPeriodKey = useMemo(() => {
    const gifMsg = messages.find((m) => m.sender === 'me' && m.gifUrl);
    return dailyVibePeriodKeyForTimestamp(gifMsg?.timestamp);
  }, [messages]);
  const [brandMoreClickCount, setBrandMoreClickCount] = useState(0);
  const [brandSearchFieldKey, setBrandSearchFieldKey] = useState(0);
  const [brandSearchResults, setBrandSearchResults] = useState([]);
  const [brandSearchLoading, setBrandSearchLoading] = useState(false);
  const [brandSearchHasText, setBrandSearchHasText] = useState(false);
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [brandSearchHasMore, setBrandSearchHasMore] = useState(false);
  const [emojiMoreClickCount, setEmojiMoreClickCount] = useState(0);
  const [emojiSearchFieldKey, setEmojiSearchFieldKey] = useState(0);
  const [emojiSearchResults, setEmojiSearchResults] = useState([]);
  const [emojiSearchEmpty, setEmojiSearchEmpty] = useState(false);
  const [emojiSearchHasText, setEmojiSearchHasText] = useState(false);
  const [emojiSearchQuery, setEmojiSearchQuery] = useState('');
  const [emojiSearchHasMore, setEmojiSearchHasMore] = useState(false);
  const [placeMoreClickCount, setPlaceMoreClickCount] = useState(0);
  const [placeSearchFieldKey, setPlaceSearchFieldKey] = useState(0);
  const [placeSearchResults, setPlaceSearchResults] = useState([]);
  const [placeSearchLoading, setPlaceSearchLoading] = useState(false);
  const [placeSearchHasText, setPlaceSearchHasText] = useState(false);
  const [placeSearchQuery, setPlaceSearchQuery] = useState('');
  const [placeSearchHasMore, setPlaceSearchHasMore] = useState(false);
  const [placeSearchDone, setPlaceSearchDone] = useState(false);
  const [gifMoreClickCount, setGifMoreClickCount] = useState(0);
  const [gifSearchFieldKey, setGifSearchFieldKey] = useState(0);
  const [gifSearchResults, setGifSearchResults] = useState([]);
  const [gifSearchLoading, setGifSearchLoading] = useState(false);
  const [gifSearchHasText, setGifSearchHasText] = useState(false);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  const [gifSearchHasMore, setGifSearchHasMore] = useState(false);
  const [gifSearchDone, setGifSearchDone] = useState(false);
  const [loadingMoreGifs, setLoadingMoreGifs] = useState(false);
  const [gifFetchHint, setGifFetchHint] = useState(null);
  /** Which "MORE" button is currently loading the next options bubble (`brand` | `emoji` | `place` | `gif` | `nickname`). */
  const [moreOptionsLoading, setMoreOptionsLoading] = useState(null);
  /** Which tile-search step just received a custom search pick (`brand` | `emoji` | `place` | `gif`). */
  const [surveySearchCustomAdded, setSurveySearchCustomAdded] = useState(null);
  const [chatGifs, setChatGifs] = useState([]);
  const [chatGifSearch, setChatGifSearch] = useState('');
  const [loadingChatGifs, setLoadingChatGifs] = useState(false);
  const [sendingFoxGif, setSendingFoxGif] = useState(false);
  const [nicknameMoreClickCount, setNicknameMoreClickCount] = useState(0);
  const [nicknameFieldKey, setNicknameFieldKey] = useState(0);
  const [locationFieldKey, setLocationFieldKey] = useState(0);
  const [luvConfirmed, setLuvConfirmed] = useState(false);
  const [romanticLookingConfirmed, setRomanticLookingConfirmed] = useState(false);
  const [previewAuraColor, setPreviewAuraColor] = useState(null);
  const [auraRecomputing, setAuraRecomputing] = useState(false);
  const auraRecomputeTimerRef = useRef(null);
  const [ageInput, setAgeInput] = useState('');
  /* After age CONFIRM, wheel is idle until the user taps the YOUR AGE tile again. */
  const [ageWheelInteractionUnlocked, setAgeWheelInteractionUnlocked] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [photoHintIndex, setPhotoHintIndex] = useState(0);
  const [photoMagazinePair, setPhotoMagazinePair] = useState(buildMagazinePair);
  const [photoScissorsOffset, setPhotoScissorsOffset] = useState(buildScissorsOffset);
  /* SAVE state for the terminal collage bubble.
       saveState : 'idle' | 'saving' | 'saved' | 'error'
       saveError : last error message (only meaningful in 'error')
       savedPosterId : the id assigned to the saved poster (used to drive the post-save CTA copy). */
  const [saveState, setSaveState] = useState('idle');
  const [savingDots, setSavingDots] = useState(1);
  const [saveError, setSaveError] = useState(null);
  const [savedPosterId, setSavedPosterId] = useState(null);
  /** True when SAVE wrote via the email-signup token (browser stays signed out). */
  const [savedToEmailSignupProfile, setSavedToEmailSignupProfile] = useState(false);
  /** True when signup or a reset email attached the collage; hint asks them to check email. */
  const [savedAwaitingEmail, setSavedAwaitingEmail] = useState(false);
  /** Signed-out profile hint, shown 3s after SAVE even if the request is still in flight. */
  const [signedOutSaveHintEarly, setSignedOutSaveHintEarly] = useState(false);
  const signedOutSaveHintTimerRef = useRef(null);
  const clearSignedOutSaveHintTimer = () => {
    if (signedOutSaveHintTimerRef.current) {
      window.clearTimeout(signedOutSaveHintTimerRef.current);
      signedOutSaveHintTimerRef.current = null;
    }
  };
  /* Only meaningful while the request is still pending; once it settles the regular hint takes over. */
  const showEarlySignedOutHint = signedOutSaveHintEarly && saveState === 'saving';

  /* Cycle "SAVING." / "SAVING.." / "SAVING..." while a self-collage save is in flight. */
  const showSavingDots = saveState === 'saving' && !opsSendToOpsFlow;
  useEffect(() => {
    if (!showSavingDots) {
      setSavingDots(1);
      return undefined;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSavingDots(3);
      return undefined;
    }
    const id = window.setInterval(() => {
      setSavingDots((n) => (n % 3) + 1);
    }, 400);
    return () => window.clearInterval(id);
  }, [showSavingDots]);
  useEffect(() => () => clearSignedOutSaveHintTimer(), []);
  const [opsSendModalOpen, setOpsSendModalOpen] = useState(false);
  const [opsSendModalPhase, setOpsSendModalPhase] = useState('menu');
  const [opsReferEmail, setOpsReferEmail] = useState('');
  const [opsReferEmailError, setOpsReferEmailError] = useState('');
  const [opsReferEmailSubmitted, setOpsReferEmailSubmitted] = useState(false);
  const [opsReferIg, setOpsReferIg] = useState('');
  const [opsReferIgError, setOpsReferIgError] = useState('');
  const [opsReferIgSubmitted, setOpsReferIgSubmitted] = useState(false);
  const [opsShareLinkCopied, setOpsShareLinkCopied] = useState(false);
  /* Drives the "shake" animation on the bubble of a question whose answer is missing when the
     user reaches the photo step (e.g. they scrolled up and dis-selected all gender tiles). */
  const [shakeMessageId, setShakeMessageId] = useState(null);
  const shakeTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  /* Set to true by handleMore{Brand,Emoji,Place}Options just before the in-place insert so the
     "scroll to bottom on every messages change" effect skips one tick. Without this the user
     would be yanked to the latest message at the bottom of the thread instead of seeing the
     freshly-inserted "more" bubble appear right under the bubble whose MORE button they clicked. */
  const suppressNextAutoScrollRef = useRef(false);
  const inputRef = useRef(null);
  /* Focus target for the freshly-opened panel. Claiming focus here on open stops Safari from
     falling back to the navbar SIGN IN link (which sits under the ×) when the launcher unmounts. */
  const panelRef = useRef(null);
  /* False until the panel's first auto-scroll after opening has run. The open scroll jumps
     instantly (behavior: 'auto') instead of smooth so there's no in-progress scroll animation —
     on iOS the first tap during a momentum/smooth scroll only stops the scroll, which was leaving
     the × inactive on the first tap when reopening a chat that scrolls to the bottom. */
  const foxChatOpenedScrollRef = useRef(false);
  const brandSearchWarmupRef = useRef(false);
  const placeSearchWarmupRef = useRef(false);
  const gifSearchWarmupRef = useRef(false);
  /** KLIPY page cursor once local mood GIFs are exhausted (collage survey MORE GIFS). */
  const gifKlipyPageRef = useRef(1);
  const brandSearchPoolRef = useRef([]);
  const emojiSearchShownRef = useRef([]);
  const placeSearchPageTokenRef = useRef('');
  const gifQuerySearchPageRef = useRef(1);
  /** Tile message id to scroll into view after search results render. */
  const surveySearchScrollTargetRef = useRef(null);
  /** Tile message id to scroll into view after a MORE button inserts a new options bubble. */
  const moreOptionsScrollTargetRef = useRef(null);
  const surveySearchCustomAddedTimerRef = useRef(null);
  const brandPromptSentRef = useRef(false);
  const emojiPromptSentRef = useRef(false);
  /** Next index in {@link OPPS_EMOJI_LIBRARY} for Antifox “More emojis”; then standard pool. */
  const oppsEmojiCursorRef = useRef(0);
  const placesPromptSentRef = useRef(false);
  /** Place tiles pre-picked and warmed when the emoji question lands, consumed by the places step. */
  const pendingPlaceTilesRef = useRef(null);
  const gifPromptSentRef = useRef(false);
  /** GIF tiles pre-picked and warmed when the places question lands, consumed by the GIF step. */
  const pendingGifTilesRef = useRef(null);
  const nicknamePromptSentRef = useRef(false);
  const genderPromptSentRef = useRef(false);
  const luvPromptSentRef = useRef(false);
  /* Tracks last ROMANCE/FRIENDS flags so we can invalidate `luvConfirmed` when the user edits tiles. */
  const prevRomanticFriendLuvRef = useRef(null);
  /* Tracks last match-gender picks so we can invalidate `romanticLookingConfirmed` after edits. */
  const prevLookingForGendersForConfirmRef = useRef(null);
  const romanticLookingPromptSentRef = useRef(false);
  const agePromptSentRef = useRef(false);
  const locationPromptSentRef = useRef(false);
  const photoPromptSentRef = useRef(false);
  const photoInputRef = useRef(null);
  const photoObjectUrlRef = useRef(null);
  const photoStorageUrlRef = useRef(null);
  const photoUploadInFlightRef = useRef(null);
  /** First complete-collage tile fingerprint (or last saved) — same baseline idea as user-page edits. */
  const auraTileBaselineRef = useRef(null);
  const lastSavedAuraTilesRef = useRef(null);
  const lastDriftedAuraTilesRef = useRef(null);
  const auraPhotoKeyRef = useRef(null);
  const pendingAuraRgbRef = useRef(null);
  const pendingAuraHslRef = useRef(null);
  const auraColorByTilesRef = useRef(new Map());
  const auraDriftDebounceRef = useRef(null);
  const auraPreviewGenRef = useRef(0);
  const auraPreviewInFlightRef = useRef(null);
  const auraPreviewQueuedSnapshotRef = useRef(null);

  const rememberAuraForSnapshot = (snapshot, hsl, rgb) => {
    const key = foxChatAuraTileKey(snapshot);
    if (!key || !rgb) return;
    auraColorByTilesRef.current.set(key, { hsl: hsl || null, rgb });
  };

  const applyTempAura = (nextRgb, nextHsl) => {
    if (!nextRgb) return;
    pendingAuraRgbRef.current = nextRgb;
    pendingAuraHslRef.current = nextHsl || null;
    beginAuraRecomputeHold(nextRgb);
  };

  const beginAuraRecomputeHold = (nextRgb, holdStartedAt = Date.now()) => {
    setAuraRecomputing(true);
    if (auraRecomputeTimerRef.current) {
      clearTimeout(auraRecomputeTimerRef.current);
    }
    const wait = Math.max(0, FOX_CHAT_AURA_RECOMPUTE_MS - (Date.now() - holdStartedAt));
    auraRecomputeTimerRef.current = setTimeout(() => {
      if (nextRgb) {
        pendingAuraRgbRef.current = nextRgb;
        setPreviewAuraColor(nextRgb);
      }
      setAuraRecomputing(false);
      auraRecomputeTimerRef.current = null;
    }, wait);
  };

  const assignInitialTempAura = (baseline) => {
    setAuraRecomputing(true);
    setPreviewAuraColor(null);
    const holdStartedAt = Date.now();
    const gen = (auraPreviewGenRef.current += 1);
    /* Deferred via a microtask so `auraPreviewInFlightRef` is assigned before the body runs
       (see driftTempAuraForSnapshot). */
    const work = Promise.resolve().then(async () => {
      try {
        const assigned = await assignPreviewAuraColor();
        if (gen !== auraPreviewGenRef.current) return;
        const rgb = resolveAuraRgb(assigned?.auraColor);
        if (!rgb) throw new Error('Missing assigned aura color');
        pendingAuraRgbRef.current = rgb;
        pendingAuraHslRef.current = assigned.auraHsl || null;
        rememberAuraForSnapshot(baseline, assigned.auraHsl, rgb);
        beginAuraRecomputeHold(rgb, holdStartedAt);
      } catch (auraErr) {
        if (gen !== auraPreviewGenRef.current) return;
        console.warn('FoxChat assignPreviewAuraColor failed:', auraErr?.message || auraErr);
        pendingAuraRgbRef.current = FOX_CHAT_DEFAULT_AURA;
        pendingAuraHslRef.current = null;
        rememberAuraForSnapshot(baseline, null, FOX_CHAT_DEFAULT_AURA);
        beginAuraRecomputeHold(FOX_CHAT_DEFAULT_AURA, holdStartedAt);
      } finally {
        if (auraPreviewInFlightRef.current === work) {
          auraPreviewInFlightRef.current = null;
        }
        const queued = auraPreviewQueuedSnapshotRef.current;
        auraPreviewQueuedSnapshotRef.current = null;
        if (queued) {
          await driftTempAuraForSnapshot(queued);
        }
      }
    });
    auraPreviewInFlightRef.current = work;
    return work;
  };

  const driftTempAuraForSnapshot = (nextSnapshot) => {
    if (opsSendToOpsFlow || !nextSnapshot) return Promise.resolve();
    const prev = lastDriftedAuraTilesRef.current || auraTileBaselineRef.current;
    if (!prev) return Promise.resolve();
    const changes = countCollageTileChanges(prev, nextSnapshot);
    if (!hasCollageTileChanges(changes)) return Promise.resolve();

    const cached = auraColorByTilesRef.current.get(foxChatAuraTileKey(nextSnapshot));
    if (cached?.rgb) {
      auraPreviewGenRef.current += 1;
      lastDriftedAuraTilesRef.current = nextSnapshot;
      applyTempAura(cached.rgb, cached.hsl);
      return Promise.resolve();
    }

    if (auraPreviewInFlightRef.current) {
      auraPreviewQueuedSnapshotRef.current = nextSnapshot;
      return auraPreviewInFlightRef.current;
    }

    setAuraRecomputing(true);
    const gen = (auraPreviewGenRef.current += 1);
    /* Deferred via a microtask so `auraPreviewInFlightRef` is assigned before the body runs —
       a synchronous throw would otherwise run `finally` first and leave a stale settled promise
       in the ref, which stalls every later drift on “recomputing…” and hangs SAVE. */
    const work = Promise.resolve().then(async () => {
      try {
        const preview = await previewAuraColorApi({
          tileChanges: changes,
          auraColor: pendingAuraRgbRef.current || FOX_CHAT_DEFAULT_AURA,
          auraHsl: pendingAuraHslRef.current,
        });
        if (gen !== auraPreviewGenRef.current) return;
        const rgb = resolveAuraRgb(preview?.auraColor);
        if (!rgb) return;
        lastDriftedAuraTilesRef.current = nextSnapshot;
        rememberAuraForSnapshot(nextSnapshot, preview.auraHsl, rgb);
        applyTempAura(rgb, preview.auraHsl);
      } catch (auraErr) {
        if (gen === auraPreviewGenRef.current) {
          console.warn('FoxChat previewAuraColor failed (keeping current aura):', auraErr?.message || auraErr);
          setAuraRecomputing(false);
        }
      } finally {
        if (auraPreviewInFlightRef.current === work) {
          auraPreviewInFlightRef.current = null;
        }
        const queued = auraPreviewQueuedSnapshotRef.current;
        auraPreviewQueuedSnapshotRef.current = null;
        if (queued) {
          await driftTempAuraForSnapshot(queued);
        }
      }
    });

    auraPreviewInFlightRef.current = work;
    return work;
  };

  const flushTempAuraDrift = async () => {
    if (auraDriftDebounceRef.current) {
      clearTimeout(auraDriftDebounceRef.current);
      auraDriftDebounceRef.current = null;
    }
    await driftTempAuraForSnapshot(foxChatAuraTileSnapshot(answers, auraPhotoKeyRef.current));
    while (auraPreviewInFlightRef.current) {
      try {
        await auraPreviewInFlightRef.current;
      } catch {
        /* already logged */
      }
    }
  };

  useEffect(() => () => {
    if (auraRecomputeTimerRef.current) {
      clearTimeout(auraRecomputeTimerRef.current);
      auraRecomputeTimerRef.current = null;
    }
    if (auraDriftDebounceRef.current) {
      clearTimeout(auraDriftDebounceRef.current);
      auraDriftDebounceRef.current = null;
    }
  }, []);

  const revokePhotoObjectUrl = () => {
    if (photoObjectUrlRef.current) {
      URL.revokeObjectURL(photoObjectUrlRef.current);
      photoObjectUrlRef.current = null;
    }
  };
  const localChatGifsBaseRef = useRef([]);
  const chatKlipyFetchGenRef = useRef(0);
  const chatGifGridScrollRef = useRef(null);
  const chatSpiritGifWarmupRef = useRef(false);
  const dailyVibeHydratedPeriodRef = useRef(null);

  const resetFoxSurveyRefs = () => {
    brandPromptSentRef.current = false;
    emojiPromptSentRef.current = false;
    placesPromptSentRef.current = false;
    pendingPlaceTilesRef.current = null;
    gifPromptSentRef.current = false;
    pendingGifTilesRef.current = null;
    nicknamePromptSentRef.current = false;
    genderPromptSentRef.current = false;
    luvPromptSentRef.current = false;
    romanticLookingPromptSentRef.current = false;
    agePromptSentRef.current = false;
    locationPromptSentRef.current = false;
    photoPromptSentRef.current = false;
    auraTileBaselineRef.current = null;
    lastSavedAuraTilesRef.current = null;
    lastDriftedAuraTilesRef.current = null;
    auraPhotoKeyRef.current = null;
    pendingAuraRgbRef.current = null;
    pendingAuraHslRef.current = null;
    auraColorByTilesRef.current = new Map();
    auraPreviewQueuedSnapshotRef.current = null;
    if (auraRecomputeTimerRef.current) {
      clearTimeout(auraRecomputeTimerRef.current);
      auraRecomputeTimerRef.current = null;
    }
    if (auraDriftDebounceRef.current) {
      clearTimeout(auraDriftDebounceRef.current);
      auraDriftDebounceRef.current = null;
    }
  };

  const resetFoxSurveySession = () => {
    resetFoxSurveyRefs();
    setAnswers({
      brands: [],
      activities: [],
      places: [],
      spirit_gif: null,
      vibe_photo: null,
      user_photo: null,
      user_photo_original: null,
    });
    setPosterFields({
      posterNickname: null,
      selfGender: null,
      romanticLuv: false,
      friendLuv: false,
      lookingForGenders: [],
      selfAge: null,
      homeBase: null,
    });
    setNicknameMoreClickCount(0);
    setNicknameFieldKey((k) => k + 1);
    setLocationFieldKey((k) => k + 1);
    setLuvConfirmed(false);
    setRomanticLookingConfirmed(false);
    setAuraRecomputing(false);
    setPreviewAuraColor(null);
    setGifMoreClickCount(0);
    setGifSearchFieldKey((k) => k + 1);
    setGifSearchResults([]);
    setGifSearchLoading(false);
    setGifSearchDone(false);
    setGifSearchHasText(false);
    setGifSearchQuery('');
    setGifSearchHasMore(false);
    setBrandSearchQuery('');
    setBrandSearchHasMore(false);
    setBrandSearchHasText(false);
    setEmojiSearchQuery('');
    setEmojiSearchHasMore(false);
    setEmojiSearchHasText(false);
    setPlaceSearchQuery('');
    setPlaceSearchHasMore(false);
    setPlaceSearchHasText(false);
    setLoadingMoreGifs(false);
    setMoreOptionsLoading(null);
    setGifFetchHint(null);
    setSurveySearchCustomAdded(null);
    gifKlipyPageRef.current = 1;
    gifQuerySearchPageRef.current = 1;
    brandSearchPoolRef.current = [];
    emojiSearchShownRef.current = [];
    placeSearchPageTokenRef.current = '';
  };

  const clearSurveySearchCustomAdded = (step) => {
    if (step) {
      setSurveySearchCustomAdded((prev) => (prev === step ? null : prev));
    } else {
      setSurveySearchCustomAdded(null);
    }
    if (surveySearchCustomAddedTimerRef.current) {
      clearTimeout(surveySearchCustomAddedTimerRef.current);
      surveySearchCustomAddedTimerRef.current = null;
    }
  };

  const markSurveySearchCustomAdded = (step) => {
    setSurveySearchCustomAdded(step);
    if (surveySearchCustomAddedTimerRef.current) {
      clearTimeout(surveySearchCustomAddedTimerRef.current);
    }
    surveySearchCustomAddedTimerRef.current = setTimeout(() => {
      setSurveySearchCustomAdded((prev) => (prev === step ? null : prev));
      surveySearchCustomAddedTimerRef.current = null;
    }, 3000);
  };
  /* Set to true when the user clicks SAVE while signed-out (we route them to signup). When
     `currentUser` next flips truthy, the post-signup auto-save effect re-fires the save
     without requiring a second tap. Cleared if the user closes the signup popup without
     completing auth, so a later sign-in via a different surface doesn't ambush them. */
  const pendingSaveAfterSignUpRef = useRef(false);
  /** Last poster doc from a successful ops send-flow save — used for `opscollages` snapshots. */
  const lastSavedOpsPosterRef = useRef(null);

  const resetOpsSendModalFields = () => {
    setOpsSendModalPhase('menu');
    setOpsReferEmail('');
    setOpsReferEmailError('');
    setOpsReferEmailSubmitted(false);
    setOpsReferIg('');
    setOpsReferIgError('');
    setOpsReferIgSubmitted(false);
    setOpsShareLinkCopied(false);
  };

  const closeOpsSendModal = () => {
    setOpsSendModalOpen(false);
    resetOpsSendModalFields();
  };

  /** Append an ops collage share row (full poster snapshot + channel / contact). */
  const persistOpsCollageShare = async ({ channel, friendEmail = null, friendIgHandle = null }) => {
    if (!opsSendToOpsFlow || !currentUser?.uid) return;
    const poster = lastSavedOpsPosterRef.current;
    const posterId = poster?.id || savedPosterId;
    if (!posterId) return;
    const posterForStore = poster
      ? stripUnmaskedPhotoForPublicShare(sanitizeForFirestore(poster))
      : null;
    try {
      await addDoc(
        collection(db, 'opscollages'),
        sanitizeForFirestore({
          userId: currentUser.uid,
          posterId,
          channel,
          friendEmail: friendEmail || null,
          friendIgHandle: friendIgHandle || null,
          poster: posterForStore,
          source: 'foxchat_ops',
          createdAt: serverTimestamp(),
        }),
      );
    } catch (err) {
      console.warn('opscollages write failed:', err);
    }
  };

  const lastBrandTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].brandTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastEmojiTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].emojiTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastPlaceTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].placeTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastGifTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].gifTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastNicknameTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].nicknameTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastLuvTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].luvTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastRomanticLookingTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].romanticLookingTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastAgeTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].ageTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastLocationTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].locationTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastPhotoTilesId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].photoTiles) return messages[i].id;
    }
    return null;
  }, [messages]);

  /* Open the survey in two beats so the first question lands with the same cadence as every later
     question instead of appearing instantly alongside the greeting:
       1) ~250ms after the greeting → drop the "What stuff do you like?" text bubble on its own.
       2) preload the tile images, then ~250ms later → drop the tiles bubble, already decoded so it
          never pops in half-loaded.
     Skipped for the logged-in daily-check thread and whenever the collage survey is suppressed. */
  useEffect(() => {
    if (isLoggedInSelfFoxChat) return undefined;
    if (suppressFoxCollageSurvey) return undefined;
    if (!isOpen && !embeddedInInbox) return undefined;
    if (brandPromptSentRef.current) return undefined;
    brandPromptSentRef.current = true;
    const promptId = `${messageIdPrefix}-brand-prompt`;
    /* Uses the sample the homepage pre-picked and warmed after load (when available), so the
       preload below resolves from cache and the tiles land instantly on first open. */
    const brandTiles = takeInitialBrandSample(9);
    setMessages((prev) => {
      if (prev.some((m) => m.brandTiles) || prev.some((m) => m.id === promptId)) return prev;
      return [
        ...prev,
        {
          id: promptId,
          sender: 'fox',
          text: brandPromptText,
          timestamp: new Date(),
          hideTimestamp: true,
          surveyPromptBubble: true,
          surveyPromptStep: 0,
        },
      ];
    });
    /* Still preload the tile images so the bubble renders fully decoded (no half-loaded pop-in),
       but drop the artificial typing delay so the tiles land as soon as the images are ready. */
    preloadImageUrls(brandTiles.map((b) => b.imageUrl)).then(() => {
      setMessages((prev) => {
        if (prev.some((m) => m.brandTiles)) return prev;
        return [
          ...prev,
          {
            id: `${messageIdPrefix}-brand-tiles`,
            sender: 'fox',
            timestamp: new Date(),
            brandTiles: true,
            brands: brandTiles,
          },
        ];
      });
    });
    return undefined;
  }, [isLoggedInSelfFoxChat, suppressFoxCollageSurvey, isOpen, embeddedInInbox, brandPromptText, messageIdPrefix]);

  /* Once the user picks 3 brand tiles, follow up with the emoji "Pick 3" question. */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (answers.brands.length !== BRAND_PICK_MAX) return undefined;
    if (emojiPromptSentRef.current) return undefined;
    emojiPromptSentRef.current = true;
    let emojiGrid;
    if (opsSendToOpsFlow && OPPS_EMOJI_LIBRARY.length > 0) {
      oppsEmojiCursorRef.current = 0;
      emojiGrid = getNextOppsEmojiBatch(0, 9);
      oppsEmojiCursorRef.current = emojiGrid.length;
    } else {
      emojiGrid = getRandomEmojis(9);
    }
    const promptId = `${messageIdPrefix}-emoji-prompt`;
    /* The places question comes right after the emojis, so pre-pick its 9 tiles now and start
       warming their images while the user picks emojis — by the time the places bubble is due,
       its preload resolves from cache and the tiles land instantly. */
    if (!pendingPlaceTilesRef.current) {
      pendingPlaceTilesRef.current = getVariedPlacesSample(9);
      preloadImageUrls(pendingPlaceTilesRef.current.map((p) => p.imageUrl));
    }
    /* Emojis are plain text (nothing to preload), so drop the typing delay entirely and land the
       prompt and tiles together. */
    setMessages((prev) => {
      if (prev.some((m) => m.emojiTiles) || prev.some((m) => m.id === promptId)) return prev;
      return [
        ...prev,
        {
          id: promptId,
          sender: 'fox',
          text: emojiPromptText,
          timestamp: new Date(),
          hideTimestamp: true,
          surveyPromptBubble: true,
          surveyPromptStep: 1,
        },
        {
          id: `${messageIdPrefix}-emoji-${Date.now()}`,
          sender: 'fox',
          timestamp: new Date(),
          emojiTiles: true,
          emojis: emojiGrid,
        },
      ];
    });
    return undefined;
  }, [answers.brands.length, emojiPromptText, messageIdPrefix, opsSendToOpsFlow, suppressFoxCollageSurvey]);

  const refreshEmojiSearchResults = () => {
    if (!(emojiSearchResults.length > 0 && emojiSearchQuery)) return;
    const next = searchActivityPlusEmojisByQuery(
      emojiSearchQuery,
      SURVEY_SEARCH_PAGE,
      emojiSearchShownRef.current,
    );
    if (!next.length) {
      setEmojiSearchHasMore(false);
      return;
    }
    emojiSearchShownRef.current = [...emojiSearchShownRef.current, ...next];
    setEmojiSearchResults(next);
    setEmojiSearchHasMore(next.length >= SURVEY_SEARCH_PAGE);
  };

  const handleMoreEmojiOptions = () => {
    const shown = new Set();
    messages.forEach((m) => {
      if (m.emojiTiles && Array.isArray(m.emojis)) {
        m.emojis.forEach((e) => shown.add(e));
      }
    });
    const shownArr = Array.from(shown);
    let next;
    if (opsSendToOpsFlow && oppsEmojiCursorRef.current < OPPS_EMOJI_LIBRARY.length) {
      next = getNextOppsEmojiBatch(oppsEmojiCursorRef.current, 9);
      oppsEmojiCursorRef.current += next.length;
    } else {
      next = getRandomEmojis(9, shownArr);
    }
    if (!next?.length) return;
    setMoreOptionsLoading('emoji');
    /* Insert the new emoji-tile bubble right AFTER the latest emojiTiles message (which is the
       one that hosts the MORE EMOJIS button the user just clicked) instead of appending at the
       end of the thread. Matters when the conversation has progressed past the emoji step and
       the user scrolls back to add more emojis — we want the new bubble to appear in context,
       not all the way at the bottom of the chat. Emoji tiles are unicode (nothing to preload), so
       we add the standard ~250ms delay to keep the cadence in line with the rest of the flow and
       the image-based MORE buttons. The click count is bumped in the same tick as the insert so
       the scroll-to-new-bubble effect fires once the new bubble (with its search field) is in. */
    setTimeout(() => {
      suppressNextAutoScrollRef.current = true;
      const newId = `${messageIdPrefix}-emoji-more-${Date.now()}`;
      moreOptionsScrollTargetRef.current = newId;
      setMessages((prev) => {
        const newMsg = {
          id: newId,
          sender: 'fox',
          timestamp: new Date(),
          emojiTiles: true,
          emojis: next,
        };
        let lastIdx = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].emojiTiles) {
            lastIdx = i;
            break;
          }
        }
        if (lastIdx === -1) return [...prev, newMsg];
        return [...prev.slice(0, lastIdx + 1), newMsg, ...prev.slice(lastIdx + 1)];
      });
      setEmojiMoreClickCount((c) => c + 1);
      setEmojiSearchResults([]);
      setEmojiSearchQuery('');
      setEmojiSearchHasMore(false);
      setEmojiSearchHasText(false);
      setEmojiSearchEmpty(false);
      emojiSearchShownRef.current = [];
      setEmojiSearchFieldKey((k) => k + 1);
      clearSurveySearchCustomAdded('emoji');
      setMoreOptionsLoading(null);
    }, 250);
  };

  /* Once the user picks 3 emoji tiles, follow up with the "Now pick 3 places in the Bay Area:" question. */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (answers.activities.length !== EMOJI_PICK_MAX) return undefined;
    if (placesPromptSentRef.current) return undefined;
    placesPromptSentRef.current = true;
    const promptId = `${messageIdPrefix}-places-prompt`;
    /* Prefer the sample pre-picked (and warmed) when the emoji question landed. */
    const placeTiles = pendingPlaceTilesRef.current || getVariedPlacesSample(9);
    pendingPlaceTilesRef.current = null;
    /* The GIF question comes right after the places, so pre-pick its 9 tiles now and start
       warming their previews while the user picks places. */
    if (!pendingGifTilesRef.current) {
      pendingGifTilesRef.current = getRandomMoodGifs(9);
      preloadImageUrls(pendingGifTilesRef.current.map((g) => g.stillUrl || g.gifUrl || g.fullUrl));
    }
    setMessages((prev) => {
      if (prev.some((m) => m.placeTiles) || prev.some((m) => m.id === promptId)) return prev;
      return [
        ...prev,
        {
          id: promptId,
          sender: 'fox',
          text: placesPromptText,
          timestamp: new Date(),
          hideTimestamp: true,
          surveyPromptBubble: true,
          surveyPromptStep: 2,
        },
      ];
    });
    preloadImageUrls(placeTiles.map((p) => p.imageUrl)).then(() => {
      setMessages((prev) => {
        if (prev.some((m) => m.placeTiles)) return prev;
        return [
          ...prev,
          {
            id: `${messageIdPrefix}-places-${Date.now()}`,
            sender: 'fox',
            timestamp: new Date(),
            placeTiles: true,
            places: placeTiles,
          },
        ];
      });
    });
    return undefined;
  }, [answers.activities.length, placesPromptText, messageIdPrefix, suppressFoxCollageSurvey]);

  const refreshPlaceSearchResults = () => {
    if (!(placeSearchResults.length > 0 && placeSearchQuery)) return;
    const pageToken = placeSearchPageTokenRef.current;
    if (placeSearchLoading) return;
    if (!pageToken) {
      setPlaceSearchHasMore(false);
      return;
    }
    setPlaceSearchLoading(true);
    searchPlacesApi(placeSearchQuery, pageToken)
      .then(async (results) => {
        const list = Array.isArray(results) ? results : [];
        placeSearchPageTokenRef.current = results?.nextPageToken || '';
        setPlaceSearchHasMore(!!placeSearchPageTokenRef.current);
        if (!list.length) return;
        await preloadImageUrls(list.map((p) => p.imageUrl));
        setPlaceSearchResults(list);
      })
      .catch((err) => {
        console.error('Place search error:', err);
      })
      .finally(() => {
        setPlaceSearchLoading(false);
      });
  };

  const handleMorePlaceOptions = () => {
    const shownIds = new Set();
    messages.forEach((m) => {
      if (m.placeTiles && Array.isArray(m.places)) {
        m.places.forEach((p) => shownIds.add(p.id));
      }
    });
    const pool = allPlaces.filter((p) => !shownIds.has(p.id));
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    const nextPlaces = shuffled.slice(0, Math.min(9, shuffled.length));
    setMoreOptionsLoading('place');
    /* Preload the new tile images before inserting the bubble so it renders fully decoded (no
       half-loaded pop-in). Inserted right after the latest placeTiles bubble (the one whose MORE
       PLACES button was clicked) so the new tiles appear in context. Bump the click count in the
       same tick as the insert so the scroll-to-new-bubble effect fires once the bubble is in. */
    preloadImageUrls(nextPlaces.map((p) => p.imageUrl)).then(() => {
      suppressNextAutoScrollRef.current = true;
      const newId = `${messageIdPrefix}-places-more-${Date.now()}`;
      moreOptionsScrollTargetRef.current = newId;
      setMessages((prev) => {
        const newMsg = {
          id: newId,
          sender: 'fox',
          timestamp: new Date(),
          placeTiles: true,
          places: nextPlaces,
        };
        let lastIdx = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].placeTiles) {
            lastIdx = i;
            break;
          }
        }
        if (lastIdx === -1) return [...prev, newMsg];
        return [...prev.slice(0, lastIdx + 1), newMsg, ...prev.slice(lastIdx + 1)];
      });
      setPlaceMoreClickCount((c) => c + 1);
      setPlaceSearchResults([]);
      setPlaceSearchQuery('');
      setPlaceSearchHasMore(false);
      setPlaceSearchHasText(false);
      setPlaceSearchDone(false);
      placeSearchPageTokenRef.current = '';
      setPlaceSearchFieldKey((k) => k + 1);
      clearSurveySearchCustomAdded('place');
      setMoreOptionsLoading(null);
    });
  };

  const collectShownGifKeys = () => {
    const shownKeys = new Set();
    messages.forEach((m) => {
      if (m.gifTiles && Array.isArray(m.gifs)) {
        m.gifs.forEach((g) => {
          const k = gifDedupeKey(g);
          if (k) shownKeys.add(k);
          if (g?.id != null) shownKeys.add(String(g.id));
        });
      }
    });
    if (answers.spirit_gif) {
      const k = gifDedupeKey(answers.spirit_gif);
      if (k) shownKeys.add(k);
      if (answers.spirit_gif.id != null) shownKeys.add(String(answers.spirit_gif.id));
    }
    return shownKeys;
  };

  const insertGifTilesMessage = (gifs) => {
    /* Preload the GIF previews before inserting the bubble so it renders fully decoded (no
       half-loaded pop-in). Inserted right after the latest gifTiles bubble so the new tiles
       appear in context. Returns a promise that resolves once the bubble is inserted so callers
       can bump the click count (which triggers the scroll-to-new-bubble effect) in the same tick. */
    return preloadImageUrls((gifs || []).map((g) => g.stillUrl || g.gifUrl || g.fullUrl)).then(() => {
      suppressNextAutoScrollRef.current = true;
      const newId = `${messageIdPrefix}-gif-more-${Date.now()}`;
      moreOptionsScrollTargetRef.current = newId;
      setMessages((prev) => {
        const newMsg = {
          id: newId,
          sender: 'fox',
          timestamp: new Date(),
          gifTiles: true,
          gifs,
        };
        let lastIdx = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].gifTiles) {
            lastIdx = i;
            break;
          }
        }
        if (lastIdx === -1) return [...prev, newMsg];
        return [...prev.slice(0, lastIdx + 1), newMsg, ...prev.slice(lastIdx + 1)];
      });
    });
  };

  const refreshGifSearchResults = async () => {
    if (!(gifSearchResults.length > 0 && gifSearchQuery) || gifSearchLoading) return;
    setGifSearchLoading(true);
    try {
      const customerId = currentUser?.uid || `anon_${Date.now()}`;
      const page = gifQuerySearchPageRef.current;
      const remote = await searchSpiritGifsApi(
        gifSearchQuery,
        customerId,
        SURVEY_SEARCH_PAGE,
        page,
      );
      const next = (remote || []).slice(0, SURVEY_SEARCH_PAGE);
      gifQuerySearchPageRef.current = page + 1;
      if (next.length > 0) {
        setGifSearchResults(next);
      }
      setGifSearchHasMore(next.length >= SURVEY_SEARCH_PAGE);
    } catch (err) {
      console.error('Spirit GIF search error:', err);
    } finally {
      setGifSearchLoading(false);
    }
  };

  const handleMoreGifOptions = async () => {
    if (gifSearchLoading || loadingMoreGifs) return;
    const shownKeys = collectShownGifKeys();
    const isGifUnseen = (g) => {
      const k = gifDedupeKey(g);
      if (!k || shownKeys.has(k)) return false;
      if (g?.id != null && shownKeys.has(String(g.id))) return false;
      return true;
    };

    setMoreOptionsLoading('gif');
    const localPool = MOOD_LOCAL_GIFS.filter(isGifUnseen);
    if (localPool.length > 0) {
      const shuffled = [...localPool].sort(() => Math.random() - 0.5);
      const nextGifs = shuffled.slice(0, Math.min(9, shuffled.length));
      insertGifTilesMessage(nextGifs).then(() => {
        setGifMoreClickCount((c) => c + 1);
        setGifSearchResults([]);
        setGifSearchQuery('');
        setGifSearchHasMore(false);
        setGifSearchHasText(false);
        setGifSearchDone(false);
        gifQuerySearchPageRef.current = 1;
        setGifSearchFieldKey((k) => k + 1);
        clearSurveySearchCustomAdded('gif');
        setMoreOptionsLoading(null);
      });
      return;
    }

    setLoadingMoreGifs(true);
    setGifFetchHint(null);
    try {
      const customerId = currentUser?.uid || `anon_${Date.now()}`;
      let page = gifKlipyPageRef.current;
      let nextGifs = [];
      for (let attempt = 0; attempt < 3 && nextGifs.length === 0; attempt += 1) {
        const remote = await searchSpiritGifsApi('vibe', customerId, 9, page);
        page += 1;
        nextGifs = (remote || []).filter(isGifUnseen).slice(0, 9);
        nextGifs.forEach((g) => {
          const k = gifDedupeKey(g);
          if (k) shownKeys.add(k);
        });
      }
      gifKlipyPageRef.current = page;
      if (nextGifs.length === 0) {
        setGifFetchHint('no_results');
        setGifMoreClickCount((c) => c + 1);
        setMoreOptionsLoading(null);
      } else {
        insertGifTilesMessage(nextGifs).then(() => {
          setGifMoreClickCount((c) => c + 1);
          setGifSearchResults([]);
          setGifSearchQuery('');
          setGifSearchHasMore(false);
          setGifSearchHasText(false);
          setGifSearchDone(false);
          gifQuerySearchPageRef.current = 1;
          setGifSearchFieldKey((k) => k + 1);
          clearSurveySearchCustomAdded('gif');
          setMoreOptionsLoading(null);
        });
      }
    } catch (err) {
      console.error('Spirit GIF fetch error:', err);
      setGifFetchHint('error');
      setMoreOptionsLoading(null);
    } finally {
      setLoadingMoreGifs(false);
    }
  };

  const addGifToLatestSet = (gif) => {
    suppressNextAutoScrollRef.current = true;
    setMessages((prev) => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].gifTiles) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx === -1) return prev;
      const next = [...prev];
      const target = next[lastIdx];
      const gifs = Array.isArray(target.gifs) ? [...target.gifs] : [];
      const key = gifDedupeKey(gif);
      if (gifs.some((g) => gifDedupeKey(g) === key || g.id === gif.id)) return prev;
      const replaceIdx = gifs.findIndex(
        (g) =>
          answers.spirit_gif?.id !== g.id
          && gifDedupeKey(g) !== gifDedupeKey(answers.spirit_gif),
      );
      if (replaceIdx !== -1) {
        gifs[replaceIdx] = gif;
      } else {
        gifs.push(gif);
      }
      next[lastIdx] = { ...target, gifs };
      return next;
    });
  };

  const addPlaceToLatestSet = (place) => {
    suppressNextAutoScrollRef.current = true;
    setMessages((prev) => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].placeTiles) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx === -1) return prev;
      const next = [...prev];
      const target = next[lastIdx];
      const places = Array.isArray(target.places) ? [...target.places] : [];
      if (places.some((p) => p.id === place.id)) return prev;
      const replaceIdx = places.findIndex(
        (p) => !answers.places.some((s) => s.id === p.id) && p.id !== place.id,
      );
      if (replaceIdx !== -1) {
        places[replaceIdx] = place;
      } else {
        places.push(place);
      }
      next[lastIdx] = { ...target, places };
      return next;
    });
  };

  /* Once the user picks 3 places, follow up with the "Pick one GIF:" question. */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (answers.places.length !== PLACE_PICK_MAX) return undefined;
    if (gifPromptSentRef.current) return undefined;
    gifPromptSentRef.current = true;
    const promptId = `${messageIdPrefix}-gifs-prompt`;
    /* Prefer the sample pre-picked (and warmed) when the places question landed. */
    const gifTiles = pendingGifTilesRef.current || getRandomMoodGifs(9);
    pendingGifTilesRef.current = null;
    setMessages((prev) => {
      if (prev.some((m) => m.gifTiles) || prev.some((m) => m.id === promptId)) return prev;
      return [
        ...prev,
        {
          id: promptId,
          sender: 'fox',
          text: gifPromptText,
          timestamp: new Date(),
          hideTimestamp: true,
          surveyPromptBubble: true,
          surveyPromptStep: 3,
        },
      ];
    });
    preloadImageUrls(gifTiles.map((g) => g.stillUrl || g.gifUrl || g.fullUrl)).then(() => {
      setMessages((prev) => {
        if (prev.some((m) => m.gifTiles)) return prev;
        return [
          ...prev,
          {
            id: `${messageIdPrefix}-gifs-${Date.now()}`,
            sender: 'fox',
            timestamp: new Date(),
            gifTiles: true,
            gifs: gifTiles,
          },
        ];
      });
    });
    return undefined;
  }, [answers.places.length, gifPromptText, messageIdPrefix, suppressFoxCollageSurvey]);

  /* Once the user picks a GIF, follow up with the "Let's pick your nickname!" question. */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (!answers.spirit_gif) return undefined;
    if (nicknamePromptSentRef.current) return undefined;
    nicknamePromptSentRef.current = true;
    const promptId = `${messageIdPrefix}-nicknames-prompt`;
    const nicknameTiles = getRandomNicknames(3);
    /* Nicknames are plain text (nothing to preload), so land the prompt and tiles together. */
    setMessages((prev) => {
      if (prev.some((m) => m.nicknameTiles) || prev.some((m) => m.id === promptId)) return prev;
      return [
        ...prev,
        {
          id: promptId,
          sender: 'fox',
          text: nicknamePromptText,
          timestamp: new Date(),
          hideTimestamp: true,
          surveyPromptBubble: true,
          surveyPromptStep: 4,
        },
        {
          id: `${messageIdPrefix}-nicknames-${Date.now()}`,
          sender: 'fox',
          timestamp: new Date(),
          nicknameTiles: true,
          nicknames: nicknameTiles,
        },
      ];
    });
    return undefined;
  }, [answers.spirit_gif, nicknamePromptText, messageIdPrefix, suppressFoxCollageSurvey]);

  /* Once the user picks a nickname, follow up with the "Pick your gender:" question. */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (skipGenderLuvAgeSteps) return undefined;
    if (!posterFields.posterNickname) return undefined;
    if (genderPromptSentRef.current) return undefined;
    genderPromptSentRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: `${messageIdPrefix}-gender-${Date.now()}`,
        sender: 'fox',
        text: genderPromptText,
        timestamp: new Date(),
        genderTiles: true,
      },
    ]);
    return undefined;
  }, [posterFields.posterNickname, genderPromptText, messageIdPrefix, skipGenderLuvAgeSteps, suppressFoxCollageSurvey]);

  /* Once the user confirms their age, follow up with the romance/friends LUV question.
     (Age now comes right after gender, so LUV is gated on age rather than gender.) */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (skipGenderLuvAgeSteps) return undefined;
    if (posterFields.selfAge === null) return undefined;
    if (luvPromptSentRef.current) return undefined;
    luvPromptSentRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: `${messageIdPrefix}-luv-${Date.now()}`,
        sender: 'fox',
        text: luvPromptText,
        timestamp: new Date(),
        luvTiles: true,
      },
    ]);
    return undefined;
  }, [posterFields.selfAge, luvPromptText, messageIdPrefix, skipGenderLuvAgeSteps, suppressFoxCollageSurvey]);

  /* Picking BOTH ROMANCE and FRIENDS is unambiguous, so treat it the same as tapping CONFIRM
     and auto-advance to the next question. The CONFIRM button stays available for any single-
     option pick. */
  useEffect(() => {
    if (luvConfirmed) return;
    if (posterFields.romanticLuv && posterFields.friendLuv) {
      setLuvConfirmed(true);
    }
  }, [posterFields.romanticLuv, posterFields.friendLuv, luvConfirmed]);

  /* After CONFIRM, any change to ROMANCE / FRIENDS tiles requires confirming again (hint + button). */
  useEffect(() => {
    const r = posterFields.romanticLuv;
    const f = posterFields.friendLuv;
    const prev = prevRomanticFriendLuvRef.current;
    if (
      prev !== null
      && luvConfirmed
      && (prev.romanticLuv !== r || prev.friendLuv !== f)
    ) {
      setLuvConfirmed(false);
    }
    prevRomanticFriendLuvRef.current = { romanticLuv: r, friendLuv: f };
  }, [posterFields.romanticLuv, posterFields.friendLuv, luvConfirmed]);

  /* After CONFIRM on romantic match genders, any change to the picks requires confirming again. */
  useEffect(() => {
    const ids = posterFields.lookingForGenders;
    const prev = prevLookingForGendersForConfirmRef.current;
    const same =
      prev !== null
      && prev.length === ids.length
      && prev.every((id, i) => id === ids[i]);
    if (prev !== null && romanticLookingConfirmed && !same) {
      setRomanticLookingConfirmed(false);
    }
    prevLookingForGendersForConfirmRef.current = [...ids];
  }, [posterFields.lookingForGenders, romanticLookingConfirmed]);

  /* Once the user confirms LUV with ROMANCE checked, ask which gender(s) they want romantically. */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (skipGenderLuvAgeSteps) return undefined;
    if (!luvConfirmed || !posterFields.romanticLuv) return undefined;
    if (romanticLookingPromptSentRef.current) return undefined;
    romanticLookingPromptSentRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: `${messageIdPrefix}-romantic-looking-${Date.now()}`,
        sender: 'fox',
        text: romanticLookingPromptText,
        timestamp: new Date(),
        romanticLookingTiles: true,
      },
    ]);
    return undefined;
  }, [luvConfirmed, posterFields.romanticLuv, romanticLookingPromptText, messageIdPrefix, skipGenderLuvAgeSteps, suppressFoxCollageSurvey]);

  /* Turning ROMANCE off hides the "who are you looking for?" bubble; clear match genders + confirm. */
  useEffect(() => {
    if (posterFields.romanticLuv) return undefined;
    setRomanticLookingConfirmed(false);
    setPosterFields((prev) => {
      if (prev.lookingForGenders.length === 0) return prev;
      return { ...prev, lookingForGenders: [] };
    });
    return undefined;
  }, [posterFields.romanticLuv]);

  /* Once the user is ready for the photo step: full flow requires age confirmed; ops-skip flow
     jumps here right after nickname. */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (photoPromptSentRef.current) return undefined;
    if (skipGenderLuvAgeSteps) {
      const nick = posterFields.posterNickname;
      if (!nick || (typeof nick === 'string' && !nick.trim())) return undefined;
    } else if (posterFields.selfAge === null || !String(posterFields.homeBase || '').trim()) {
      return undefined;
    }
    const missingId = findFirstUnansweredMsgId();
    if (missingId) {
      triggerShakeAtMessage(missingId);
      return undefined;
    }
    photoPromptSentRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: `${messageIdPrefix}-photo-${Date.now()}`,
        sender: 'fox',
        text: photoPromptText,
        timestamp: new Date(),
        photoTiles: true,
      },
    ]);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    skipGenderLuvAgeSteps,
    posterFields.selfAge,
    posterFields.homeBase,
    posterFields.selfGender,
    posterFields.romanticLuv,
    posterFields.friendLuv,
    posterFields.lookingForGenders.length,
    posterFields.posterNickname,
    answers.brands.length,
    answers.activities.length,
    answers.places.length,
    answers.spirit_gif,
    luvConfirmed,
    romanticLookingConfirmed,
    photoPromptText,
    messageIdPrefix,
    suppressFoxCollageSurvey,
  ]);

  /* Step through the hint messages while the photo is uploading + getting a fox mask applied (mirrors the homepage hero
     animation under the COLLAGE / START button). Walks the hint list once and clamps at the final
     "sticking it together..." string so the text stays put rather than looping — but we KEEP ticking
     past that point so the magazine + scissors offsets keep re-randomizing (the cut-out animation)
     until processing actually finishes and the collage is shown. The effect cleanup clears the timer
     once `photoUploading` and `photoProcessing` are both false. */
  useEffect(() => {
    if (!photoUploading && !photoProcessing) {
      setPhotoHintIndex(0);
      return undefined;
    }
    setPhotoHintIndex(0);
    let idx = 0;
    let timerId;
    const tick = () => {
      idx += 1;
      const clamped = Math.min(idx, PHOTO_PROCESSING_HINTS.length - 1);
      setPhotoHintIndex(clamped);
      setPhotoMagazinePair(buildMagazinePair());
      setPhotoScissorsOffset(buildScissorsOffset());
      timerId = setTimeout(tick, 1100);
    };
    timerId = setTimeout(tick, 1100);
    return () => clearTimeout(timerId);
  }, [photoUploading, photoProcessing]);

  useEffect(() => () => {
    if (photoObjectUrlRef.current) URL.revokeObjectURL(photoObjectUrlRef.current);
  }, []);

  const handlePhotoUpload = async (file) => {
    if (!file || photoUploading || photoProcessing) return;
    setPhotoError(null);
    /* Clear any previous masked photo + drop the prior collage message so the loader replaces them in place
       (covers both the first PICK PHOTO upload and the CHANGE PHOTO re-upload path). */
    setAnswers((prev) => ({ ...prev, user_photo: null, user_photo_original: null }));
    setMessages((prev) => prev.filter((m) => !m.collageMessage));
    revokePhotoObjectUrl();
    photoStorageUrlRef.current = null;
    setPhotoUploading(true);
    try {
      let uploadFile = file;
      try {
        uploadFile = await ensureBrowserDisplayablePhotoFile(file);
      } catch (convErr) {
        console.error('HEIF conversion failed:', convErr);
        setPhotoError('Couldn’t read this HEIF photo. Try exporting it as JPEG.');
        setPhotoUploading(false);
        return;
      }

      const localUrl = URL.createObjectURL(uploadFile);
      photoObjectUrlRef.current = localUrl;
      auraPhotoKeyRef.current = `${uploadFile.name}:${uploadFile.size}:${uploadFile.lastModified}`;
      if (!auraTileBaselineRef.current) {
        const baseline = foxChatAuraTileSnapshot(
          { ...answers, user_photo: localUrl },
          auraPhotoKeyRef.current,
        );
        auraTileBaselineRef.current = baseline;
        lastDriftedAuraTilesRef.current = baseline;
        void assignInitialTempAura(baseline);
      }

      const uploadToStorage = async () => {
        const path = `user_photos/${Date.now()}_${nanoid(8)}/${uploadFile.name}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, uploadFile, { contentType: uploadFile.type || 'image/jpeg' });
        const downloadUrl = await getDownloadURL(ref);
        photoStorageUrlRef.current = downloadUrl;
        return downloadUrl;
      };

      if (deferFoxMaskToSave) {
        /* Show a local preview immediately; Storage upload continues in the background.
           SAVE waits on photoUploadInFlightRef so we never persist a blob: URL. */
        setAnswers((prev) => ({ ...prev, user_photo: localUrl, user_photo_original: localUrl }));
        if (photoInputRef.current) photoInputRef.current.value = '';
        setMessages((prev) => [
          ...prev,
          {
            id: `${messageIdPrefix}-collage-${Date.now()}`,
            sender: 'fox',
            text: photoCollageIntroText,
            timestamp: new Date(),
            collageMessage: true,
            partnerCutout: localUrl,
          },
        ]);
        setPhotoUploading(false);
        const uploadPromise = uploadToStorage();
        photoUploadInFlightRef.current = uploadPromise;
        try {
          const downloadUrl = await uploadPromise;
          if (photoObjectUrlRef.current !== localUrl) return;
          setAnswers((prev) => ({
            ...prev,
            user_photo: prev.user_photo === localUrl ? downloadUrl : prev.user_photo,
            user_photo_original: prev.user_photo_original === localUrl ? downloadUrl : prev.user_photo_original,
          }));
          setMessages((prev) => prev.map((m) => (
            m.collageMessage && m.partnerCutout === localUrl
              ? { ...m, partnerCutout: downloadUrl }
              : m
          )));
          revokePhotoObjectUrl();
        } catch (err) {
          if (photoObjectUrlRef.current !== localUrl) return;
          console.error('Photo upload failed:', err);
          setPhotoError('Upload failed. Please try again.');
        } finally {
          if (photoUploadInFlightRef.current === uploadPromise) {
            photoUploadInFlightRef.current = null;
          }
        }
        return;
      }

      const downloadUrl = await uploadToStorage();
      setPhotoUploading(false);
      setPhotoProcessing(true);
      try {
        const editedUrl = await applyUserPhotoFoxMask(downloadUrl);
        setAnswers((prev) => ({ ...prev, user_photo: editedUrl, user_photo_original: downloadUrl }));
        if (photoInputRef.current) photoInputRef.current.value = '';
        setMessages((prev) => [
          ...prev,
          {
            id: `${messageIdPrefix}-collage-${Date.now()}`,
            sender: 'fox',
            text: photoCollageIntroText,
            timestamp: new Date(),
            collageMessage: true,
            partnerCutout: editedUrl,
          },
        ]);
        revokePhotoObjectUrl();
      } catch (err) {
        console.error('Fox-mask photo edit failed:', err);
        setPhotoError('Couldn’t add the fox mask. Try a different photo.');
      } finally {
        setPhotoProcessing(false);
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      setPhotoError('Upload failed. Please try again.');
      setPhotoUploading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setPhotoError('Image must be under 15 MB');
      return;
    }
    setPhotoError(null);
    void handlePhotoUpload(file);
  };

  const handlePhotoRetry = () => {
    /* Skip the PICK PHOTO intermediate state — open the system file picker directly so the user can
       immediately swap photos. The previous masked photo + collage message stay visible until a new
       file is actually picked (handled inside handlePhotoUpload). */
    setPhotoError(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
      photoInputRef.current.click();
    }
  };

  /* Re-arm the SAVE flow whenever the user materially edits the collage after a previous save —
     they may want to save the tweaked version as a NEW collage. We re-enable the button by
     dropping the 'saved' state back to 'idle'. The previously-saved poster is still in their
     "Your collages" list (we don't delete it). */
  useEffect(() => {
    if (saveState === 'saved') {
      clearSignedOutSaveHintTimer();
      setSaveState('idle');
      setSavedPosterId(null);
      setSavedToEmailSignupProfile(false);
      setSavedAwaitingEmail(false);
      setSignedOutSaveHintEarly(false);
    }
    /* Intentionally NOT depending on saveState — listing it would create a tight loop where
       toggling back to idle re-runs the effect and clears state again. We only want to react
       to changes in the upstream collage data. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    answers.brands,
    answers.activities,
    answers.places,
    answers.spirit_gif,
    answers.user_photo,
    answers.user_photo_original,
    posterFields.posterNickname,
    posterFields.selfGender,
    posterFields.romanticLuv,
    posterFields.friendLuv,
    posterFields.lookingForGenders,
    posterFields.selfAge,
    posterFields.homeBase,
  ]);

  /* Live vibe preview: tile swaps ask the server for a temp color (not persisted until SAVE).
     Debounced so unselect + select counts as one change. */
  useEffect(() => {
    if (opsSendToOpsFlow) return undefined;
    if (photoUploading || photoProcessing) return undefined;
    if (!auraTileBaselineRef.current) return undefined;
    if (saveState === 'saving') return undefined;

    const next = foxChatAuraTileSnapshot(answers, auraPhotoKeyRef.current);
    const prev = lastDriftedAuraTilesRef.current || auraTileBaselineRef.current;
    if (!hasCollageTileChanges(countCollageTileChanges(prev, next))) {
      /* Unselect + reselect the same tile inside the debounce window nets to no change, but the
         previous run already flipped “recomputing” on — release it unless real work is pending. */
      if (!auraPreviewInFlightRef.current && !auraRecomputeTimerRef.current) {
        setAuraRecomputing(false);
      }
      return undefined;
    }

    setAuraRecomputing(true);
    if (auraDriftDebounceRef.current) {
      clearTimeout(auraDriftDebounceRef.current);
    }
    auraDriftDebounceRef.current = setTimeout(() => {
      auraDriftDebounceRef.current = null;
      driftTempAuraForSnapshot(foxChatAuraTileSnapshot(answers, auraPhotoKeyRef.current));
    }, FOX_CHAT_AURA_DRIFT_DEBOUNCE_MS);
    return () => {
      if (auraDriftDebounceRef.current) {
        clearTimeout(auraDriftDebounceRef.current);
        auraDriftDebounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    answers.brands,
    answers.activities,
    answers.places,
    answers.spirit_gif,
    answers.user_photo,
    opsSendToOpsFlow,
    photoUploading,
    photoProcessing,
    saveState,
  ]);

  /* Primary collage action — save (default) or save + ops send modal. */
  const handleSaveButtonClick = () => {
    if (saveState === 'saving') return;
    if (opsSendToOpsFlow && saveState === 'saved' && savedPosterId) {
      setOpsSendModalOpen(true);
      return;
    }
    if (!opsSendToOpsFlow && saveState === 'saved') return;
    void handleSaveCollage();
  };

  /** Build canonical poster doc (+ survey blob); used by SAVE and password-reset email attach. */
  const composeFoxChatPosterData = async (uidForDoc) => {
    if (photoUploadInFlightRef.current) {
      try {
        await photoUploadInFlightRef.current;
      } catch {
        throw new Error('Photo upload failed. Please try again.');
      }
    }
    const photoUrl = photoStorageUrlRef.current || answers.user_photo;
    if (!photoUrl || String(photoUrl).startsWith('blob:')) {
      throw new Error('No collage photo to save.');
    }
    const originalUrl = photoStorageUrlRef.current || answers.user_photo_original || photoUrl;

    const posterId = nanoid();

    /* In FoxChat the user's masked cutout IS the partner cutout (no AI-generated partner
       like HomePage's match-creation flow), so we pass the same URL for both fields. The
       util uploads each to its own folder; for already-stable Firebase Storage URLs this is
       a no-op and just returns the URL. */
    const imageBundle = await persistCollageImageBundle({
      partnerCutout: photoUrl,
      userPhotoMasked: photoUrl,
      userPhotoOriginal: originalUrl || null,
      swags: (answers.brands || []).slice(0, 3),
      places: (answers.places || []).slice(0, 3),
    });

    const surveyAnswersForStore = {
      brands: (answers.brands || []).slice(0, 3),
      activities: (answers.activities || []).slice(0, 3),
      places: (answers.places || []).slice(0, 3),
      spirit_gif: answers.spirit_gif || null,
      vibe_photo: answers.vibe_photo || null,
      user_photo: photoUrl,
      user_photo_original: originalUrl || null,
    };

    const previewAura = resolveAuraRgb(pendingAuraRgbRef.current || FOX_CHAT_DEFAULT_AURA);
    const posterData = {
      ...buildPosterDoc({
        id: posterId,
        userId: uidForDoc ?? null,
        surveyAnswers: surveyAnswersForStore,
        imageBundle,
        posterNickname: (posterFields.posterNickname && String(posterFields.posterNickname).trim())
          ? String(posterFields.posterNickname).trim()
          : 'The One',
        selfGender: posterFields.selfGender,
        friendLuv: !!posterFields.friendLuv,
        romanticLuv: !!posterFields.romanticLuv,
        lookingForGenders: posterFields.lookingForGenders,
        selfAge: posterFields.selfAge,
        homeBase: posterFields.homeBase,
        words: [],
        isPrivateCollage: false,
        postedIrl: false,
        postedTiktok: false,
        friendCollageShare: false,
        friendFoxMaskPlaceholder: false,
        cutoutCustomizedSinceSurveyDefault: !!answers.user_photo,
      }),
      ...(previewAura ? { auraColor: previewAura } : {}),
      ...(pendingAuraHslRef.current ? { auraHsl: pendingAuraHslRef.current } : {}),
    };

    return { posterData, surveyAnswersForStore, posterId };
  };

  /** Build a copy whose displayed cutout is the fox mask. Returns the original poster if the edit fails. */
  async function withFoxMaskForEmailSignupSave(posterData) {
    if (!deferFoxMaskToSave || !posterData) return posterData;
    const unmaskedUrl = posterData.userPhotoOriginalUrl || posterData.userPhotoUrl;
    if (!unmaskedUrl || String(unmaskedUrl).startsWith('blob:')) return posterData;
    try {
      const maskedRaw = await applyUserPhotoFoxMask(unmaskedUrl);
      const maskedUrl = (await persistImageUrlToStorage(maskedRaw, 'user_photos', 'jpg')) || maskedRaw;
      if (!maskedUrl || maskedUrl === unmaskedUrl) return posterData;
      const surveyAnswers = posterData.surveyAnswers && typeof posterData.surveyAnswers === 'object'
        ? { ...posterData.surveyAnswers, user_photo: maskedUrl }
        : posterData.surveyAnswers;
      return {
        ...posterData,
        partnerCutout: maskedUrl,
        userPhotoUrl: maskedUrl,
        surveyAnswers,
      };
    } catch (maskErr) {
      console.warn('FoxChat email-signup fox mask failed; saving unmasked photo:', maskErr);
      return posterData;
    }
  }

  /** After the signed-out save has already flipped off SAVING..., patch that poster with the fox mask. */
  async function patchEmailSignupPosterWithFoxMask(posterData, session) {
    try {
      const masked = await withFoxMaskForEmailSignupSave(posterData);
      if (!masked || masked === posterData || masked.userPhotoUrl === posterData.userPhotoUrl) return;
      await attachFoxChatPosterApi(masked, session.idToken, { skipSurvey: true });
    } catch (maskErr) {
      console.warn('FoxChat email-signup background fox mask failed:', maskErr);
    }
  }

  /**
   * Email signup already created `session.uid` and signed the browser out. SAVE attaches the
   * collage and leaves SAVING... as soon as that write finishes. The fox mask runs afterward
   * and patches the same poster. A mask failure leaves the unmasked photo; HomePage backfills
   * it on the next sign-in.
   */
  async function saveCollageToRememberedEmailAccount(session) {
    setSaveError(null);
    setSavedToEmailSignupProfile(false);
    setSavedAwaitingEmail(false);
    setSignedOutSaveHintEarly(false);
    clearSignedOutSaveHintTimer();
    signedOutSaveHintTimerRef.current = window.setTimeout(() => {
      signedOutSaveHintTimerRef.current = null;
      setSignedOutSaveHintEarly(true);
    }, 3000);
    setSaveState('saving');
    try {
      await flushTempAuraDrift();
      const { posterData } = await composeFoxChatPosterData(session.uid);
      const data = await attachFoxChatPosterApi(posterData, session.idToken);
      if (!data?.attachedPosterId) {
        throw new Error('Save failed. Please try again.');
      }
      const nextAuraTiles = foxChatAuraTileSnapshot(
        { ...answers, user_photo: posterData.userPhotoUrl },
        auraPhotoKeyRef.current,
      );
      lastSavedAuraTilesRef.current = nextAuraTiles;
      auraTileBaselineRef.current = nextAuraTiles;
      lastDriftedAuraTilesRef.current = nextAuraTiles;
      clearSignedOutSaveHintTimer();
      setSignedOutSaveHintEarly(false);
      setSavedPosterId(data.attachedPosterId);
      setSavedToEmailSignupProfile(true);
      setSaveState('saved');
      void patchEmailSignupPosterWithFoxMask(posterData, session);
    } catch (err) {
      console.error('FoxChat email-signup save failed:', err);
      clearSignedOutSaveHintTimer();
      setSignedOutSaveHintEarly(false);
      const unauthorized = err?.status === 401 || err?.code === 'invalid_id_token' || err?.code === 'anonymous';
      if (unauthorized) {
        clearEmailSignupAttachSession();
        pendingSaveAfterSignUpRef.current = true;
        setSaveState('idle');
        if (typeof onShowSignUp === 'function') onShowSignUp();
        return;
      }
      setSaveError(err?.message || 'Save failed. Please try again.');
      setSaveState('error');
    }
  }

  /**
   * Deferred-mask flow only: after the collage is saved with the user's unmasked photo, generate
   * the fox mask in the background and patch the saved poster (private doc + public shared mirror).
   * HomePage also retries on login when this job misses (new-user OAuth races / edit API errors).
   */
  const applyFoxMaskInBackground = ({ posterId, unmaskedUrl, uid }) => {
    void applyFoxMaskToSavedPoster({
      db,
      posterId,
      unmaskedUrl,
      uid,
      displayName: currentUser?.displayName,
    });
  };

  const handleSaveCollage = async () => {
    if (saveState === 'saving') return;
    if (!opsSendToOpsFlow && saveState === 'saved') return;
    if (opsSendToOpsFlow && saveState === 'saved') return;

    if (!currentUser) {
      /* Email signup earlier in this tab already created the account and signed out.
         Attach the collage to that account instead of asking for the email again.
         The UI treats anonymous Firebase sessions as signed out (`currentUser` is null). */
      if (!opsSendToOpsFlow) {
        const emailSignupSession = readEmailSignupAttachSession();
        if (emailSignupSession) {
          await saveCollageToRememberedEmailAccount(emailSignupSession);
          return;
        }
      }
      pendingSaveAfterSignUpRef.current = true;
      if (typeof onShowSignUp === 'function') onShowSignUp();
      return;
    }

    setSaveError(null);
    setSavedAwaitingEmail(false);
    setSaveState('saving');

    try {
      await flushTempAuraDrift();
      const { posterData, surveyAnswersForStore, posterId } = await composeFoxChatPosterData(currentUser.uid);

      const nextAuraTiles = foxChatAuraTileSnapshot(
        { ...answers, user_photo: posterData.userPhotoUrl },
        auraPhotoKeyRef.current,
      );
      const auraForShare = resolveAuraRgb(pendingAuraRgbRef.current || previewAuraColor);
      const heldAuraReveal = auraRecomputing || !!auraRecomputeTimerRef.current;
      const auraPersist = !opsSendToOpsFlow
        && pendingAuraRgbRef.current
        && pendingAuraHslRef.current
        ? {
          auraColor: {
            r: pendingAuraRgbRef.current.r,
            g: pendingAuraRgbRef.current.g,
            b: pendingAuraRgbRef.current.b,
          },
          auraHsl: pendingAuraHslRef.current,
        }
        : null;

      /* Prize opt-in / `promoteOnLuvwtfSocials` is set later on Your collages (HomePage PRIZE OPT-IN). */

      /* Private save only — no `wantedFeed` write. The HomePage editor handles feed publishing
         and "MEET PEOPLE" if the user wants those later. */
      const userDocRef = doc(db, 'users', currentUser.uid);
      const savePayload = {
        savedWantedPosters: arrayUnion(posterData),
        updatedAt: new Date(),
        ...(auraPersist || {}),
      };
      try {
        await updateDoc(userDocRef, savePayload);
      } catch (writeErr) {
        /* New OAuth users: provision may not have created users/{uid} yet. */
        await setDoc(userDocRef, savePayload, { merge: true });
      }

      /* Mirror to `sharedPosters/{id}` so the poster is reachable via the public `/poster/{id}`
         shareable URL (this is HomePage's A.2 + B.4 write — folded together because FoxChat
         already collected `lookingForGenders` mid-chat and embedded them in posterData). The
         helper strips `userPhotoOriginalUrl` because sharedPosters is world-readable. Failures
         here don't fail the save (the poster is already persisted privately; the user can
         re-share later via the editor). */
      try {
        await writeSharedPosterMirror(db, posterData, { auraColor: auraForShare });
      } catch (shareErr) {
        console.warn('FoxChat sharedPosters mirror write failed:', shareErr);
      }

      /* Survey analytics continuity — InlineSurvey writes a row to `survey_responses` on
         completion; mirror that here so downstream reporting still sees FoxChat funnels.
         `source: 'foxchat'` distinguishes these rows from legacy InlineSurvey ones. Failures
         here don't fail the save (the collage is already persisted). */
      try {
        await addDoc(collection(db, 'survey_responses'), {
          userId: currentUser.uid,
          answers: surveyAnswersForStore,
          posterFields: {
            posterNickname: posterData.posterNickname,
            selfGender: posterData.selfGender || null,
            friendLuv: !!posterData.friendLuv,
            romanticLuv: !!posterData.romanticLuv,
            lookingForGenders: Array.isArray(posterData.lookingForGenders) ? posterData.lookingForGenders : [],
            selfAge: typeof posterData.selfAge === 'number' ? posterData.selfAge : null,
            homeBase: typeof posterData.homeBase === 'string' ? posterData.homeBase : null,
          },
          posterId,
          source: analyticsSource,
          createdAt: serverTimestamp(),
        });
      } catch (analyticsErr) {
        console.warn('FoxChat survey_responses analytics write failed:', analyticsErr);
      }

      lastSavedAuraTilesRef.current = nextAuraTiles;
      auraTileBaselineRef.current = nextAuraTiles;
      lastDriftedAuraTilesRef.current = nextAuraTiles;
      setSavedPosterId(posterId);
      setSaveState('saved');

      /* Deferred-mask flow: kick off the fox-mask edit in the background (non-blocking) so the
         user's SAVE → "Your collages" redirect isn't held up by the slow edit API. The helper
         patches the saved poster to the masked image once it's ready. */
      if (deferFoxMaskToSave && !opsSendToOpsFlow) {
        void applyFoxMaskInBackground({
          posterId,
          unmaskedUrl: photoStorageUrlRef.current || answers.user_photo_original || answers.user_photo || null,
          uid: currentUser.uid,
        });
      }

      if (opsSendToOpsFlow) {
        lastSavedOpsPosterRef.current = posterData;
        setOpsSendModalOpen(true);
      } else {
        lastSavedOpsPosterRef.current = null;
      }
      if (!opsSendToOpsFlow && typeof onCollageSaved === 'function') {
        /* Brief delay so the user sees the SAVED! state (and, after a vibe drift, the
           recomputing hold + updated ball) before HomePage redirects to "Your collages." */
        const redirectMs = heldAuraReveal
          ? FOX_CHAT_AURA_RECOMPUTE_MS + FOX_CHAT_SAVE_REDIRECT_MS
          : FOX_CHAT_SAVE_REDIRECT_MS;
        window.setTimeout(() => {
          try { onCollageSaved(posterData); } catch (cbErr) { console.warn('onCollageSaved callback threw:', cbErr); }
        }, redirectMs);
      }
    } catch (err) {
      console.error('FoxChat save failed:', err);
      setSaveError(err?.message || 'Save failed. Please try again.');
      setSaveState('error');
    }
  };

  useEffect(() => {
    if (!registerFoxChatResetEmailAttachHooks) return undefined;
    registerFoxChatResetEmailAttachHooks({
      buildPosterForResetEmail: async () => {
        try {
          const { posterData } = await composeFoxChatPosterData(null);
          return posterData;
        } catch {
          return null;
        }
      },
      onPosterAttachedViaResetEmail: ({ posterId }) => {
        pendingSaveAfterSignUpRef.current = false;
        setSaveError(null);
        setSavedPosterId(posterId);
        setSavedAwaitingEmail(true);
        setSaveState('saved');
      },
      /* Email signup (SignUpPopup): the account is signed out right after creation, which races
         the post-signup client auto-save. The popup "claims" the pending save BEFORE creating the
         account — disarming the auto-save — and attaches the returned poster server-side via the
         welcome email API. Returns null when no FoxChat save is pending. */
      claimPendingSaveForEmailSignup: async () => {
        if (!pendingSaveAfterSignUpRef.current) return null;
        pendingSaveAfterSignUpRef.current = false;
        try {
          const { posterData } = await composeFoxChatPosterData(null);
          return posterData;
        } catch (err) {
          console.warn('FoxChat poster build for email signup failed:', err);
          pendingSaveAfterSignUpRef.current = true;
          return null;
        }
      },
      /* Re-arm after a failed account creation (e.g. email already in use → login popup),
         so signing in later still triggers the normal post-signup auto-save. */
      restorePendingSaveAfterEmailSignup: () => {
        pendingSaveAfterSignUpRef.current = true;
      },
    });
    return () => registerFoxChatResetEmailAttachHooks(null);
    /* composeFoxChatPosterData closes over latest answers/posterFields — refresh hooks when they change. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    registerFoxChatResetEmailAttachHooks,
    answers.user_photo,
    answers.user_photo_original,
    answers.brands,
    answers.activities,
    answers.places,
    answers.spirit_gif,
    answers.vibe_photo,
    posterFields.posterNickname,
    posterFields.selfGender,
    posterFields.romanticLuv,
    posterFields.friendLuv,
    posterFields.lookingForGenders,
    posterFields.selfAge,
    analyticsSource,
  ]);

  /* Post-signup auto-save: if the user hit SAVE while signed-out (which armed
     `pendingSaveAfterSignUpRef`), fire the save the moment a real uid appears.
     Depend on uid (not the user object) so a token-refresh identity change can't
     cancel a scheduled save and then no-op because the pending flag is already cleared. */
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (!pendingSaveAfterSignUpRef.current) return;
    pendingSaveAfterSignUpRef.current = false;
    void handleSaveCollage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  /* If the signup popup closes WITHOUT the user actually authenticating, drop the pending
     flags so a later sign-in via a different surface (e.g. Navbar login) doesn't silently
     save a collage they may have abandoned. Delay the clear: OAuth often closes the popup
     before React's `currentUser` catches up, and Firebase may already have a real user. */
  useEffect(() => {
    if (signUpPopupOpen || loginPopupOpen || currentUser) return undefined;
    if (!pendingSaveAfterSignUpRef.current) return undefined;
    if (auth.currentUser && !auth.currentUser.isAnonymous) return undefined;
    const t = window.setTimeout(() => {
      if (!pendingSaveAfterSignUpRef.current) return;
      const fbUser = auth.currentUser;
      if (fbUser && !fbUser.isAnonymous) return;
      pendingSaveAfterSignUpRef.current = false;
    }, 2000);
    return () => clearTimeout(t);
  }, [signUpPopupOpen, loginPopupOpen, currentUser]);

  /* Ask "How old are you?" right after the user picks a gender (age now precedes the LUV step). */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (skipGenderLuvAgeSteps) return undefined;
    if (!posterFields.selfGender) return undefined;
    if (agePromptSentRef.current) return undefined;
    agePromptSentRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: `${messageIdPrefix}-age-${Date.now()}`,
        sender: 'fox',
        text: agePromptText,
        timestamp: new Date(),
        ageTiles: true,
      },
    ]);
    return undefined;
  }, [posterFields.selfGender, agePromptText, messageIdPrefix, skipGenderLuvAgeSteps, suppressFoxCollageSurvey]);

  useEffect(() => {
    if (posterFields.selfAge === null) {
      setAgeWheelInteractionUnlocked(true);
    }
  }, [posterFields.selfAge]);

  /* Ask for home base once the LUV step is fully resolved:
     - if the user picked ROMANCE, wait until they confirm their romantic looking-for genders
     - if the user only picked FRIENDS, ask right after they confirm the LUV step */
  useEffect(() => {
    if (suppressFoxCollageSurvey) return undefined;
    if (skipGenderLuvAgeSteps) return undefined;
    if (!luvConfirmed) return undefined;
    const ready = posterFields.romanticLuv
      ? romanticLookingConfirmed
      : posterFields.friendLuv;
    if (!ready) return undefined;
    if (locationPromptSentRef.current) return undefined;
    locationPromptSentRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: `${messageIdPrefix}-location-${Date.now()}`,
        sender: 'fox',
        text: locationPromptText,
        timestamp: new Date(),
        locationTiles: true,
      },
    ]);
    return undefined;
  }, [
    luvConfirmed,
    posterFields.romanticLuv,
    posterFields.friendLuv,
    romanticLookingConfirmed,
    locationPromptText,
    messageIdPrefix,
    skipGenderLuvAgeSteps,
    suppressFoxCollageSurvey,
  ]);

  const handleMoreNicknameOptions = () => {
    const shown = new Set();
    messages.forEach((m) => {
      if (m.nicknameTiles && Array.isArray(m.nicknames)) {
        m.nicknames.forEach((n) => shown.add(n));
      }
    });
    const next = getRandomNicknames(3, Array.from(shown));
    if (!next.length) return;
    setMoreOptionsLoading('nickname');
    /* Nicknames are plain text (nothing to preload), so use the standard ~250ms delay to match the
       cadence of the image-based MORE buttons. Insert right after the latest nicknameTiles bubble
       (the one whose MORE NICKNAMES button was clicked) so the new tiles appear in context, and bump
       the click count in the same tick so the scroll-to-new-bubble effect fires once the bubble is in. */
    setTimeout(() => {
      suppressNextAutoScrollRef.current = true;
      const newId = `${messageIdPrefix}-nickname-more-${Date.now()}`;
      moreOptionsScrollTargetRef.current = newId;
      setMessages((prev) => {
        const newMsg = {
          id: newId,
          sender: 'fox',
          timestamp: new Date(),
          nicknameTiles: true,
          nicknames: next,
        };
        let lastIdx = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].nicknameTiles) {
            lastIdx = i;
            break;
          }
        }
        if (lastIdx === -1) return [...prev, newMsg];
        return [...prev.slice(0, lastIdx + 1), newMsg, ...prev.slice(lastIdx + 1)];
      });
      setNicknameMoreClickCount((c) => c + 1);
      setMoreOptionsLoading(null);
    }, 250);
  };

  const refreshBrandSearchResults = () => {
    if (!(brandSearchResults.length > 0 && brandSearchQuery) || brandSearchLoading) return;
    const next = takeNextSearchPage(brandSearchPoolRef);
    if (!next.length) {
      setBrandSearchHasMore(false);
      return;
    }
    setBrandSearchLoading(true);
    preloadImageUrls(next.map((b) => b.imageUrl)).then(() => {
      setBrandSearchResults(next);
      setBrandSearchHasMore(brandSearchPoolRef.current.length > 0);
    }).finally(() => {
      setBrandSearchLoading(false);
    });
  };

  const handleMoreBrandOptions = () => {
    const shownIds = new Set();
    messages.forEach((m) => {
      if (m.brandTiles && Array.isArray(m.brands)) {
        m.brands.forEach((b) => shownIds.add(b.id));
      }
    });
    const pool = allBrands.filter((b) => !shownIds.has(b.id));
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    const nextBrands = shuffled.slice(0, Math.min(9, shuffled.length));
    setMoreOptionsLoading('brand');
    /* Preload the new tile images before inserting the bubble so it renders fully decoded (no
       half-loaded pop-in). Inserted right after the latest brandTiles bubble (the one whose MORE
       ITEMS button was clicked) so the new tiles appear in context. Bump the click count in the
       same tick as the insert so the scroll-to-new-bubble effect fires once the bubble is in. */
    preloadImageUrls(nextBrands.map((b) => b.imageUrl)).then(() => {
      suppressNextAutoScrollRef.current = true;
      const newId = `${messageIdPrefix}-more-${Date.now()}`;
      moreOptionsScrollTargetRef.current = newId;
      setMessages((prev) => {
        const newMsg = {
          id: newId,
          sender: 'fox',
          timestamp: new Date(),
          brandTiles: true,
          brands: nextBrands,
        };
        let lastIdx = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i].brandTiles) {
            lastIdx = i;
            break;
          }
        }
        if (lastIdx === -1) return [...prev, newMsg];
        return [...prev.slice(0, lastIdx + 1), newMsg, ...prev.slice(lastIdx + 1)];
      });
      setBrandMoreClickCount((c) => c + 1);
      setBrandSearchResults([]);
      setBrandSearchQuery('');
      setBrandSearchHasMore(false);
      setBrandSearchHasText(false);
      brandSearchPoolRef.current = [];
      setBrandSearchFieldKey((k) => k + 1);
      clearSurveySearchCustomAdded('brand');
      setMoreOptionsLoading(null);
    });
  };

  const addBrandToLatestSet = (brand) => {
    suppressNextAutoScrollRef.current = true;
    setMessages((prev) => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].brandTiles) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx === -1) return prev;
      const next = [...prev];
      const target = next[lastIdx];
      const brands = Array.isArray(target.brands) ? [...target.brands] : [];
      if (brands.some((b) => b.id === brand.id)) return prev;
      const replaceIdx = brands.findIndex(
        (b) => !answers.brands.some((s) => s.id === b.id) && b.id !== brand.id,
      );
      if (replaceIdx !== -1) {
        brands[replaceIdx] = brand;
      } else {
        brands.push(brand);
      }
      next[lastIdx] = { ...target, brands };
      return next;
    });
  };

  /* When the user picks an emoji from the search results, splice it into the latest emojiTiles
     bubble (replacing an unpicked tile, or appending if all are picked) so the picked emoji
     becomes part of the visible 9-grid. Mirrors addBrandToLatestSet for parity with the STUFF
     picker's search → tile flow. */
  const addEmojiToLatestSet = (emoji) => {
    suppressNextAutoScrollRef.current = true;
    setMessages((prev) => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].emojiTiles) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx === -1) return prev;
      const next = [...prev];
      const target = next[lastIdx];
      const emojis = Array.isArray(target.emojis) ? [...target.emojis] : [];
      if (emojis.includes(emoji)) return prev;
      const replaceIdx = emojis.findIndex(
        (e) => !answers.activities.includes(e) && e !== emoji,
      );
      if (replaceIdx !== -1) {
        emojis[replaceIdx] = emoji;
      } else {
        emojis.push(emoji);
      }
      next[lastIdx] = { ...target, emojis };
      return next;
    });
  };

  /* Copy a custom search-result image into Firebase Storage, then swap the tile URL so SAVE
     doesn't depend on ephemeral Brave / Places / KLIPY links. Emoji picks have no image. */
  const persistCustomSearchResultImage = (kind, sourceUrl, applyStableUrl) => {
    if (!sourceUrl || typeof sourceUrl !== 'string' || !/^https?:\/\//i.test(sourceUrl)) return;
    if (/firebasestorage\.googleapis\.com|storage\.googleapis\.com/i.test(sourceUrl)) return;
    persistSearchResultImage(sourceUrl, kind)
      .then((stableUrl) => {
        if (!stableUrl || stableUrl === sourceUrl) return;
        suppressNextAutoScrollRef.current = true;
        applyStableUrl(stableUrl);
      })
      .catch((err) => {
        console.warn('Could not persist custom search image:', err);
      });
  };

  /* Walk the chat in order and return the id of the FIRST tile bubble whose answer is missing
     or incomplete. Returns null if every required answer is present. Used as a guard before the
     photo prompt fires (see the "scroll-and-shake" effect below). */
  const findFirstUnansweredMsgId = () => {
    const seen = {
      brand: false,
      emoji: false,
      place: false,
      gif: false,
      nickname: false,
      gender: false,
      luv: false,
      rmnLook: false,
      age: false,
      location: false,
    };
    for (let i = 0; i < messages.length; i += 1) {
      const m = messages[i];
      if (!seen.brand && m.brandTiles) {
        seen.brand = true;
        if (answers.brands.length < BRAND_PICK_MAX) return m.id;
      } else if (!seen.emoji && m.emojiTiles) {
        seen.emoji = true;
        if (answers.activities.length < EMOJI_PICK_MAX) return m.id;
      } else if (!seen.place && m.placeTiles) {
        seen.place = true;
        if (answers.places.length < PLACE_PICK_MAX) return m.id;
      } else if (!seen.gif && m.gifTiles) {
        seen.gif = true;
        if (!answers.spirit_gif) return m.id;
      } else if (!seen.nickname && m.nicknameTiles) {
        seen.nickname = true;
        const nick = posterFields.posterNickname;
        if (!nick || (typeof nick === 'string' && !nick.trim())) return m.id;
      } else if (!seen.gender && m.genderTiles) {
        seen.gender = true;
        if (!posterFields.selfGender) return m.id;
      } else if (!seen.luv && m.luvTiles) {
        seen.luv = true;
        if (!luvConfirmed || (!posterFields.romanticLuv && !posterFields.friendLuv)) return m.id;
      } else if (!seen.rmnLook && m.romanticLookingTiles) {
        seen.rmnLook = true;
        if (
          posterFields.romanticLuv
          && (!romanticLookingConfirmed || posterFields.lookingForGenders.length === 0)
        ) {
          return m.id;
        }
      } else if (!seen.age && m.ageTiles) {
        seen.age = true;
        if (posterFields.selfAge === null) return m.id;
      } else if (!seen.location && m.locationTiles) {
        seen.location = true;
        const homeBase = posterFields.homeBase;
        if (!homeBase || (typeof homeBase === 'string' && !homeBase.trim())) return m.id;
      }
    }
    return null;
  };

  /** Scroll only `.messaging-messages` — never `scrollIntoView` on thread nodes (that scrolls the homepage). */
  const handleFoxChatPanelWheel = useCallback((e) => {
    const container = messagesScrollRef.current;
    if (!container || container.contains(e.target)) return;
    if (e.target.closest?.(
      '.messaging-gif-grid-scroll, .fox-chat-streak-carousel, .fox-chat-input-bar, .fox-chat-selected-bar',
    )) {
      return;
    }
    const maxScroll = container.scrollHeight - container.clientHeight;
    if (maxScroll <= 0) return;
    const prev = container.scrollTop;
    container.scrollTop = Math.max(0, Math.min(maxScroll, prev + e.deltaY));
    if (container.scrollTop !== prev) {
      e.preventDefault();
    }
  }, []);

  const scrollWithinFoxMessagesPanel = (element, block, behavior = 'smooth') => {
    const container = messagesScrollRef.current;
    if (!element) return;
    if (!container) {
      element.scrollIntoView?.({ behavior, block: 'nearest', inline: 'nearest' });
      return;
    }
    const cRect = container.getBoundingClientRect();
    const eRect = element.getBoundingClientRect();
    const st = container.scrollTop;
    const elTop = eRect.top - cRect.top + st;
    const elH = eRect.height;
    const cH = cRect.height;
    let nextTop = st;
    if (block === 'end') {
      nextTop = elTop + elH - cH;
    } else {
      nextTop = elTop + elH / 2 - cH / 2;
    }
    const maxTop = Math.max(0, container.scrollHeight - cH);
    container.scrollTo({ top: Math.min(maxTop, Math.max(0, nextTop)), behavior });
  };

  /** After a tile-step search submit, scroll so the bottom of that card (incl. results) is visible. */
  const scrollSurveyTileCardToBottom = (msgId) => {
    if (!msgId || typeof document === 'undefined') return;
    suppressNextAutoScrollRef.current = true;
    const stuffEl = document.getElementById(`${domIdPrefix}-stuff-${msgId}`);
    if (stuffEl) {
      scrollWithinFoxMessagesPanel(stuffEl, 'end');
      return;
    }
    const textEl = document.getElementById(`${domIdPrefix}-msg-${msgId}`);
    if (!textEl) return;
    let sib = textEl.nextElementSibling;
    while (sib) {
      if (sib.classList?.contains('fox-chat-stuff-bubble')) {
        scrollWithinFoxMessagesPanel(sib, 'end');
        break;
      }
      sib = sib.nextElementSibling;
    }
  };

  const scrollFoxMessagesToBottom = (behavior = 'smooth') => {
    const container = messagesScrollRef.current;
    if (!container) {
      messagesEndRef.current?.scrollIntoView?.({ behavior });
      return;
    }
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTo({ top: maxTop, behavior });
  };

  /* Scroll the bubble for `msgId` into view (centered) and trigger the shake animation on it.
     We clear the shake class for one frame before re-applying it so the keyframe restarts even
     when the same message id is re-shaken back-to-back. The auto-scroll-on-new-messages effect
     is briefly suppressed so it doesn't fight this manual scroll on the next render. */
  const triggerShakeAtMessage = (msgId) => {
    if (!msgId) return;
    suppressNextAutoScrollRef.current = true;
    const el = typeof document !== 'undefined'
      ? document.getElementById(`${domIdPrefix}-msg-${msgId}`)
      : null;
    if (el) {
      scrollWithinFoxMessagesPanel(el, 'center');
    }
    if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    setShakeMessageId(null);
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        setShakeMessageId(msgId);
      });
    } else {
      setShakeMessageId(msgId);
    }
    shakeTimerRef.current = setTimeout(() => {
      setShakeMessageId((cur) => (cur === msgId ? null : cur));
    }, 700);
  };

  const firstIncompleteFoxSurveyStepIndex = useMemo(
    () =>
      getFirstIncompleteFoxSurveyStepIndex(
        answers,
        posterFields,
        luvConfirmed,
        romanticLookingConfirmed,
        skipGenderLuvAgeSteps,
      ),
    [
      answers.brands.length,
      answers.activities.length,
      answers.places.length,
      answers.spirit_gif,
      posterFields.posterNickname,
      posterFields.selfGender,
      posterFields.romanticLuv,
      posterFields.friendLuv,
      posterFields.lookingForGenders.length,
      posterFields.selfAge,
      posterFields.homeBase,
      luvConfirmed,
      romanticLookingConfirmed,
      skipGenderLuvAgeSteps,
    ],
  );

  /* Profile-setup progress (collage builder only). 10 questions; LUV + romantic-looking share #8. */
  const foxSetupProgress = useMemo(
    () => getFoxSetupQuestionProgress(
      answers,
      posterFields,
      luvConfirmed,
      romanticLookingConfirmed,
    ),
    [
      answers.brands.length,
      answers.activities.length,
      answers.places.length,
      answers.spirit_gif,
      answers.user_photo,
      posterFields.posterNickname,
      posterFields.selfGender,
      posterFields.romanticLuv,
      posterFields.friendLuv,
      posterFields.lookingForGenders.length,
      posterFields.selfAge,
      posterFields.homeBase,
      luvConfirmed,
      romanticLookingConfirmed,
    ],
  );

  /* Only the collage-builder version of the chat has setup questions — hide the bar in the
     daily-vibe-check (pre-unmask) and ops flows. Once every question is answered it stays
     visible at 100% rather than disappearing. */
  const showFoxSetupProgress =
    !showPreUnmaskGifChat
    && !opsSendToOpsFlow
    && foxSetupProgress.total > 0;

  const appendKlipyGifs = async (query, isSearch = false) => {
    const gen = ++chatKlipyFetchGenRef.current;
    setLoadingChatGifs(true);
    try {
      const customerId = currentUser?.uid || `anon_${Date.now()}`;
      const remote = await searchSpiritGifsApi(query, customerId, KLIPY_PICKER_PAGE);
      if (gen !== chatKlipyFetchGenRef.current) return;
      const merged = isSearch
        ? mergeKlipyThenLocal(remote, localChatGifsBaseRef.current)
        : mergeLocalThenKlipy(localChatGifsBaseRef.current, remote);
      setChatGifs(merged);
      if (isSearch) {
        requestAnimationFrame(() => {
          const el = chatGifGridScrollRef.current;
          if (el) el.scrollTop = 0;
        });
      }
    } catch (err) {
      console.error('Fox chat GIF search error:', err);
    } finally {
      if (gen === chatKlipyFetchGenRef.current) {
        setLoadingChatGifs(false);
      }
    }
  };

  /* Logged-in gif chat: replace stale signed-out / pre-auth survey greeting once auth is ready.
     Do not carry survey GIFs into the daily-check thread — today's check is restored below. */
  useEffect(() => {
    if (!isLoggedInSelfFoxChat || foxSurveyInProgress) return;

    let needsSurveyReset = false;
    setMessages((prev) => {
      const foxGreeting = prev.find(isFoxDailyCheckOpeningMessage);
      const hasSurveyMessages = prev.some(isFoxSurveyMessage);

      if (!hasSurveyMessages && foxGreeting) return prev;

      if (hasSurveyMessages) needsSurveyReset = true;

      return assembleDailyCheckThread({
        messageIdPrefix,
        greetingText: dailyCheckGreetingText,
        computingReplyText: dailyCheckComputingReplyText,
        userGifMessage: null,
        greetingTimestamp: resolveDailyCheckGreetingTimestamp(currentUser?.uid, null),
      });
    });

    if (needsSurveyReset) resetFoxSurveySession();
  }, [
    isLoggedInSelfFoxChat,
    foxSurveyInProgress,
    isOpen,
    messageIdPrefix,
    dailyCheckGreetingText,
    dailyCheckComputingReplyText,
  ]);

  /* Signed in with a primary collage while the builder still holds a collage that is already
     saved (SAVED! with no edits since): drop the stale builder so the chat shows the daily vibe
     check instead of the old selections. Never runs while a collage is unsaved (idle / saving /
     error), so signing in can't discard work in progress. For the signed-in SAVE we wait until
     HomePage closes the panel so SAVED! is still shown before the redirect; the signed-out
     email-signup save leaves the panel open, so reset right away there. */
  useEffect(() => {
    if (!isLoggedInSelfFoxChat || embeddedInInbox) return;
    if (!foxSurveyInProgress || saveState !== 'saved') return;
    if (isOpen && !savedToEmailSignupProfile && !savedAwaitingEmail) return;
    resetFoxSurveySession();
    photoStorageUrlRef.current = null;
    clearSignedOutSaveHintTimer();
    setSaveState('idle');
    setSavedPosterId(null);
    setSavedToEmailSignupProfile(false);
    setSavedAwaitingEmail(false);
    setSignedOutSaveHintEarly(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedInSelfFoxChat,
    foxSurveyInProgress,
    saveState,
    isOpen,
    embeddedInInbox,
    savedToEmailSignupProfile,
    savedAwaitingEmail,
  ]);

  const prevDailyVibeUidRef = useRef(currentUser?.uid);

  useEffect(() => {
    if (prevDailyVibeUidRef.current !== currentUser?.uid) {
      dailyVibeHydratedPeriodRef.current = null;
      prevDailyVibeUidRef.current = currentUser?.uid;
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    dailyVibeHydratedPeriodRef.current = null;
  }, [dailyVibePeriodKey]);

  /* Record when the user first sees today's daily-check greeting (stable across re-opens). */
  useEffect(() => {
    if (!currentUser?.uid || !isLoggedInSelfFoxChat || foxSurveyInProgress) return;
    if (!isOpen && !embeddedInInbox) return;

    const greetingTimestamp = resolveDailyCheckGreetingTimestamp(currentUser.uid, null, {
      recordIfMissing: true,
    });
    setMessages((prev) => {
      const greeting = prev.find(isFoxDailyCheckOpeningMessage);
      if (!greeting) return prev;
      const currentMs = greeting.timestamp instanceof Date
        ? greeting.timestamp.getTime()
        : new Date(greeting.timestamp).getTime();
      if (currentMs === greetingTimestamp.getTime()) return prev;
      return prev.map((m) => (
        isFoxDailyCheckOpeningMessage(m)
          ? { ...m, timestamp: greetingTimestamp }
          : m
      ));
    });
  }, [
    currentUser?.uid,
    isLoggedInSelfFoxChat,
    foxSurveyInProgress,
    isOpen,
    embeddedInInbox,
  ]);

  /* Messages "GET A NEW MATCH": while this token is in flight, don't restore the match
     already stamped for today — the search below replaces it. */
  const requestAnotherMatchTokenRef = useRef(requestAnotherMatchToken);
  const suppressPriorMatchRestoreRef = useRef(requestAnotherMatchToken > 0);
  const handledAnotherMatchTokenRef = useRef(0);
  const anotherMatchStartedTokenRef = useRef(0);
  const anotherMatchSettledRef = useRef(false);
  if (requestAnotherMatchTokenRef.current !== requestAnotherMatchToken) {
    requestAnotherMatchTokenRef.current = requestAnotherMatchToken;
    if (requestAnotherMatchToken > 0) {
      suppressPriorMatchRestoreRef.current = true;
      anotherMatchSettledRef.current = false;
      anotherMatchStartedTokenRef.current = 0;
      handledAnotherMatchTokenRef.current = 0;
    }
  }
  /* Restore today's daily-check thread when the panel opens (Firestore + local cache).
     FoxChat stays mounted across logout/login and overnight, so in-memory messages can
     still hold yesterday's GIF — only keep a local GIF when it belongs to this period. */
  useEffect(() => {
    if (!currentUser?.uid || !isLoggedInSelfFoxChat || foxSurveyInProgress) return undefined;
    if (!isOpen && !embeddedInInbox) return undefined;

    const periodKey = dailyVibePeriodKey;
    let cancelled = false;
    const skipPriorMatch = () => (
      requestAnotherMatchToken > 0
      && suppressPriorMatchRestoreRef.current
      && !anotherMatchSettledRef.current
      && handledAnotherMatchTokenRef.current !== requestAnotherMatchToken
    );

    const applyDailyCheckThread = (check) => {
      setMessages((prev) => {
        const localGif = prev.find((m) => m.sender === 'me' && m.gifUrl);
        const existingGreeting = prev.find(isFoxDailyCheckOpeningMessage);
        const existingReply = prev.find(isFoxDailyCheckComputingReplyMessage);
        const localGifIsCurrentPeriod = Boolean(
          localGif && dailyVibePeriodKeyForTimestamp(localGif.timestamp) === periodKey,
        );
        const greetingFromCurrentPeriod = Boolean(
          existingGreeting
          && dailyVibePeriodKeyForTimestamp(existingGreeting.timestamp) === periodKey,
        );
        const greetingTimestamp = resolveDailyCheckGreetingTimestamp(
          currentUser.uid,
          greetingFromCurrentPeriod ? existingGreeting : null,
        );
        const computingReplyTimestamp = localGifIsCurrentPeriod
          ? (existingReply?.timestamp ?? null)
          : null;

        if (localGifIsCurrentPeriod) {
          dailyVibeHydratedPeriodRef.current = periodKey;
          /* The match can change mid-period (Messages "GET A NEW MATCH" runs in the
             embedded FoxChat while this floating instance keeps its own messages), so
             only keep the in-memory thread when its "Found someone!" still points at
             the cached conversation. */
          const cachedMatchFound = skipPriorMatch()
            ? null
            : readDailyCheckMatchFoundForThread(currentUser.uid);
          const existingMatchFound = prev.find(isFoxDailyCheckMatchFoundMessage);
          const matchFoundInSync =
            (existingMatchFound?.matchConversationId || null)
            === (cachedMatchFound?.conversationId || null);
          if (existingGreeting && existingReply && greetingFromCurrentPeriod && matchFoundInSync) {
            return prev;
          }
          return assembleDailyCheckThread({
            messageIdPrefix,
            greetingText: dailyCheckGreetingText,
            computingReplyText: dailyCheckComputingReplyText,
            userGifMessage: localGif,
            greetingTimestamp,
            computingReplyTimestamp,
            matchFoundText: dailyCheckMatchFoundText,
            matchFound: cachedMatchFound,
            checkOutcome: skipPriorMatch() ? null : readDailyCheckOutcomeForThread(currentUser.uid),
            gateTexts: { verify: dailyCheckVerifyGateText },
            noMatchesText: dailyCheckNoMatchesText,
          });
        }

        if (check?.gifUrl) {
          const ts = check.createdAt?.toDate?.() || new Date(check.timestamp || Date.now());
          writeDailyVibeChatCache(currentUser.uid, {
            gifUrl: check.gifUrl,
            checkId: check.id || check.checkId || null,
            gifId: check.gifId || null,
            timestamp: ts.getTime(),
          }, periodKey);
          dailyVibeHydratedPeriodRef.current = periodKey;
          return assembleDailyCheckThread({
            messageIdPrefix,
            greetingText: dailyCheckGreetingText,
            computingReplyText: dailyCheckComputingReplyText,
            userGifMessage: {
              id: `me-gif-${check.id || check.checkId || ts.getTime()}`,
              sender: 'me',
              gifUrl: check.gifUrl,
              timestamp: ts,
            },
            greetingTimestamp,
            computingReplyTimestamp,
            matchFoundText: dailyCheckMatchFoundText,
            matchFound: skipPriorMatch() ? null : readDailyCheckMatchFoundForThread(currentUser.uid),
            checkOutcome: skipPriorMatch() ? null : readDailyCheckOutcomeForThread(currentUser.uid),
            gateTexts: { verify: dailyCheckVerifyGateText },
            noMatchesText: dailyCheckNoMatchesText,
          });
        }

        dailyVibeHydratedPeriodRef.current = periodKey;
        if (existingGreeting && greetingFromCurrentPeriod && !localGif) return [existingGreeting];
        return assembleDailyCheckThread({
          messageIdPrefix,
          greetingText: dailyCheckGreetingText,
          computingReplyText: dailyCheckComputingReplyText,
          userGifMessage: null,
          greetingTimestamp,
        });
      });
    };

    const cached = readDailyVibeChatCache(currentUser.uid, periodKey);
    applyDailyCheckThread(cached?.gifUrl ? cached : null);

    (async () => {
      try {
        const check = await loadDailyVibeCheckForPeriod(db, currentUser.uid, periodKey);
        if (cancelled || anotherMatchSettledRef.current) return;
        /* Keep the day's match list current (another device may have matched), so
           the daily-limit fallback and the Messages counter see every match. */
        if (check?.matchConversationIds?.length) {
          syncDailyVibeMatchIds(currentUser.uid, check.matchConversationIds, periodKey);
        }
        if (skipPriorMatch() && !check?.gifUrl && !readDailyVibeChatCache(currentUser.uid, periodKey)?.gifUrl) {
          /* No daily GIF yet — leave the picker up and let the first send run the normal search. */
          suppressPriorMatchRestoreRef.current = false;
          handledAnotherMatchTokenRef.current = requestAnotherMatchToken;
        }
        /* A match stamped on the check doc (possibly from another device) wins over
           the empty local cache — record it first so thread assembly includes it.
           A pending "get a new match" keeps that stamp off the thread until the
           fresh search replaces it. */
        if (!skipPriorMatch() && check?.matchConversationId) {
          recordDailyVibeMatchFound(currentUser.uid, {
            conversationId: check.matchConversationId,
            foundAt: typeof check.matchFoundAt === 'number' ? check.matchFoundAt : Date.now(),
          }, periodKey);
        }
        if (anotherMatchSettledRef.current) return;
        applyDailyCheckThread(check?.gifUrl ? check : null);
      } catch (err) {
        if (!cancelled) {
          console.warn('Daily vibe check load failed:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.uid,
    isLoggedInSelfFoxChat,
    foxSurveyInProgress,
    isOpen,
    embeddedInInbox,
    dailyVibePeriodKey,
    messageIdPrefix,
    dailyCheckGreetingText,
    dailyCheckComputingReplyText,
    dailyCheckMatchFoundText,
    requestAnotherMatchToken,
  ]);

  /* Guards the once-per-period VVTF match check (keyed by uid + vibe period). */
  const dailyMatchCheckKeyRef = useRef(null);
  /* Tracks `profileVerified` so a false->true flip can re-run the gated match check. */
  const lastProfileVerifiedRef = useRef(profileVerified);

  useEffect(() => {
    dailyMatchCheckKeyRef.current = null;
  }, [currentUser?.uid, dailyVibePeriodKey]);

  /* While "computing a new match…" is shown, look for unseen newmatches entries.
     If one exists, initiate the match conversation (client mirror of the ops
     createVvtfMatchedConversation flow) and reply "Found someone!" with an
     OPEN MESSAGES CTA. Cached per period so the reply survives re-opens. */
  useEffect(() => {
    if (!currentUser?.uid || !showPreUnmaskGifChat || !dailyCheckGifSent) return undefined;

    const uid = currentUser.uid;
    if (dailyCheckGifPeriodKey !== dailyVibePeriodKey) {
      return undefined;
    }
    const checkKey = `${uid}:${dailyVibePeriodKey}`;

    /* If the profile just became verified while the "verify" gate is showing, drop the
       cached gate + in-memory guard so the check below re-runs and finds a real match. */
    const wasVerified = lastProfileVerifiedRef.current === true;
    lastProfileVerifiedRef.current = profileVerified === true;
    const justVerified = profileVerified === true && !wasVerified;
    if (justVerified) {
      const outcome = readDailyVibeCheckOutcome(uid);
      const hasVerifyGate = outcome?.type === 'gate' && outcome.gate === 'verify';
      if (hasVerifyGate) {
        clearDailyVibeCheckOutcome(uid);
        dailyMatchCheckKeyRef.current = null;
        setMessages((prev) => prev.filter(
          (m) => !(isFoxDailyCheckGateMessage(m) && m.foxDailyCheckGate === 'verify'),
        ));
      }
    }

    const appendMatchFoundMessage = (conversationId, timestamp) => {
      setMessages((prev) => {
        const existing = prev.find(isFoxDailyCheckMatchFoundMessage);
        if (existing && (existing.matchConversationId || null) === (conversationId || null)) {
          return prev;
        }
        /* A different conversation: replace the stale "Found someone!" rather than keep it. */
        const withoutTerminal = prev.filter(
          (m) => !isFoxDailyCheckMatchFoundMessage(m)
            && !isFoxDailyCheckGateMessage(m)
            && !isFoxDailyCheckNoMatchesMessage(m),
        );
        return [
          ...withoutTerminal,
          buildFoxDailyCheckMatchFoundMessage(
            messageIdPrefix,
            dailyCheckMatchFoundText,
            conversationId,
            timestamp,
          ),
        ];
      });
    };

    const runAnotherMatch = requestAnotherMatchToken > 0
      && !anotherMatchSettledRef.current
      && handledAnotherMatchTokenRef.current !== requestAnotherMatchToken
      && anotherMatchStartedTokenRef.current !== requestAnotherMatchToken;

    if (
      requestAnotherMatchToken > 0
      && !runAnotherMatch
      && handledAnotherMatchTokenRef.current !== requestAnotherMatchToken
      && !anotherMatchSettledRef.current
    ) {
      /* This token's search is already in flight. */
      return undefined;
    }

    if (runAnotherMatch) {
      anotherMatchStartedTokenRef.current = requestAnotherMatchToken;
      clearDailyVibeMatchFound(uid, dailyVibePeriodKey);
      clearDailyVibeCheckOutcome(uid, dailyVibePeriodKey);
      const computingAt = new Date();
      setMessages((prev) => {
        const withoutTerminal = prev.filter(
          (m) => !isFoxDailyCheckMatchFoundMessage(m)
            && !isFoxDailyCheckGateMessage(m)
            && !isFoxDailyCheckNoMatchesMessage(m),
        );
        const withFreshComputing = withoutTerminal.map((m) => (
          isFoxDailyCheckComputingReplyMessage(m) ? { ...m, timestamp: computingAt } : m
        ));
        if (withFreshComputing.some(isFoxDailyCheckComputingReplyMessage)) return withFreshComputing;
        return [
          ...withFreshComputing,
          buildFoxDailyCheckComputingReplyMessage(
            messageIdPrefix,
            dailyCheckComputingReplyText,
            computingAt,
          ),
        ];
      });
    } else {
      const cachedMatch = readDailyVibeMatchFound(uid);
      if (cachedMatch?.conversationId) {
        appendMatchFoundMessage(
          cachedMatch.conversationId,
          new Date(cachedMatch.foundAt || Date.now()),
        );
        return undefined;
      }

      if (dailyMatchCheckKeyRef.current === checkKey) return undefined;
      dailyMatchCheckKeyRef.current = checkKey;
    }

    let cancelled = false;
    /* The "get a new match" search is guarded by refs shared across effect re-runs
       (StrictMode remount, dep change mid-flight), and a re-run is blocked above as
       "in flight". So this one request must land its own result — bailing on cleanup
       would leave the thread on "computing…" forever. On a real unmount setMessages
       is a no-op. The normal daily check keeps the old cancel-on-cleanup behaviour. */
    const bail = () => cancelled && !runAnotherMatch;
    (async () => {
      const finishAnotherMatch = () => {
        if (!runAnotherMatch) return;
        anotherMatchSettledRef.current = true;
        suppressPriorMatchRestoreRef.current = false;
        handledAnotherMatchTokenRef.current = requestAnotherMatchToken;
      };

      const appendGateMessage = (gate, gateText) => {
        finishAnotherMatch();
        recordDailyVibeCheckOutcome(uid, { type: 'gate', gate });
        let addedNew = false;
        setMessages((prev) => {
          const existingGate = prev.find(isFoxDailyCheckGateMessage);
          if (existingGate?.foxDailyCheckGate === gate) return prev;
          addedNew = true;
          const withoutTerminal = prev.filter(
            (m) => !isFoxDailyCheckGateMessage(m) && !isFoxDailyCheckNoMatchesMessage(m),
          );
          return [...withoutTerminal, buildFoxDailyCheckGateMessage(messageIdPrefix, gate, gateText)];
        });
        if (gate === 'verify' && addedNew && typeof onVerifyProfile === 'function') {
          window.setTimeout(() => onVerifyProfile(), 0);
        }
      };

      const showMatch = (conversationId, foundAt) => {
        finishAnotherMatch();
        recordDailyVibeMatchFound(uid, { conversationId, foundAt });
        clearDailyVibeCheckOutcome(uid);
        appendMatchFoundMessage(conversationId, new Date(foundAt));
      };

      const appendNoMatchesMessage = () => {
        finishAnotherMatch();
        recordDailyVibeCheckOutcome(uid, { type: 'noMatches' });
        setMessages((prev) => {
          if (prev.some(isFoxDailyCheckNoMatchesMessage)) return prev;
          const withoutGate = prev.filter((m) => !isFoxDailyCheckGateMessage(m));
          return [...withoutGate, buildFoxDailyCheckNoMatchesMessage(messageIdPrefix, dailyCheckNoMatchesText)];
        });
      };

      /* Returns true when the server produced a terminal reply (gate or match). */
      const applyServerResult = (result) => {
        if (result?.status === 'gate' && result.gate === 'verify') {
          appendGateMessage('verify', dailyCheckVerifyGateText);
          return true;
        }
        if (result?.status === 'dailyLimit') {
          const lastId = readDailyVibeMatchIds(uid).slice(-1)[0];
          if (lastId) showMatch(lastId, Date.now());
          else appendNoMatchesMessage();
          return true;
        }
        if (result?.status === 'match' && result.conversationId) {
          showMatch(result.conversationId, Number(result.foundAt) || Date.now());
          return true;
        }
        return false;
      };

      try {
        /* Server-side (functions/vvtfMatchInitiate.js): verified-profile gate,
           cross-device restore from this period's check doc,
           candidate re-verification, conversation + intro seeding, seen-flip and
           check-doc stamp. The client only renders the outcome. */
        const periodKey = dailyVibePeriodKey;
        const result = await initiateMyVvtfMatchApi(periodKey, { another: runAnotherMatch });
        if (bail()) return;
        if (applyServerResult(result)) return;

        /* No unseen matches: run the match engine on demand and retry once.
           The daily check only scans once per period; an explicit "get a new match"
           may scan again so the next candidate can be stored. */
        if (!runAnotherMatch && readDailyVibeMatchEngineRan(uid)) {
          appendNoMatchesMessage();
          return;
        }
        recordDailyVibeMatchEngineRan(uid);
        await computeMyNewMatchesApi();
        if (bail()) return;
        const retry = await initiateMyVvtfMatchApi(periodKey, { another: runAnotherMatch });
        if (bail()) return;
        if (applyServerResult(retry)) return;
        appendNoMatchesMessage();
      } catch (err) {
        if (bail()) return;
        console.warn('VVTF new-match check failed:', err);
        finishAnotherMatch();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.uid,
    profileVerified,
    showPreUnmaskGifChat,
    dailyCheckGifSent,
    dailyCheckGifPeriodKey,
    dailyVibePeriodKey,
    messageIdPrefix,
    dailyCheckMatchFoundText,
    dailyCheckVerifyGateText,
    dailyCheckNoMatchesText,
    dailyCheckComputingReplyText,
    onVerifyProfile,
    requestAnotherMatchToken,
  ]);

  /* Keep the vibe-period key current across 3am, tab wake, and login without a full reload. */
  useEffect(() => {
    const syncPeriodKey = () => {
      const next = getDailyVibePeriodKey();
      setDailyVibePeriodKey((prev) => (prev === next ? prev : next));
    };

    let timerId = null;
    const armReset = () => {
      const delay = getNextDailyVibePeriodStart().getTime() - Date.now();
      timerId = window.setTimeout(() => {
        syncPeriodKey();
        armReset();
      }, Math.max(delay, 0));
    };
    armReset();

    const onResume = () => syncPeriodKey();
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      if (timerId) window.clearTimeout(timerId);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setChatGifSearch('');
    }
  }, [isOpen]);

  /* Load this month's daily-check GIFs for the streak day carousel. */
  useEffect(() => {
    if (!currentUser?.uid || !showPreUnmaskGifChat) return undefined;
    if (!isOpen && !embeddedInInbox) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const byDay = await loadDailyVibeChecksForMonth(db, currentUser.uid);
        if (!cancelled) setMonthVibeGifs(byDay);
      } catch (err) {
        if (!cancelled) console.warn('Daily vibe month load failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, showPreUnmaskGifChat, isOpen, embeddedInInbox]);

  /* Keep the streak carousel scrolled to the latest day. */
  useEffect(() => {
    const el = streakCarouselRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [monthVibeGifs, isOpen, embeddedInInbox]);

  useEffect(() => {
    if (!isOpen || !showPreUnmaskGifChat) {
      chatSpiritGifWarmupRef.current = false;
      return;
    }
    localChatGifsBaseRef.current = getShuffledMoodAndHelloGifsAll();
    setChatGifs(mergeLocalThenKlipy(localChatGifsBaseRef.current, []));
    setChatGifSearch('');
    void appendKlipyGifs('sf vibes', false);
    if (chatSpiritGifWarmupRef.current) return;
    chatSpiritGifWarmupRef.current = true;
    const customerId = currentUser?.uid || 'anonymous';
    searchSpiritGifsApi('warmup', customerId).catch(() => {});
  }, [isOpen, showPreUnmaskGifChat, currentUser?.uid]);

  useEffect(() => {
    if (isOpen || embeddedInInbox) setHasOpenedOnce(true);
  }, [isOpen, embeddedInInbox]);

  useEffect(() => {
    if (!embeddedInInbox) return;
    onInboxThreadOpened?.();
  }, [embeddedInInbox, onInboxThreadOpened]);

  useEffect(() => {
    if (launcherVisible && !hasVibratedOnce) {
      const t = setTimeout(() => setHasVibratedOnce(true), 2200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [launcherVisible, hasVibratedOnce]);

  useEffect(() => {
    if (!isOpen) {
      foxChatOpenedScrollRef.current = false;
      return undefined;
    }
    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false;
      return undefined;
    }
    const firstScrollAfterOpen = !foxChatOpenedScrollRef.current;
    foxChatOpenedScrollRef.current = true;
    const behavior = firstScrollAfterOpen ? 'auto' : 'smooth';
    const run = () => {
      const missingId = findFirstUnansweredMsgId();
      if (missingId && typeof document !== 'undefined') {
        const anchor = document.getElementById(`${domIdPrefix}-msg-${missingId}`);
        if (anchor) {
          /* Prefer the stuff bubble (tiles + MORE ITEMS / MORE EMOJIS / …) so the bottom rows
             scroll into view, not only the prompt text centered above the fold. */
          let scrollTarget = anchor;
          let sib = anchor.nextElementSibling;
          while (sib) {
            if (sib.classList?.contains('fox-chat-stuff-bubble')) {
              scrollTarget = sib;
              break;
            }
            sib = sib.nextElementSibling;
          }
          scrollWithinFoxMessagesPanel(scrollTarget, 'end', behavior);
          return;
        }
      }
      scrollFoxMessagesToBottom(behavior);
    };
    /* Wait for survey-gate re-renders so the next bubble exists before we query its id. */
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(run);
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isOpen, messages, firstIncompleteFoxSurveyStepIndex, domIdPrefix]);

  /* After tile-step search submit, scroll the card bottom into view once results are painted. */
  useEffect(() => {
    const msgId = surveySearchScrollTargetRef.current;
    if (!msgId) return;
    if (gifSearchLoading || placeSearchLoading || brandSearchLoading) return;
    surveySearchScrollTargetRef.current = null;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => scrollSurveyTileCardToBottom(msgId));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [
    gifSearchResults,
    gifSearchLoading,
    placeSearchResults,
    placeSearchLoading,
    brandSearchResults,
    brandSearchLoading,
    emojiSearchResults,
  ]);

  /* After a MORE button inserts a new options bubble, scroll to that bubble's bottom
     (tiles + search field) so the new options aren't hidden above the fold. */
  useEffect(() => {
    const msgId = moreOptionsScrollTargetRef.current;
    if (!msgId) return undefined;
    let cancelled = false;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        if (moreOptionsScrollTargetRef.current !== msgId) return;
        moreOptionsScrollTargetRef.current = null;
        scrollSurveyTileCardToBottom(msgId);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [
    messages,
    brandMoreClickCount,
    emojiMoreClickCount,
    placeMoreClickCount,
    gifMoreClickCount,
    nicknameMoreClickCount,
  ]);

  useEffect(() => {
    if (!isOpen || brandSearchWarmupRef.current) return;
    brandSearchWarmupRef.current = true;
    searchBrandImages('warmup', 1).catch(() => {});
  }, [isOpen]);

  /* Warm searchPlaces (Firebase + Google Places) once the places step is on screen. */
  useEffect(() => {
    if (!lastPlaceTilesId || placeSearchWarmupRef.current) return;
    placeSearchWarmupRef.current = true;
    searchPlacesApi('warmup').catch(() => {});
  }, [lastPlaceTilesId]);

  /* Warm searchSpiritGifs (Firebase + KLIPY) once the GIF step is on screen. */
  useEffect(() => {
    if (!lastGifTilesId || gifSearchWarmupRef.current) return;
    gifSearchWarmupRef.current = true;
    searchSpiritGifsApi('warmup', 'anonymous').catch(() => {});
  }, [lastGifTilesId]);

  useEffect(() => {
    if (!isOpen || !messageInputEnabled) return undefined;
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 120);
    return () => clearTimeout(t);
  }, [isOpen, messageInputEnabled]);

  /* When the launcher unmounts on open, Safari hands focus to the next tabbable element — usually
     the navbar SIGN IN link sitting under the × (or it leaves a homepage input focused under the
     overlay). That makes the first tap on × only clear that focus, so it takes two taps to close,
     and leaves a sticky :hover on SIGN IN. Claim focus for the chat window itself (a tabIndex=-1
     container, so no focus ring) the moment it opens so the browser never falls back to SIGN IN.
     useLayoutEffect runs right after the commit that removes the launcher, before paint; the rAF
     pass re-claims focus in case Safari moves it asynchronously after the launcher unmounts. */
  useLayoutEffect(() => {
    if (!isOpen || embeddedInInbox) return undefined;
    const focusPanel = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const active = document.activeElement;
      if (active && active !== document.body && panel.contains(active)) return;
      panel.focus({ preventScroll: true });
    };
    focusPanel();
    let cancelled = false;
    const id1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) focusPanel();
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id1);
    };
  }, [isOpen, embeddedInInbox]);

  const handleSend = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setMessages((prev) => [
      ...prev,
      { id: `me-${Date.now()}`, sender: 'me', text, timestamp: new Date() },
    ]);
    setInput('');
  };

  const tilePickHint = useMemo(() => {
    if (answers.brands.length < BRAND_PICK_MAX) {
      return tilePickRemainingHint(answers.brands.length, BRAND_PICK_MAX, 'items', 'item');
    }
    if (answers.activities.length < EMOJI_PICK_MAX) {
      return tilePickRemainingHint(answers.activities.length, EMOJI_PICK_MAX, 'emojis', 'emoji');
    }
    if (answers.places.length < PLACE_PICK_MAX) {
      return tilePickRemainingHint(answers.places.length, PLACE_PICK_MAX, 'places', 'place');
    }
    if (!answers.spirit_gif) {
      return '^ pick 1 GIF ^';
    }
    const nick = posterFields.posterNickname;
    if (!nick || (typeof nick === 'string' && !nick.trim())) {
      return tileHintPickNickname;
    }
    if (!skipGenderLuvAgeSteps) {
      if (!posterFields.selfGender) {
        return tileHintPickGender;
      }
      if (posterFields.selfAge === null) {
        return tileHintConfirmAge;
      }
      if (!luvConfirmed) {
        return '^ romance or friends? ^';
      }
      if (posterFields.romanticLuv && !romanticLookingConfirmed) {
        return '^ romance with? ^';
      }
      if (!String(posterFields.homeBase || '').trim()) {
        return tileHintPickLocation;
      }
    }
    if (!answers.user_photo) {
      return tileHintPickPhoto;
    }
    return null;
  }, [
    answers.brands.length,
    answers.activities.length,
    answers.places.length,
    answers.spirit_gif,
    answers.user_photo,
    posterFields.posterNickname,
    posterFields.selfGender,
    posterFields.romanticLuv,
    posterFields.friendLuv,
    posterFields.lookingForGenders.length,
    posterFields.selfAge,
    posterFields.homeBase,
    luvConfirmed,
    romanticLookingConfirmed,
    tileHintPickNickname,
    tileHintPickGender,
    tileHintConfirmAge,
    tileHintPickLocation,
    tileHintPickPhoto,
    skipGenderLuvAgeSteps,
  ]);

  return (
    <>
      {launcherVisible && !isOpen && !embeddedInInbox && (
        <button
          type="button"
          className={`fox-chat-launcher${opsSendToOpsFlow ? ' fox-chat-launcher--ops' : ''}${!hasVibratedOnce ? ' fox-chat-launcher--vibrate' : ''}`}
          aria-label={chatLauncherAriaLabel}
          onClick={onLauncherClick}
        >
          <img
            src={opsSendToOpsFlow ? antifoxSmall : foxMaskFallback}
            alt=""
            className="fox-chat-launcher-icon"
            aria-hidden
          />
          {!hasOpenedOnce && (
            <span className="fox-chat-launcher-badge" aria-label="1 new message">1</span>
          )}
        </button>
      )}
      {(isOpen || embeddedInInbox) && (
        <FoxChatPanelShell
          embeddedInInbox={embeddedInInbox}
          onClose={onClose}
          overlayBehindModal={signUpPopupOpen || loginPopupOpen || verifyPopupOpen}
          panelRef={panelRef}
          onPanelWheel={handleFoxChatPanelWheel}
          panelClassName={`messaging-panel messaging-panel-thread fox-chat-panel${
            opsSendToOpsFlow ? ' fox-chat-panel--ops' : ''
          }${showPreUnmaskGifChat ? ' fox-chat-panel--peer-thread' : ''}${
            embeddedInInbox ? ' fox-chat-panel--inbox-embedded' : ''
          }`}
        >
            <div className="messaging-header">
              {embeddedInInbox ? (
                <button
                  type="button"
                  className="messaging-back"
                  onClick={onInboxBack}
                  aria-label="Back"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
              ) : showPreUnmaskGifChat ? (
                <button
                  type="button"
                  className="messaging-back fox-chat-header-peer-spacer"
                  tabIndex={-1}
                  aria-hidden="true"
                  disabled
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
              ) : (
                <span className="messaging-header-balance" aria-hidden="true" />
              )}
              <div className="messaging-thread-title-block">
                <img
                  src={opsSendToOpsFlow ? antifoxSmall : theFoxMini}
                  alt=""
                  className="messaging-conv-avatar messaging-conv-avatar-img fox-chat-header-avatar"
                />
                <h3 className="messaging-title">{displayChatHeaderTitle}</h3>
              </div>
              <button
                type="button"
                className="messaging-close"
                onClick={onClose}
                onTouchEnd={(e) => {
                  /* iOS Safari focuses the navbar SIGN IN link when the launcher unmounts
                     (it sits directly under the ×). The first tap on × is then consumed just
                     to clear that focus — its synthetic click is swallowed, so the panel only
                     closes on the second tap, and the suppressed click leaves a sticky :hover
                     on SIGN IN. Closing on touchend + preventDefault fires on the first tap and
                     cancels the compatibility mouse events that otherwise stick the hover. */
                  e.preventDefault();
                  onClose?.(e);
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            {showFoxSetupProgress && (
              <div className="fox-chat-setup-progress-wrap">
                <p className="fox-chat-setup-progress-label" aria-live="polite">
                  {`${foxSetupProgress.current} / ${foxSetupProgress.total}`}
                </p>
                <div
                  className="fox-chat-setup-progress"
                  role="progressbar"
                  aria-label="Profile setup progress"
                  aria-valuemin={0}
                  aria-valuemax={foxSetupProgress.total}
                  aria-valuenow={foxSetupProgress.current}
                  aria-valuetext={`${foxSetupProgress.current} of ${foxSetupProgress.total}`}
                >
                  {Array.from({ length: foxSetupProgress.total }, (_, i) => (
                    <div
                      key={i}
                      className={`fox-chat-setup-progress-seg${
                        i < foxSetupProgress.current ? ' fox-chat-setup-progress-seg--filled' : ''
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="messaging-messages" ref={messagesScrollRef}>
              {messages.map((msg) => {
                if (showPreUnmaskGifChat && isFoxSurveyMessage(msg)) {
                  return <React.Fragment key={msg.id} />;
                }
                const isSent = msg.sender === 'me';
                const surveyStep = getFoxChatSurveyStep(msg);
                if (!isSent && msg.romanticLookingTiles && !posterFields.romanticLuv) {
                  return <React.Fragment key={msg.id} />;
                }
                /* Latest tile bubble of its step (so MORE-tile bubbles don't each get a badge). */
                const isLatestTileBubble =
                  (msg.brandTiles && msg.id === lastBrandTilesId)
                  || (msg.emojiTiles && msg.id === lastEmojiTilesId)
                  || (msg.placeTiles && msg.id === lastPlaceTilesId)
                  || (msg.gifTiles && msg.id === lastGifTilesId)
                  || (msg.nicknameTiles && msg.id === lastNicknameTilesId)
                  || !!msg.genderTiles
                  || (msg.luvTiles && msg.id === lastLuvTilesId)
                  || (msg.romanticLookingTiles && msg.id === lastRomanticLookingTilesId)
                  || (msg.ageTiles && msg.id === lastAgeTilesId)
                  || (msg.locationTiles && msg.id === lastLocationTilesId);
                /* Green check on a tile bubble once its question is answered — i.e. its survey step
                   is behind the first still-incomplete one (or the whole survey is complete). */
                const stuffBubbleComplete =
                  !isSent
                  && isLatestTileBubble
                  && surveyStep !== null
                  && (firstIncompleteFoxSurveyStepIndex === null
                    || surveyStep < firstIncompleteFoxSurveyStepIndex);
                if (
                  !isSent
                  && surveyStep !== null
                  && firstIncompleteFoxSurveyStepIndex !== null
                  && surveyStep > firstIncompleteFoxSurveyStepIndex
                ) {
                  return <React.Fragment key={msg.id} />;
                }
                if (!isSent && msg.collageMessage) {
                  const cutoutUrl = msg.partnerCutout || answers.user_photo;
                  const collageId = msg.id;
                  const swagsForCollage = (answers.brands || []).slice(0, 3);
                  const emojisForCollage = (answers.activities || []).slice(0, 3);
                  const placesForCollage = (answers.places || []).slice(0, 3);
                  const placesSplit = Math.floor(seedFromString(`${collageId}-places-split`) * 2) + 1;
                  const placesRowOne = placesForCollage.slice(0, placesSplit);
                  const placesRowTwo = placesForCollage.slice(placesSplit);
                  const gifOverlay = answers.spirit_gif?.gifUrl || answers.spirit_gif?.fullUrl || answers.spirit_gif?.stillUrl || null;
                  const nicknameLabel = (posterFields.posterNickname || '').trim();
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div className="messaging-bubble received fox-chat-stuff-bubble fox-chat-collage-bubble">
                      <div className="shared-poster-container fox-chat-collage-shell">
                        <div className="match-a-friend-collage fox-chat-collage">
                          <div className="collage-poster-area">
                            {nicknameLabel && (
                              <div className="fox-chat-collage-nickname-wrap">
                                <FeedNicknameTile
                                  ad={{
                                    id: collageId,
                                    posterNickname: nicknameLabel,
                                    selfGender: posterFields.selfGender,
                                    selfAge: posterFields.selfAge,
                                    lookingForGenders: posterFields.lookingForGenders,
                                    friendLuv: posterFields.friendLuv,
                                    romanticLuv: posterFields.romanticLuv,
                                    auraColor: previewAuraColor,
                                  }}
                                  savedPosters={[]}
                                  currentUserId={currentUser?.uid ?? null}
                                  findLuvFlags={{
                                    friend: !!posterFields.friendLuv,
                                    romantic: !!posterFields.romanticLuv,
                                  }}
                                  showVibeAura={!!previewAuraColor}
                                />
                              </div>
                            )}
                            <div className="partner-swag-container">
                              <div className="partner-swag-left">
                                {swagsForCollage.length > 0 && (
                                  <div className="swag-images-left">
                                    {swagsForCollage.map((brand, i) => {
                                      const seed = seedFromString(`${collageId}-swag-${i}`);
                                      const seedX = seedFromString(`${collageId}-swag-x-${i}`);
                                      const rotation = (seed * 30 - 15).toFixed(1);
                                      const horizontalOffset = (seedX * 40 - 20).toFixed(1);
                                      return (
                                        <div key={brand.id || i} className="swag-image-wrapper">
                                          <img
                                            src={brand.imageUrl}
                                            alt={brand.name || ''}
                                            className="swag-image"
                                            style={{
                                              transform: `rotate(${rotation}deg)`,
                                              marginLeft: `${horizontalOffset}px`,
                                            }}
                                            onError={fallbackToSourceImage(brand)}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <div className="partner-swag-center">
                                <div className="partner-swag-center-stack">
                                  {cutoutUrl ? (
                                    <div className="final-partner-image-wrapper">
                                      <img
                                        src={cutoutUrl}
                                        alt=""
                                        className="final-partner-image"
                                      />
                                      {gifOverlay && (
                                        <img
                                          src={gifOverlay}
                                          alt=""
                                          className="partner-cutout-gif-overlay"
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <div className="final-partner-image final-partner-image-placeholder">
                                      <img src={foxMaskFallback} alt="" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="partner-swag-right">
                                {emojisForCollage.length > 0 && (
                                  <div className="activities-emojis-right">
                                    {emojisForCollage.map((emoji, i) => {
                                      const seed = seedFromString(`${collageId}-act-${i}`);
                                      const seedX = seedFromString(`${collageId}-act-x-${i}`);
                                      const rotation = (seed * 30 - 15).toFixed(1);
                                      const horizontalOffset = (seedX * 40 - 20).toFixed(1);
                                      const hue = 32 + seed * 20;
                                      const bgColor = `hsl(${hue.toFixed(0)}, 90%, 72%)`;
                                      const displayLabel = typeof emoji === 'string' ? emoji : (emoji?.emoji ?? emoji?.name ?? '');
                                      return (
                                        <div
                                          key={i}
                                          className="activity-tile-outer"
                                          style={{
                                            transform: `rotate(${rotation}deg)`,
                                            marginLeft: `${horizontalOffset}px`,
                                          }}
                                        >
                                          <div
                                            className="activity-emoji-wrapper collage-activity-tile"
                                            style={{ background: bgColor }}
                                          >
                                            <div
                                              className="activity-emoji"
                                              style={{ fontSize: `${(2.5 + seed * 1.5).toFixed(1)}rem` }}
                                            >
                                              {displayLabel}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                            {placesForCollage.length > 0 && (
                              <div className="collage-aspirations-row">
                                {[placesRowOne, placesRowTwo].map((rowPlaces, rowIdx) => (
                                  rowPlaces.length > 0 && (
                                    <div key={rowIdx} className="collage-places-row-inner">
                                      {rowPlaces.map((place, i) => (
                                        <div key={place.id || `${rowIdx}-${i}`} className="aspiration-tile-outer">
                                          <div className="collage-place-tile">
                                            <img
                                              src={place.imageUrl}
                                              alt={place.name || ''}
                                              className="collage-place-img"
                                              onError={fallbackToSourceImage(place)}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="fox-chat-collage-save-area">
                        {!opsSendToOpsFlow && (
                          <CollageDetailsLuvLegend
                            auraColor={previewAuraColor}
                            vibeLabel="your vibe"
                            vibeStatus={auraRecomputing || !previewAuraColor ? 'recomputing...' : null}
                          />
                        )}
                        <button
                          type="button"
                          className={`personality-chart-finder-gender-btn fox-chat-photo-btn fox-chat-collage-save-btn${
                            saveState === 'saved' ? ' fox-chat-collage-save-btn--saved' : ''
                          }${saveState === 'error' ? ' fox-chat-collage-save-btn--error' : ''}`}
                          onClick={handleSaveButtonClick}
                          disabled={
                            saveState === 'saving'
                            || (!opsSendToOpsFlow && saveState === 'saved')
                          }
                          aria-busy={saveState === 'saving'}
                          aria-label={showSavingDots ? 'Saving' : undefined}
                        >
                          {saveState === 'saving' && opsSendToOpsFlow && 'SENDING...'}
                          {showSavingDots && (
                            <>
                              SAVING
                              <span className="fox-chat-saving-dots" aria-hidden="true">
                                {'.'.repeat(savingDots)}
                              </span>
                            </>
                          )}
                          {saveState === 'saved' && !opsSendToOpsFlow && 'SAVED!'}
                          {saveState === 'saved' && opsSendToOpsFlow && collageCtaSignedIn}
                          {saveState === 'error' && 'TRY AGAIN'}
                          {saveState === 'idle' && (currentUser ? collageCtaSignedIn : collageCtaSignedOut)}
                        </button>
                        {collageFoxMaskSaveHint && !opsSendToOpsFlow && saveState !== 'saved' && !showEarlySignedOutHint && (
                          <p className="fox-chat-collage-save-hint fox-chat-collage-foxmask-hint">
                            {collageFoxMaskSaveHint}
                          </p>
                        )}
                        {((saveState === 'saved' && savedPosterId) || showEarlySignedOutHint) && !opsSendToOpsFlow && (
                          <p className="fox-chat-collage-save-hint">
                            {showEarlySignedOutHint
                              ? signedOutProfileSaveHint
                              : savedAwaitingEmail
                                ? emailSignupSavedHint
                                : savedToEmailSignupProfile
                                  ? signedOutProfileSaveHint
                                  : saveOpeningCollagesHint}
                          </p>
                        )}
                        {saveState === 'error' && saveError && (
                          <p className="fox-chat-photo-error" role="alert">{saveError}</p>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.photoTiles) {
                  const isLatestPhotoTiles = msg.id === lastPhotoTilesId;
                  const isBusy = photoUploading || photoProcessing;
                  const inputId = `${domIdPrefix}-photo-input-${msg.id}`;
                  /* Idle = the user hasn't picked a photo yet AND we're not currently uploading /
                     processing. In that case the PICK PHOTO label lives inline inside the text
                     bubble (right under the prompt). The lower stuff bubble is reserved for the
                     busy loader and the CHANGE PHOTO state. */
                  const showIdlePick =
                    isLatestPhotoTiles && !isBusy && !answers.user_photo;
                  const showLowerBubble =
                    isLatestPhotoTiles && (isBusy || !!answers.user_photo);
                  return (
                    <React.Fragment key={msg.id}>
                      {(msg.text || showIdlePick) && (
                        <div className={`messaging-bubble received fox-chat-text-bubble fox-chat-photo-text-bubble${answers.user_photo ? ' fox-chat-text-bubble--complete' : ''}`}>
                          {msg.text && (
                            <p className="messaging-bubble-text">{msg.text}</p>
                          )}
                          {showIdlePick && (
                            <div className="fox-chat-photo-pick-area fox-chat-photo-pick-area-inline">
                              <input
                                ref={photoInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoSelect}
                                id={inputId}
                                className="fox-chat-photo-input"
                              />
                              <label
                                htmlFor={inputId}
                                className="personality-chart-finder-gender-btn fox-chat-photo-btn fox-chat-photo-pick-label"
                              >
                                PICK PHOTO
                              </label>
                              {photoError && (
                                <p className="fox-chat-photo-error" role="alert">{photoError}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {showLowerBubble && (
                      <div className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}>
                      {/* The hidden file input lives next to the CHANGE PHOTO button so
                          handlePhotoRetry's photoInputRef.click() can fire even after a photo has
                          already been picked. */}
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoSelect}
                        id={inputId}
                        className="fox-chat-photo-input"
                      />
                      <div className="inline-survey-content fox-chat-survey-inner fox-chat-photo-survey">
                        {isBusy ? (
                          <div className="fox-chat-photo-loader" aria-live="polite">
                            <div className="fox-chat-photo-loader-row">
                              <img
                                src={photoMagazinePair.left.src}
                                alt=""
                                className="fox-chat-photo-loader-magazine"
                                style={{
                                  transform: `rotate(${photoMagazinePair.left.rot}deg) translateX(${photoMagazinePair.left.dx}px)`,
                                }}
                                aria-hidden
                              />
                              <img
                                src={scissorsImage}
                                alt=""
                                className="fox-chat-photo-loader-scissors fox-chat-photo-loader-scissors-left"
                                style={{
                                  transform: `scaleX(-1) scaleY(-1) rotate(${photoScissorsOffset.left.rot}deg) translate(${photoScissorsOffset.left.dx}px, ${photoScissorsOffset.left.dy}px)`,
                                }}
                                aria-hidden
                              />
                              <img
                                src={theFoxMini}
                                alt=""
                                className="fox-chat-photo-loader-fox"
                                aria-hidden
                              />
                              <img
                                src={scissorsImage}
                                alt=""
                                className="fox-chat-photo-loader-scissors fox-chat-photo-loader-scissors-right"
                                style={{
                                  transform: `scaleY(-1) rotate(${photoScissorsOffset.right.rot}deg) translate(${photoScissorsOffset.right.dx}px, ${photoScissorsOffset.right.dy}px)`,
                                }}
                                aria-hidden
                              />
                              <img
                                src={photoMagazinePair.right.src}
                                alt=""
                                className="fox-chat-photo-loader-magazine"
                                style={{
                                  transform: `rotate(${photoMagazinePair.right.rot}deg) translateX(${photoMagazinePair.right.dx}px)`,
                                }}
                                aria-hidden
                              />
                            </div>
                            <p className="fox-chat-photo-loader-hint">
                              {PHOTO_PROCESSING_HINTS[photoHintIndex]}
                            </p>
                          </div>
                        ) : answers.user_photo ? (
                          <div className="fox-chat-photo-result">
                            <button
                              type="button"
                              className="personality-chart-finder-gender-btn fox-chat-photo-btn"
                              onClick={handlePhotoRetry}
                            >
                              CHANGE PHOTO
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                      )}
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.ageTiles) {
                  const isLatestAgeTiles = msg.id === lastAgeTilesId;
                  /* Wheel-driven age. The wheel writes its centered value into ageInput as a
                     string so the existing isValidAge() / setPosterFields confirm flow keeps
                     working unchanged. Default to AGE_WHEEL_DEFAULT (22) when the wheel hasn't
                     emitted a value yet (first render before the user has scrolled). */
                  const ageInputValid = isValidAge(ageInput || String(AGE_WHEEL_DEFAULT));
                  const wheelValue = isValidAge(ageInput) ? Number(ageInput) : AGE_WHEEL_DEFAULT;
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${stuffBubbleComplete ? ' fox-chat-text-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}>
                      <div className="inline-survey-content fox-chat-survey-inner fox-chat-age-survey">
                        <div
                          className={
                            posterFields.selfAge !== null && !ageWheelInteractionUnlocked
                              ? 'fox-chat-age-wheel-activator fox-chat-age-wheel-activator--tap-to-edit'
                              : 'fox-chat-age-wheel-activator'
                          }
                          onClick={() => {
                            if (posterFields.selfAge !== null && !ageWheelInteractionUnlocked) {
                              setAgeWheelInteractionUnlocked(true);
                              /* Clear committed age so CONFIRM shows again without forcing a scroll first. */
                              setPosterFields((prev) => ({ ...prev, selfAge: null }));
                            }
                          }}
                        >
                          <AgeWheel
                            value={wheelValue}
                            interactionLocked={
                              posterFields.selfAge !== null && !ageWheelInteractionUnlocked
                            }
                            onChange={(next) => {
                              setAgeInput(String(next));
                              if (posterFields.selfAge !== null) {
                                setPosterFields((prev) => ({ ...prev, selfAge: null }));
                              }
                            }}
                          />
                        </div>
                        {isLatestAgeTiles && posterFields.selfAge === null && (
                          <button
                            type="button"
                            className="personality-chart-title-descriptor-confirm fox-chat-luv-confirm"
                            disabled={!ageInputValid}
                            onClick={() => {
                              const raw = ageInput || String(AGE_WHEEL_DEFAULT);
                              if (!isValidAge(raw)) return;
                              setAgeWheelInteractionUnlocked(false);
                              setPosterFields((prev) => ({ ...prev, selfAge: Number(raw) }));
                            }}
                          >
                            CONFIRM
                          </button>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.locationTiles) {
                  const isLatestLocationTiles = msg.id === lastLocationTilesId;
                  const presetValues = HOME_BASE_PRESETS.map((o) => o.value);
                  const homeBaseTrim = String(posterFields.homeBase || '').trim();
                  const isCustomHomeBase = homeBaseTrim && !presetValues.includes(homeBaseTrim);
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${stuffBubbleComplete ? ' fox-chat-text-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}>
                      <div className="inline-survey-content fox-chat-survey-inner fox-chat-gender-survey fox-chat-location-survey">
                        <div className="personality-chart-finder-gender-row fox-chat-gender-row">
                          {HOME_BASE_PRESETS.map((opt) => {
                            const isSelected = posterFields.homeBase === opt.value && !isCustomHomeBase;
                            /* Once a home base is set, fade the unpicked presets so the answered
                               bubble reads as done (mirrors the other completed questions). */
                            const isDimmed = !!posterFields.homeBase && !isSelected;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                className={`personality-chart-finder-gender-btn fox-chat-gender-btn${isSelected ? ' personality-chart-finder-gender-btn--selected fox-chat-gender-btn--selected' : ''}${isDimmed ? ' fox-chat-gender-btn--dimmed' : ''}`}
                                onClick={() => {
                                  setLocationFieldKey((k) => k + 1);
                                  setPosterFields((prev) => ({
                                    ...prev,
                                    homeBase: isSelected ? null : opt.value,
                                  }));
                                }}
                                aria-pressed={isSelected}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                        {isLatestLocationTiles && (
                          <FoxChatSurveyCustomEntryField
                            key={locationFieldKey}
                            defaultValue={isCustomHomeBase ? homeBaseTrim : ''}
                            showActiveBorder={isCustomHomeBase}
                            formClassName={`fox-chat-nickname-custom-form fox-chat-location-custom-form${!!posterFields.homeBase && !isCustomHomeBase ? ' fox-chat-location-custom-form--dimmed' : ''}`}
                            placeholder={locationOtherPlaceholder}
                            ariaLabel={locationOtherInputAriaLabel}
                            submitAriaLabel="Submit location"
                            maxLength={64}
                            onInputChange={() => {
                              if (posterFields.homeBase) {
                                setPosterFields((prev) => ({ ...prev, homeBase: null }));
                              }
                            }}
                            onSubmit={(value) => {
                              setPosterFields((prev) => ({ ...prev, homeBase: value }));
                            }}
                          />
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.romanticLookingTiles) {
                  const isLatestRomanticLookingTiles =
                    msg.id === lastRomanticLookingTilesId;
                  const hasRomanticSelection = posterFields.lookingForGenders.length > 0;
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${stuffBubbleComplete ? ' fox-chat-text-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}>
                      <div className="inline-survey-content fox-chat-survey-inner fox-chat-gender-survey fox-chat-romantic-looking-survey">
                        <div className="personality-chart-finder-gender-row fox-chat-gender-row">
                          {GENDER_OPTIONS.map((g) => {
                            const isSelected = posterFields.lookingForGenders.includes(g.id);
                            /* Once confirmed, fade any unpicked option so the answered bubble
                               reads as done (mirrors the gender / LUV completed look). */
                            const isDimmed = romanticLookingConfirmed && !isSelected;
                            return (
                              <button
                                key={g.id}
                                type="button"
                                className={`personality-chart-finder-gender-btn fox-chat-gender-btn${isSelected ? ' personality-chart-finder-gender-btn--selected fox-chat-gender-btn--selected' : ''}${isDimmed ? ' fox-chat-gender-btn--dimmed' : ''}`}
                                onClick={() => {
                                  setPosterFields((prev) => ({
                                    ...prev,
                                    lookingForGenders: prev.lookingForGenders.includes(g.id)
                                      ? prev.lookingForGenders.filter((x) => x !== g.id)
                                      : [...prev.lookingForGenders, g.id],
                                  }));
                                }}
                                aria-pressed={isSelected}
                              >
                                <img
                                  src={orheartImage}
                                  alt=""
                                  className="fox-chat-gender-btn-icon"
                                  aria-hidden
                                />
                                {g.label}
                              </button>
                            );
                          })}
                        </div>
                        {isLatestRomanticLookingTiles && !romanticLookingConfirmed && (
                          <div className="fox-chat-luv-confirm-wrap">
                            <button
                              type="button"
                              className="personality-chart-title-descriptor-confirm fox-chat-luv-confirm"
                              disabled={!hasRomanticSelection}
                              onClick={() => {
                                if (!hasRomanticSelection) return;
                                setRomanticLookingConfirmed(true);
                              }}
                            >
                              CONFIRM
                            </button>
                            <p className="fox-chat-luv-confirm-hint">select one or multiple</p>
                          </div>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.luvTiles) {
                  const isLatestLuvTiles = msg.id === lastLuvTilesId;
                  const luvOptions = [
                    {
                      id: 'romantic',
                      label: 'ROMANCE',
                      icon: orheartImage,
                      isSelected: posterFields.romanticLuv,
                      toggle: () => setPosterFields((prev) => ({ ...prev, romanticLuv: !prev.romanticLuv })),
                    },
                    {
                      id: 'friend',
                      label: 'FRIENDS',
                      icon: friendsImage,
                      isSelected: posterFields.friendLuv,
                      toggle: () => setPosterFields((prev) => ({ ...prev, friendLuv: !prev.friendLuv })),
                    },
                  ];
                  const luvHasSelection = posterFields.romanticLuv || posterFields.friendLuv;
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${stuffBubbleComplete ? ' fox-chat-text-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}>
                      <div className="inline-survey-content fox-chat-survey-inner fox-chat-luv-survey">
                        <div className="saved-poster-match-tiles fox-chat-luv-tiles">
                          {luvOptions.map((opt) => {
                            /* Once confirmed, fade any unpicked option so the answered bubble reads
                               as done (mirrors the gender / image-tile completed look). */
                            const isDimmed = luvConfirmed && !opt.isSelected;
                            return (
                            <div
                              key={opt.id}
                              role="checkbox"
                              tabIndex={0}
                              aria-checked={opt.isSelected}
                              className={`saved-poster-match-tile fox-chat-luv-tile${opt.isSelected ? ' saved-poster-match-tile--selected' : ''}${isDimmed ? ' fox-chat-luv-tile--dimmed' : ''}`}
                              onClick={opt.toggle}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  opt.toggle();
                                }
                              }}
                            >
                              <img
                                src={opt.icon}
                                alt=""
                                className="saved-poster-match-tile-icon"
                              />
                              <h4 className="saved-poster-match-tile-title">{opt.label}</h4>
                            </div>
                            );
                          })}
                        </div>
                        {isLatestLuvTiles && !luvConfirmed && (
                          <div className="fox-chat-luv-confirm-wrap">
                            <button
                              type="button"
                              className="personality-chart-title-descriptor-confirm fox-chat-luv-confirm"
                              disabled={!luvHasSelection}
                              onClick={() => {
                                if (!luvHasSelection) return;
                                setLuvConfirmed(true);
                              }}
                            >
                              CONFIRM
                            </button>
                            <p className="fox-chat-luv-confirm-hint">select one or both</p>
                          </div>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.genderTiles) {
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${stuffBubbleComplete ? ' fox-chat-text-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}>
                      <div className="inline-survey-content fox-chat-survey-inner fox-chat-gender-survey">
                        <div className="personality-chart-finder-gender-row fox-chat-gender-row">
                          {GENDER_OPTIONS.map((g) => {
                            const isSelected = posterFields.selfGender === g.id;
                            /* Once a gender is picked, fade the other options (mirrors the disabled
                               look on unselected image tiles) so the answered bubble reads as done. */
                            const isDimmed = !!posterFields.selfGender && !isSelected;
                            return (
                              <button
                                key={g.id}
                                type="button"
                                className={`personality-chart-finder-gender-btn fox-chat-gender-btn${isSelected ? ' personality-chart-finder-gender-btn--selected fox-chat-gender-btn--selected' : ''}${isDimmed ? ' fox-chat-gender-btn--dimmed' : ''}`}
                                onClick={() => setPosterFields((prev) => ({ ...prev, selfGender: isSelected ? null : g.id }))}
                                aria-pressed={isSelected}
                              >
                                {g.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.nicknameTiles) {
                  const isLatestNicknameTiles = msg.id === lastNicknameTilesId;
                  const tiles = Array.isArray(msg.nicknames) ? msg.nicknames : [];
                  const nickTrim = String(posterFields.posterNickname || '').trim();
                  const isCustomNickname = nickTrim && !tiles.includes(nickTrim);
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div
                        id={`${domIdPrefix}-stuff-${msg.id}`}
                        className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                      >
                      <div className="inline-survey-content fox-chat-survey-inner fox-chat-nickname-survey">
                        <div className="fox-chat-nickname-grid">
                          {tiles.map((nickname) => {
                            const isSelected =
                              posterFields.posterNickname === nickname && !isCustomNickname;
                            const isDisabled = !isSelected && !!posterFields.posterNickname;
                            return (
                              <button
                                key={nickname}
                                type="button"
                                className={`the-one-label the-one-label-clickable fox-chat-nickname-tile ${isSelected ? 'fox-chat-nickname-tile--selected' : ''} ${isDisabled ? 'fox-chat-nickname-tile--disabled' : ''}`}
                                onClick={() => {
                                  if (isDisabled) return;
                                  setNicknameFieldKey((k) => k + 1);
                                  setPosterFields((prev) => ({ ...prev, posterNickname: isSelected ? null : nickname }));
                                }}
                                disabled={isDisabled}
                                aria-pressed={isSelected}
                              >
                                {`"${nickname}"`}
                              </button>
                            );
                          })}
                          {isLatestNicknameTiles && (
                            <button
                              type="button"
                              className={`survey-checkbox-option survey-add-custom fox-chat-nickname-more${moreOptionsLoading === 'nickname' ? ' fox-chat-more-loading' : ''}${posterFields.posterNickname ? ' disabled' : ''}`}
                              onClick={handleMoreNicknameOptions}
                              disabled={moreOptionsLoading === 'nickname' || !!posterFields.posterNickname}
                              aria-busy={moreOptionsLoading === 'nickname'}
                            >
                              {moreOptionsLoading === 'nickname' ? (
                                <span className="fox-chat-more-spinner" aria-hidden="true" />
                              ) : 'MORE NICKNAMES'}
                            </button>
                          )}
                        </div>
                        {isLatestNicknameTiles && (
                          <FoxChatSurveyCustomEntryField
                            key={nicknameFieldKey}
                            defaultValue={isCustomNickname ? nickTrim : ''}
                            showActiveBorder={isCustomNickname}
                            placeholder={nicknamePlaceholder}
                            ariaLabel={nicknameInputAriaLabel}
                            submitAriaLabel="Submit nickname"
                            maxLength={32}
                            onInputChange={() => {
                              if (posterFields.posterNickname) {
                                setPosterFields((prev) => ({ ...prev, posterNickname: null }));
                              }
                            }}
                            onSubmit={(value) => {
                              setPosterFields((prev) => ({ ...prev, posterNickname: value }));
                            }}
                          />
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.gifTiles) {
                  const isLatestGifTiles = msg.id === lastGifTilesId;
                  const tiles = Array.isArray(msg.gifs) ? msg.gifs : [];
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div
                        id={`${domIdPrefix}-stuff-${msg.id}`}
                        className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                      >
                      <div className="inline-survey-content fox-chat-survey-inner spirit-gif-section">
                        {isLatestGifTiles && !loadingMoreGifs && gifFetchHint === 'no_results' && (
                          <p className="spirit-gif-hint" role="status">
                            No more GIFs right now. Try search below!
                          </p>
                        )}
                        {isLatestGifTiles && !loadingMoreGifs && gifFetchHint === 'error' && (
                          <p className="spirit-gif-hint spirit-gif-hint--error" role="alert">
                            Couldn&apos;t load GIFs. Try again.
                          </p>
                        )}
                        <div
                          className="survey-checkbox-group spirit-gif-grid"
                          aria-busy={isLatestGifTiles && loadingMoreGifs}
                        >
                          {tiles.map((gif) => {
                            const isSelected = answers.spirit_gif?.id === gif.id;
                            const isDisabled = !isSelected && !!answers.spirit_gif;
                            const previewSrc = gif.stillUrl || gif.gifUrl || gif.fullUrl || '';
                            return (
                              <button
                                key={gif.id}
                                type="button"
                                className={`survey-checkbox-option spirit-gif-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setAnswers((prev) => ({ ...prev, spirit_gif: null, vibe_photo: null }));
                                  } else if (!isDisabled) {
                                    setAnswers((prev) => ({
                                      ...prev,
                                      spirit_gif: gif,
                                      vibe_photo: gif.fullUrl || gif.gifUrl || null,
                                    }));
                                  }
                                }}
                                disabled={isDisabled}
                                aria-label={gif.title || 'GIF'}
                              >
                                <span className="spirit-gif-spinner spirit-gif-spinner--overlay" />
                                <img
                                  src={previewSrc}
                                  alt={gif.title || 'GIF'}
                                  className="spirit-gif-img"
                                  onLoad={(e) => {
                                    const root = e.target.closest('.spirit-gif-option');
                                    root?.querySelector('.spirit-gif-spinner')?.classList.add('hidden');
                                    if (gif.stillUrl && gif.gifUrl && e.target.src !== gif.gifUrl) {
                                      e.target.src = gif.gifUrl;
                                    }
                                  }}
                                  onError={(e) => {
                                    const img = e.target;
                                    const root = img.closest('.spirit-gif-option');
                                    const spinner = root?.querySelector('.spirit-gif-spinner');
                                    if (gif.stillUrl && gif.gifUrl && !img.dataset.spiritTriedGif) {
                                      img.dataset.spiritTriedGif = '1';
                                      img.src = gif.gifUrl;
                                      return;
                                    }
                                    spinner?.classList.add('hidden');
                                    img.classList.add('spirit-gif-img--failed');
                                  }}
                                />
                              </button>
                            );
                          })}
                          {isLatestGifTiles && (
                            <button
                              type="button"
                              className={`survey-checkbox-option survey-add-custom${moreOptionsLoading === 'gif' || loadingMoreGifs || gifSearchLoading ? ' fox-chat-more-loading' : ''}${answers.spirit_gif || (gifSearchHasText && !gifSearchQuery) ? ' disabled' : ''}`}
                              onClick={handleMoreGifOptions}
                              disabled={moreOptionsLoading === 'gif' || loadingMoreGifs || gifSearchLoading || !!answers.spirit_gif || (gifSearchHasText && !gifSearchQuery)}
                              aria-busy={moreOptionsLoading === 'gif' || loadingMoreGifs || gifSearchLoading}
                            >
                              {moreOptionsLoading === 'gif' || loadingMoreGifs || gifSearchLoading ? (
                                <span className="fox-chat-more-spinner" aria-hidden="true" />
                              ) : 'MORE GIFS'}
                            </button>
                          )}
                          {isLatestGifTiles && (
                            <div className="brand-search-container">
                              <FoxChatSurveySearchField
                                key={gifSearchFieldKey}
                                refreshMode={!!gifSearchQuery && gifSearchResults.length > 0}
                                refreshing={gifSearchLoading}
                                onRefresh={refreshGifSearchResults}
                                onHasTextChange={setGifSearchHasText}
                                onInputChange={() => {
                                  setGifSearchDone(false);
                                  clearSurveySearchCustomAdded('gif');
                                  setGifSearchQuery('');
                                }}
                                placeholder="Search KLIPY GIFs..."
                                ariaLabel="Search KLIPY GIFs"
                                submitDisabled={gifSearchLoading || (!!gifSearchQuery && gifSearchResults.length > 0 && !gifSearchHasMore)}
                                disabled={!!answers.spirit_gif}
                                onSubmit={async (query) => {
                                  if (gifSearchLoading) return;
                                  surveySearchScrollTargetRef.current = msg.id;
                                  clearSurveySearchCustomAdded('gif');
                                  setGifSearchQuery(query);
                                  setGifSearchLoading(true);
                                  setGifFetchHint(null);
                                  setGifSearchDone(false);
                                  try {
                                    const customerId = currentUser?.uid || `anon_${Date.now()}`;
                                    gifQuerySearchPageRef.current = 1;
                                    const results = await searchSpiritGifsApi(
                                      query,
                                      customerId,
                                      SURVEY_SEARCH_PAGE,
                                      1,
                                    );
                                    const page = (results || []).slice(0, SURVEY_SEARCH_PAGE);
                                    gifQuerySearchPageRef.current = 2;
                                    setGifSearchResults(page);
                                    setGifSearchHasMore(page.length >= SURVEY_SEARCH_PAGE);
                                  } catch (err) {
                                    console.error('Spirit GIF search error:', err);
                                    setGifSearchResults([]);
                                    setGifSearchHasMore(false);
                                  } finally {
                                    setGifSearchLoading(false);
                                    setGifSearchDone(true);
                                  }
                                }}
                              />
                              {gifSearchLoading && (
                                <div className="brand-search-loading">Searching the web...</div>
                              )}
                              {gifSearchResults.length > 0 && (
                                <div className="brand-search-results">
                                  {gifSearchResults.map((result, index) => {
                                    const isSelected = answers.spirit_gif?.id === result.id;
                                    const isDisabled = !isSelected && !!answers.spirit_gif;
                                    const previewSrc = result.stillUrl || result.gifUrl || result.fullUrl || '';
                                    return (
                                      <button
                                        key={result.id || index}
                                        type="button"
                                        className={`survey-checkbox-option spirit-gif-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                        onClick={() => {
                                          if (isSelected) {
                                            setAnswers((prev) => ({
                                              ...prev,
                                              spirit_gif: null,
                                              vibe_photo: null,
                                            }));
                                          } else if (!isDisabled) {
                                            setAnswers((prev) => ({
                                              ...prev,
                                              spirit_gif: result,
                                              vibe_photo: result.fullUrl || result.gifUrl || null,
                                            }));
                                            addGifToLatestSet(result);
                                            setGifSearchResults([]);
                                            setGifSearchQuery('');
                                            setGifSearchHasMore(false);
                                            gifQuerySearchPageRef.current = 1;
                                            setGifSearchFieldKey((k) => k + 1);
                                            setGifSearchDone(false);
                                            markSurveySearchCustomAdded('gif');
                                            const gifSource = result.fullUrl || result.gifUrl || result.stillUrl;
                                            const gifId = result.id;
                                            persistCustomSearchResultImage('gif', gifSource, (stableUrl) => {
                                              setAnswers((prev) => {
                                                if (prev.spirit_gif?.id !== gifId) return prev;
                                                return {
                                                  ...prev,
                                                  spirit_gif: {
                                                    ...prev.spirit_gif,
                                                    stillUrl: stableUrl,
                                                    gifUrl: stableUrl,
                                                    fullUrl: stableUrl,
                                                  },
                                                  vibe_photo: stableUrl,
                                                };
                                              });
                                              setMessages((prev) => prev.map((m) => {
                                                if (!m.gifTiles || !Array.isArray(m.gifs)) return m;
                                                return {
                                                  ...m,
                                                  gifs: m.gifs.map((g) => (
                                                    g.id === gifId
                                                      ? { ...g, stillUrl: stableUrl, gifUrl: stableUrl, fullUrl: stableUrl }
                                                      : g
                                                  )),
                                                };
                                              }));
                                            });
                                          }
                                        }}
                                        disabled={isDisabled}
                                        aria-label={result.title || 'GIF'}
                                      >
                                        <span className="spirit-gif-spinner spirit-gif-spinner--overlay" />
                                        <img
                                          src={previewSrc}
                                          alt={result.title || 'GIF'}
                                          className="spirit-gif-img"
                                          onLoad={(e) => {
                                            const root = e.target.closest('.spirit-gif-option');
                                            root?.querySelector('.spirit-gif-spinner')?.classList.add('hidden');
                                            if (result.stillUrl && result.gifUrl && e.target.src !== result.gifUrl) {
                                              e.target.src = result.gifUrl;
                                            }
                                          }}
                                          onError={(e) => {
                                            const img = e.target;
                                            const root = img.closest('.spirit-gif-option');
                                            const spinner = root?.querySelector('.spirit-gif-spinner');
                                            if (result.stillUrl && result.gifUrl && !img.dataset.spiritTriedGif) {
                                              img.dataset.spiritTriedGif = '1';
                                              img.src = result.gifUrl;
                                              return;
                                            }
                                            spinner?.classList.add('hidden');
                                            img.classList.add('spirit-gif-img--failed');
                                          }}
                                        />
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {surveySearchCustomAdded === 'gif' && !gifSearchLoading && gifSearchResults.length === 0 && (
                                <div className="brand-search-loading fox-chat-search-custom-added" role="status">
                                  {customSearchAddedHint('gif', answers)}
                                </div>
                              )}
                              {gifSearchDone && !gifSearchLoading && gifSearchResults.length === 0
                                && surveySearchCustomAdded !== 'gif' && (
                                <div className="brand-search-loading">No GIFs found for that search</div>
                              )}
                            </div>
                          )}
                        </div>
                        {isLatestGifTiles && loadingMoreGifs && (
                          <div className="brand-search-loading">Searching the web...</div>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.placeTiles) {
                  const isLatestPlaceTiles = msg.id === lastPlaceTilesId;
                  const tiles = Array.isArray(msg.places) ? msg.places : [];
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div
                        id={`${domIdPrefix}-stuff-${msg.id}`}
                        className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                      >
                      <div className="inline-survey-content fox-chat-survey-inner">
                        <div className="survey-checkbox-group">
                          {tiles.map((place) => {
                            const isSelected = answers.places.some((p) => p.id === place.id);
                            const isDisabled =
                              !isSelected && answers.places.length >= PLACE_PICK_MAX;
                            return (
                              <button
                                key={place.id}
                                type="button"
                                className={`survey-checkbox-option brand-option place-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setAnswers((prev) => ({ ...prev, places: prev.places.filter((p) => p.id !== place.id) }));
                                  } else if (answers.places.length < PLACE_PICK_MAX) {
                                    setAnswers((prev) => ({ ...prev, places: [...prev.places, place] }));
                                  }
                                }}
                                disabled={isDisabled}
                                aria-label={place.name}
                              >
                                <img
                                  src={place.imageUrl}
                                  alt={place.name}
                                  className="place-option-img"
                                />
                              </button>
                            );
                          })}
                          {isLatestPlaceTiles && (
                            <button
                              type="button"
                              className={`survey-checkbox-option survey-add-custom${moreOptionsLoading === 'place' || placeSearchLoading ? ' fox-chat-more-loading' : ''}${answers.places.length >= PLACE_PICK_MAX || (placeSearchHasText && !placeSearchQuery) ? ' disabled' : ''}`}
                              onClick={handleMorePlaceOptions}
                              disabled={moreOptionsLoading === 'place' || placeSearchLoading || answers.places.length >= PLACE_PICK_MAX || (placeSearchHasText && !placeSearchQuery)}
                              aria-busy={moreOptionsLoading === 'place' || placeSearchLoading}
                            >
                              {moreOptionsLoading === 'place' || placeSearchLoading ? (
                                <span className="fox-chat-more-spinner" aria-hidden="true" />
                              ) : 'MORE PLACES'}
                            </button>
                          )}
                          {isLatestPlaceTiles && (
                            <div className="brand-search-container">
                              <FoxChatSurveySearchField
                                key={placeSearchFieldKey}
                                refreshMode={!!placeSearchQuery && placeSearchResults.length > 0}
                                refreshing={placeSearchLoading}
                                onRefresh={refreshPlaceSearchResults}
                                onHasTextChange={setPlaceSearchHasText}
                                onInputChange={() => {
                                  setPlaceSearchDone(false);
                                  clearSurveySearchCustomAdded('place');
                                  setPlaceSearchQuery('');
                                }}
                                placeholder="Search for a place..."
                                ariaLabel="Search for a place"
                                submitDisabled={placeSearchLoading || (!!placeSearchQuery && placeSearchResults.length > 0 && !placeSearchHasMore)}
                                disabled={answers.places.length >= PLACE_PICK_MAX}
                                onSubmit={async (query) => {
                                  if (placeSearchLoading) return;
                                  surveySearchScrollTargetRef.current = msg.id;
                                  clearSurveySearchCustomAdded('place');
                                  setPlaceSearchQuery(query);
                                  setPlaceSearchLoading(true);
                                  setPlaceSearchDone(false);
                                  try {
                                    const results = await searchPlacesApi(query);
                                    const list = Array.isArray(results) ? results : [];
                                    placeSearchPageTokenRef.current = results?.nextPageToken || '';
                                    setPlaceSearchResults(list);
                                    setPlaceSearchHasMore(!!placeSearchPageTokenRef.current);
                                  } catch (err) {
                                    console.error('Place search error:', err);
                                    setPlaceSearchResults([]);
                                    setPlaceSearchHasMore(false);
                                    placeSearchPageTokenRef.current = '';
                                  } finally {
                                    setPlaceSearchLoading(false);
                                    setPlaceSearchDone(true);
                                  }
                                }}
                              />
                              {placeSearchLoading && (
                                <div className="brand-search-loading">Searching the web...</div>
                              )}
                              {placeSearchResults.length > 0 && (
                                <div className="brand-search-results">
                                  {placeSearchResults.map((result, index) => {
                                    const isSelected = answers.places.some(
                                      (p) => p.id === result.id,
                                    );
                                    const isDisabled =
                                      !isSelected && answers.places.length >= PLACE_PICK_MAX;
                                    return (
                                      <button
                                        key={result.id || index}
                                        type="button"
                                        className={`survey-checkbox-option brand-option place-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                        onClick={() => {
                                          if (isSelected) {
                                            setAnswers((prev) => ({
                                              ...prev,
                                              places: prev.places.filter((p) => p.id !== result.id),
                                            }));
                                          } else if (answers.places.length < PLACE_PICK_MAX) {
                                            const picked = {
                                              ...result,
                                              sourceImageUrl: result.sourceImageUrl || result.imageUrl,
                                            };
                                            setAnswers((prev) => ({ ...prev, places: [...prev.places, picked] }));
                                            addPlaceToLatestSet(picked);
                                            setPlaceSearchResults([]);
                                            setPlaceSearchQuery('');
                                            setPlaceSearchHasMore(false);
                                            placeSearchPageTokenRef.current = '';
                                            setPlaceSearchFieldKey((k) => k + 1);
                                            setPlaceSearchDone(false);
                                            markSurveySearchCustomAdded('place');
                                            const placeId = picked.id;
                                            persistCustomSearchResultImage('place', picked.sourceImageUrl, (stableUrl) => {
                                              setAnswers((prev) => ({
                                                ...prev,
                                                places: prev.places.map((p) => (
                                                  p.id === placeId
                                                    ? {
                                                      ...p,
                                                      imageUrl: stableUrl,
                                                      sourceImageUrl: p.sourceImageUrl || picked.sourceImageUrl,
                                                      imageUrls: Array.isArray(p.imageUrls)
                                                        ? p.imageUrls.map((u) => (u === p.imageUrl ? stableUrl : u))
                                                        : [stableUrl],
                                                    }
                                                    : p
                                                )),
                                              }));
                                              setMessages((prev) => prev.map((m) => {
                                                if (!m.placeTiles || !Array.isArray(m.places)) return m;
                                                return {
                                                  ...m,
                                                  places: m.places.map((p) => (
                                                    p.id === placeId
                                                      ? {
                                                        ...p,
                                                        imageUrl: stableUrl,
                                                        sourceImageUrl: p.sourceImageUrl || picked.sourceImageUrl,
                                                        imageUrls: Array.isArray(p.imageUrls)
                                                          ? p.imageUrls.map((u) => (u === p.imageUrl ? stableUrl : u))
                                                          : [stableUrl],
                                                      }
                                                      : p
                                                  )),
                                                };
                                              }));
                                            });
                                          }
                                        }}
                                        disabled={isDisabled}
                                        aria-label={result.name}
                                      >
                                        <img
                                          src={result.imageUrl}
                                          alt={result.name}
                                          className="place-option-img"
                                        />
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {surveySearchCustomAdded === 'place' && !placeSearchLoading && placeSearchResults.length === 0 && (
                                <div className="brand-search-loading fox-chat-search-custom-added" role="status">
                                  {customSearchAddedHint('place', answers)}
                                </div>
                              )}
                              {placeSearchDone && !placeSearchLoading && placeSearchResults.length === 0
                                && surveySearchCustomAdded !== 'place' && (
                                <div className="brand-search-loading">No places found</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.emojiTiles) {
                  const isLatestEmojiTiles = msg.id === lastEmojiTilesId;
                  const tiles = Array.isArray(msg.emojis) ? msg.emojis : [];
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div
                        id={`${domIdPrefix}-stuff-${msg.id}`}
                        className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                      >
                      <div className="inline-survey-content fox-chat-survey-inner">
                        <div className="survey-checkbox-group">
                          {tiles.map((emoji) => {
                            const isSelected = answers.activities.includes(emoji);
                            const isDisabled =
                              !isSelected && answers.activities.length >= EMOJI_PICK_MAX;
                            return (
                              <button
                                key={emoji}
                                type="button"
                                className={`survey-checkbox-option activities-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setAnswers((prev) => ({ ...prev, activities: prev.activities.filter((e) => e !== emoji) }));
                                  } else if (answers.activities.length < EMOJI_PICK_MAX) {
                                    setAnswers((prev) => ({ ...prev, activities: [...prev.activities, emoji] }));
                                  }
                                }}
                                disabled={isDisabled}
                                aria-label={emoji}
                              >
                                {emoji}
                              </button>
                            );
                          })}
                          {isLatestEmojiTiles && (
                            <button
                              type="button"
                              className={`survey-checkbox-option survey-add-custom${moreOptionsLoading === 'emoji' ? ' fox-chat-more-loading' : ''}${answers.activities.length >= EMOJI_PICK_MAX || (emojiSearchHasText && !emojiSearchQuery) ? ' disabled' : ''}`}
                              onClick={handleMoreEmojiOptions}
                              disabled={moreOptionsLoading === 'emoji' || answers.activities.length >= EMOJI_PICK_MAX || (emojiSearchHasText && !emojiSearchQuery)}
                              aria-busy={moreOptionsLoading === 'emoji'}
                            >
                              {moreOptionsLoading === 'emoji' ? (
                                <span className="fox-chat-more-spinner" aria-hidden="true" />
                              ) : 'MORE EMOJIS'}
                            </button>
                          )}
                          {isLatestEmojiTiles && (
                            <div className="brand-search-container">
                              <FoxChatSurveySearchField
                                key={emojiSearchFieldKey}
                                refreshMode={!!emojiSearchQuery && emojiSearchResults.length > 0}
                                onRefresh={refreshEmojiSearchResults}
                                onHasTextChange={setEmojiSearchHasText}
                                onInputChange={() => {
                                  setEmojiSearchEmpty(false);
                                  clearSurveySearchCustomAdded('emoji');
                                  setEmojiSearchQuery('');
                                }}
                                placeholder="Search emojis..."
                                ariaLabel="Search emojis by keyword"
                                disabled={answers.activities.length >= EMOJI_PICK_MAX}
                                submitDisabled={(!!emojiSearchQuery && emojiSearchResults.length > 0 && !emojiSearchHasMore) || emojiSearchEmpty}
                                onSubmit={(query) => {
                                  surveySearchScrollTargetRef.current = msg.id;
                                  clearSurveySearchCustomAdded('emoji');
                                  setEmojiSearchQuery(query);
                                  emojiSearchShownRef.current = [];
                                  const results = searchActivityPlusEmojisByQuery(query, SURVEY_SEARCH_PAGE);
                                  emojiSearchShownRef.current = [...results];
                                  setEmojiSearchResults(results);
                                  setEmojiSearchEmpty(results.length === 0);
                                  setEmojiSearchHasMore(results.length >= SURVEY_SEARCH_PAGE);
                                }}
                              />
                              {surveySearchCustomAdded === 'emoji' && emojiSearchResults.length === 0 && (
                                <p className="brand-picker-stuff-search-empty fox-chat-search-custom-added" role="status">
                                  {customSearchAddedHint('emoji', answers)}
                                </p>
                              )}
                              {emojiSearchEmpty && emojiSearchResults.length === 0
                                && surveySearchCustomAdded !== 'emoji' && (
                                <p className="brand-picker-stuff-search-empty fox-chat-search-custom-added" role="status">
                                  No emojis found. Try a different keyword
                                </p>
                              )}
                              {emojiSearchResults.length > 0 && (
                                <div className="brand-search-results">
                                  {emojiSearchResults.map((emoji, index) => {
                                    const isSelected = answers.activities.includes(emoji);
                                    const isDisabled =
                                      !isSelected && answers.activities.length >= EMOJI_PICK_MAX;
                                    return (
                                      <button
                                        key={`${emoji}-${index}`}
                                        type="button"
                                        className={`survey-checkbox-option activities-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                        onClick={() => {
                                          if (isSelected) {
                                            setAnswers((prev) => ({
                                              ...prev,
                                              activities: prev.activities.filter((e) => e !== emoji),
                                            }));
                                          } else if (answers.activities.length < EMOJI_PICK_MAX) {
                                            setAnswers((prev) => ({
                                              ...prev,
                                              activities: [...prev.activities, emoji],
                                            }));
                                            addEmojiToLatestSet(emoji);
                                            setEmojiSearchResults([]);
                                            setEmojiSearchQuery('');
                                            setEmojiSearchHasMore(false);
                                            emojiSearchShownRef.current = [];
                                            setEmojiSearchFieldKey((k) => k + 1);
                                            setEmojiSearchEmpty(false);
                                            markSurveySearchCustomAdded('emoji');
                                          }
                                        }}
                                        disabled={isDisabled}
                                        aria-label={emoji}
                                      >
                                        {emoji}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && msg.brandTiles) {
                  const isLatestBrandTiles = msg.id === lastBrandTilesId;
                  const tiles = Array.isArray(msg.brands) ? msg.brands : [];
                  return (
                    <React.Fragment key={msg.id}>
                      {msg.text && (
                        <div
                          id={`${domIdPrefix}-msg-${msg.id}`}
                          className={`messaging-bubble received fox-chat-text-bubble${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                        >
                          <p className="messaging-bubble-text">{msg.text}</p>
                        </div>
                      )}
                      <div
                        id={`${domIdPrefix}-stuff-${msg.id}`}
                        className={`messaging-bubble received fox-chat-stuff-bubble${stuffBubbleComplete ? ' fox-chat-stuff-bubble--complete' : ''}${shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                      >
                      <div className="inline-survey-content fox-chat-survey-inner">
                        <div className="survey-checkbox-group">
                          {tiles.map((brand) => {
                            const isSelected = answers.brands.some((b) => b.id === brand.id);
                            const isDisabled =
                              !isSelected && answers.brands.length >= BRAND_PICK_MAX;
                            return (
                              <button
                                key={brand.id}
                                type="button"
                                className={`survey-checkbox-option brand-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                style={{ backgroundImage: `url('${brand.imageUrl}')` }}
                                onClick={() => {
                                  if (isSelected) {
                                    setAnswers((prev) => ({ ...prev, brands: prev.brands.filter((b) => b.id !== brand.id) }));
                                  } else if (answers.brands.length < BRAND_PICK_MAX) {
                                    setAnswers((prev) => ({ ...prev, brands: [...prev.brands, brand] }));
                                  }
                                }}
                                disabled={isDisabled}
                                aria-label={brand.name}
                              />
                            );
                          })}
                          {isLatestBrandTiles && (
                            <button
                              type="button"
                              className={`survey-checkbox-option survey-add-custom${moreOptionsLoading === 'brand' || brandSearchLoading ? ' fox-chat-more-loading' : ''}${answers.brands.length >= BRAND_PICK_MAX || (brandSearchHasText && !brandSearchQuery) ? ' disabled' : ''}`}
                              onClick={handleMoreBrandOptions}
                              disabled={moreOptionsLoading === 'brand' || brandSearchLoading || answers.brands.length >= BRAND_PICK_MAX || (brandSearchHasText && !brandSearchQuery)}
                              aria-busy={moreOptionsLoading === 'brand' || brandSearchLoading}
                            >
                              {moreOptionsLoading === 'brand' || brandSearchLoading ? (
                                <span className="fox-chat-more-spinner" aria-hidden="true" />
                              ) : 'MORE ITEMS'}
                            </button>
                          )}
                          {isLatestBrandTiles && (
                            <div className="brand-search-container">
                              <FoxChatSurveySearchField
                                key={brandSearchFieldKey}
                                refreshMode={!!brandSearchQuery && brandSearchResults.length > 0}
                                refreshing={brandSearchLoading}
                                onRefresh={refreshBrandSearchResults}
                                onHasTextChange={setBrandSearchHasText}
                                onInputChange={() => {
                                  clearSurveySearchCustomAdded('brand');
                                  setBrandSearchQuery('');
                                }}
                                placeholder="Search for any item..."
                                ariaLabel="Search for any item"
                                submitDisabled={brandSearchLoading || (!!brandSearchQuery && brandSearchResults.length > 0 && !brandSearchHasMore)}
                                disabled={answers.brands.length >= BRAND_PICK_MAX}
                                onSubmit={async (query) => {
                                  if (brandSearchLoading) return;
                                  surveySearchScrollTargetRef.current = msg.id;
                                  clearSurveySearchCustomAdded('brand');
                                  setBrandSearchQuery(query);
                                  setBrandSearchLoading(true);
                                  try {
                                    const results = await searchBrandImages(query, BRAND_SEARCH_FETCH_COUNT);
                                    const list = Array.isArray(results) ? results : [];
                                    const page = list.slice(0, SURVEY_SEARCH_PAGE);
                                    brandSearchPoolRef.current = list.slice(SURVEY_SEARCH_PAGE);
                                    setBrandSearchResults(page);
                                    setBrandSearchHasMore(brandSearchPoolRef.current.length > 0);
                                  } catch (err) {
                                    console.error('Brand search error:', err);
                                    setBrandSearchResults([]);
                                    setBrandSearchHasMore(false);
                                    brandSearchPoolRef.current = [];
                                  } finally {
                                    setBrandSearchLoading(false);
                                  }
                                }}
                              />
                              {brandSearchLoading && (
                                <div className="brand-search-loading">Searching the web...</div>
                              )}
                              {brandSearchResults.length > 0 && (
                                <div className="brand-search-results">
                                  {brandSearchResults.map((result, index) => {
                                    const isSelected = answers.brands.some(
                                      (b) => b.id === result.id,
                                    );
                                    const isDisabled =
                                      !isSelected && answers.brands.length >= BRAND_PICK_MAX;
                                    return (
                                      <button
                                        key={result.id || index}
                                        type="button"
                                        className={`survey-checkbox-option brand-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                        style={{
                                          backgroundImage: `url('${result.imageUrl}')`,
                                        }}
                                        onClick={() => {
                                          if (isSelected) {
                                            setAnswers((prev) => ({
                                              ...prev,
                                              brands: prev.brands.filter((b) => b.id !== result.id),
                                            }));
                                          } else if (answers.brands.length < BRAND_PICK_MAX) {
                                            const picked = {
                                              ...result,
                                              sourceImageUrl: result.sourceImageUrl || result.imageUrl,
                                            };
                                            setAnswers((prev) => ({ ...prev, brands: [...prev.brands, picked] }));
                                            addBrandToLatestSet(picked);
                                            setBrandSearchResults([]);
                                            setBrandSearchQuery('');
                                            setBrandSearchHasMore(false);
                                            brandSearchPoolRef.current = [];
                                            setBrandSearchFieldKey((k) => k + 1);
                                            markSurveySearchCustomAdded('brand');
                                            const brandId = picked.id;
                                            persistCustomSearchResultImage('swag', picked.sourceImageUrl, (stableUrl) => {
                                              setAnswers((prev) => ({
                                                ...prev,
                                                brands: prev.brands.map((b) => (
                                                  b.id === brandId
                                                    ? { ...b, imageUrl: stableUrl, sourceImageUrl: b.sourceImageUrl || picked.sourceImageUrl }
                                                    : b
                                                )),
                                              }));
                                              setMessages((prev) => prev.map((m) => {
                                                if (!m.brandTiles || !Array.isArray(m.brands)) return m;
                                                return {
                                                  ...m,
                                                  brands: m.brands.map((b) => (
                                                    b.id === brandId
                                                      ? { ...b, imageUrl: stableUrl, sourceImageUrl: b.sourceImageUrl || picked.sourceImageUrl }
                                                      : b
                                                  )),
                                                };
                                              }));
                                            });
                                          }
                                        }}
                                        disabled={isDisabled}
                                        aria-label={result.name}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                              {surveySearchCustomAdded === 'brand' && !brandSearchLoading && brandSearchResults.length === 0 && (
                                <div className="brand-search-loading fox-chat-search-custom-added" role="status">
                                  {customSearchAddedHint('brand', answers)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && isFoxDailyCheckGateMessage(msg) && showPreUnmaskGifChat) {
                  return (
                    <React.Fragment key={msg.id}>
                      <div className="messaging-bubble received">
                        <p className="messaging-bubble-text">{msg.text}</p>
                        <span className="messaging-bubble-meta">{formatTime(msg.timestamp)}</span>
                      </div>
                      {typeof onVerifyProfile === 'function' ? (
                        <div className="fox-chat-match-found-cta-wrap">
                          <button
                            type="button"
                            className="personality-chart-finder-gender-btn fox-chat-photo-btn fox-chat-match-found-btn"
                            onClick={onVerifyProfile}
                          >
                            {dailyCheckVerifyGateCtaText}
                          </button>
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                }
                if (!isSent && isFoxDailyCheckComputingReplyMessage(msg) && showPreUnmaskGifChat) {
                  return (
                    <React.Fragment key={msg.id}>
                      <div className="fox-chat-computing-reply-icon-row" aria-hidden="true">
                        <img src={algoIcon} alt="" className="fox-chat-computing-reply-icon" />
                      </div>
                      <div className="messaging-bubble received">
                        <p className="messaging-bubble-text" aria-label={msg.text}>
                          <AnimatedTrailingDots text={msg.text} />
                        </p>
                        <span className="messaging-bubble-meta">{formatTime(msg.timestamp)}</span>
                      </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && isFoxDailyCheckMatchFoundMessage(msg) && showPreUnmaskGifChat) {
                  return (
                    <React.Fragment key={msg.id}>
                      <div className="messaging-bubble received">
                        <p className="messaging-bubble-text">{msg.text}</p>
                        <span className="messaging-bubble-meta">{formatTime(msg.timestamp)}</span>
                      </div>
                      <div className="fox-chat-match-found-cta-wrap">
                        <button
                          type="button"
                          className="personality-chart-finder-gender-btn fox-chat-photo-btn fox-chat-match-found-btn"
                          onClick={() => onOpenMessages?.(msg.matchConversationId || null)}
                        >
                          {dailyCheckMatchFoundCtaText}
                        </button>
                      </div>
                    </React.Fragment>
                  );
                }
                if (!isSent && isFoxDailyCheckOpeningMessage(msg) && showPreUnmaskGifChat) {
                  return (
                    <React.Fragment key={msg.id}>
                      <div className="fox-chat-streak-carousel-wrap">
                        <p className="fox-chat-streak-month-label">{streakCarouselMonthLabel}</p>
                        <div
                          className="fox-chat-streak-carousel"
                          ref={streakCarouselRef}
                          role="list"
                          aria-label={`Daily vibe checks in ${streakCarouselMonthLabel}`}
                        >
                        {streakCarouselDays.map(({ day, periodKey }) => {
                          const dayGif = monthVibeGifs[periodKey];
                          return (
                            <div
                              key={periodKey}
                              className={`fox-chat-streak-day${dayGif ? ' fox-chat-streak-day--filled' : ''}`}
                              role="listitem"
                              aria-label={dayGif ? `Day ${day}: vibe check sent` : `Day ${day}: no vibe check`}
                            >
                              {dayGif ? (
                                <img
                                  src={dayGif.stillUrl || dayGif.gifUrl}
                                  alt=""
                                  className="fox-chat-streak-day-gif"
                                  loading="lazy"
                                />
                              ) : (
                                <>
                                  <span className="fox-chat-streak-day-month">
                                    {streakCarouselMonthShort}
                                  </span>
                                  <span className="fox-chat-streak-day-num">{day}</span>
                                </>
                              )}
                            </div>
                          );
                        })}
                        </div>
                        <p className="messaging-pre-unmask-hint fox-chat-daily-check-streak">
                          {dailyCheckSubtitleWithStreak}
                        </p>
                        <p className="fox-chat-daily-check-promo" role="status">
                          {dailyCheckSubtitleText}
                        </p>
                      </div>
                      <div className="messaging-bubble received">
                        <p className="messaging-bubble-text">{msg.text}</p>
                        <span className="messaging-bubble-meta">{formatTime(msg.timestamp)}</span>
                      </div>
                    </React.Fragment>
                  );
                }
                /* Once its question is answered (its survey step is behind the first incomplete
                   one, or the whole survey is done), grey out the intro prompt bubble so the
                   currently-active question stands out. */
                const surveyPromptComplete =
                  msg.surveyPromptBubble
                  && typeof msg.surveyPromptStep === 'number'
                  && (firstIncompleteFoxSurveyStepIndex === null
                    || msg.surveyPromptStep < firstIncompleteFoxSurveyStepIndex);
                return (
                  <div
                    key={msg.id}
                    className={`messaging-bubble ${isSent ? 'sent' : 'received'}${msg.surveyPromptBubble ? ' fox-chat-text-bubble' : ''}${surveyPromptComplete ? ' fox-chat-text-bubble--complete' : ''}${msg.surveyPromptBubble && shakeMessageId === msg.id ? ' fox-chat-bubble-shake' : ''}`}
                  >
                    {msg.gifUrl ? (
                      <img src={msg.gifUrl} alt={msg.text || 'GIF'} className="messaging-bubble-gif" />
                    ) : (
                      renderFoxBubbleText(msg)
                    )}
                    {!msg.hideTimestamp && (
                      <span className="messaging-bubble-meta">{formatTime(msg.timestamp)}</span>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            {tilePickHint && !showPreUnmaskGifChat && (
              <div className="fox-chat-tile-hint" role="status">
                {tilePickHint}
              </div>
            )}
            {showPreUnmaskGifChat && !dailyCheckGifSent ? (
              <div className="messaging-gif-picker-panel">
                <div className="messaging-gif-grid-scroll" ref={chatGifGridScrollRef}>
                  <div className="heart-gif-grid">
                    {(loadingChatGifs && chatGifs.length === 0
                      ? Array.from({ length: 9 }, (_, i) => ({ id: `ph-${i}`, placeholder: true }))
                      : chatGifs
                    ).map((gif) => {
                      if (gif.placeholder) {
                        return <div key={gif.id} className="heart-gif-tile heart-gif-placeholder" />;
                      }
                      return (
                        <button
                          key={gif.id}
                          type="button"
                          className="heart-gif-tile"
                          disabled={sendingFoxGif}
                          onClick={async () => {
                            const gifUrl = gif.fullUrl || gif.gifUrl;
                            if (!gifUrl) return;
                            setSendingFoxGif(true);
                            const sentAt = Date.now();
                            setMessages((prev) => [
                              ...prev,
                              {
                                id: `me-gif-${sentAt}`,
                                sender: 'me',
                                gifUrl,
                                timestamp: new Date(sentAt),
                              },
                              buildFoxDailyCheckComputingReplyMessage(
                                messageIdPrefix,
                                dailyCheckComputingReplyText,
                                new Date(sentAt + 1000),
                              ),
                            ]);
                            try {
                              if (currentUser?.uid) {
                                writeDailyVibeChatCache(currentUser.uid, {
                                  gifUrl,
                                  gifId: gif.id || null,
                                  timestamp: sentAt,
                                });
                                const saved = await saveDailyVibeCheck(db, currentUser.uid, gif);
                                dailyVibeHydratedPeriodRef.current = getDailyVibePeriodKey();
                                const periodKey = saved?.periodKey ?? getDailyVibePeriodKey();
                                setMonthVibeGifs((prev) => ({
                                  ...prev,
                                  [periodKey]: { gifUrl, stillUrl: gif.stillUrl || null },
                                }));
                                if (typeof saved?.newStreak === 'number' && Number.isFinite(saved.newStreak)) {
                                  setLocalVibeCheckStreak(saved.newStreak);
                                  setLocalVibeCheckStreakLastPeriodKey(periodKey);
                                }
                                onDailyVibeCheckCompleted?.({
                                  newStreak: saved?.newStreak ?? null,
                                  periodKey,
                                });
                                try {
                                  await shareDailyVibeCheckGifWithPartners(currentUser, gifUrl);
                                } catch (shareErr) {
                                  console.warn('Daily vibe GIF partner share failed:', shareErr);
                                }
                              }
                            } catch (err) {
                              console.warn('Daily vibe check save failed:', err);
                            } finally {
                              setSendingFoxGif(false);
                            }
                          }}
                        >
                          <span className="heart-gif-spinner" />
                          <img
                            src={gif.stillUrl || gif.gifUrl}
                            alt={gif.title}
                            className="heart-gif-tile-img"
                            loading="lazy"
                            onLoad={(e) => {
                              e.target.parentElement.querySelector('.heart-gif-spinner')?.classList.add('hidden');
                              if (gif.stillUrl && e.target.src !== gif.gifUrl) {
                                e.target.src = gif.gifUrl;
                              }
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="heart-gif-search-wrap">
                  <input
                    type="text"
                    className="heart-gif-search-input"
                    placeholder="Search KLIPY GIFs..."
                    value={chatGifSearch}
                    onChange={(e) => setChatGifSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && chatGifSearch.trim()) {
                        appendKlipyGifs(chatGifSearch.trim(), true);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="heart-gif-search-btn"
                    onClick={() => {
                      if (chatGifSearch.trim()) appendKlipyGifs(chatGifSearch.trim(), true);
                    }}
                    disabled={!chatGifSearch.trim() || loadingChatGifs}
                  >
                    SEARCH
                  </button>
                </div>
              </div>
            ) : null}
            {messageInputEnabled && (
              <form className="messaging-input-bar fox-chat-input-bar" onSubmit={handleSend}>
                <div className="fox-chat-input-wrap">
                  <input
                    ref={inputRef}
                    type="text"
                    className="messaging-input fox-chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a message..."
                  />
                  <button
                    type="submit"
                    className="messaging-send fox-chat-send"
                    disabled={!input.trim()}
                    aria-label="Send"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 19V5" />
                      <path d="M5 12l7-7 7 7" />
                    </svg>
                  </button>
                </div>
              </form>
            )}
            {!showPreUnmaskGifChat && (answers.brands.length > 0 ||
              answers.activities.length > 0 ||
              answers.places.length > 0 ||
              !!answers.spirit_gif ||
              !!posterFields.posterNickname ||
              !!posterFields.selfGender ||
              posterFields.romanticLuv ||
              posterFields.friendLuv ||
              posterFields.lookingForGenders.length > 0 ||
              posterFields.selfAge !== null ||
              !!answers.user_photo) && (
              <div className="fox-chat-selected-bar" aria-label={selectedBarAriaLabel}>
                {(() => {
                  /* Collect chips in chronological (survey) order, then reverse so the
                     most-recently-selected chip renders first (leftmost). The carousel rests at
                     its left edge, so new selections always appear on the left without scrolling. */
                  const chips = [];
                  answers.brands.forEach((brand) => {
                    chips.push(
                      <button
                        key={`selected-brand-${brand.id}`}
                        type="button"
                        className="fox-chat-selected-chip"
                        onClick={() =>
                          setAnswers((prev) => ({ ...prev, brands: prev.brands.filter((b) => b.id !== brand.id) }))
                        }
                        aria-label={`Remove ${brand.name}`}
                        title={brand.name}
                      >
                        <img
                          src={brand.imageUrl}
                          alt=""
                          className="fox-chat-selected-chip-img"
                          aria-hidden
                          onError={fallbackToSourceImage(brand)}
                        />
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  });
                  answers.activities.forEach((emoji) => {
                    chips.push(
                      <button
                        key={`selected-emoji-${emoji}`}
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-emoji"
                        onClick={() =>
                          setAnswers((prev) => ({ ...prev, activities: prev.activities.filter((e) => e !== emoji) }))
                        }
                        aria-label={`Remove ${emoji}`}
                        title={emoji}
                      >
                        <span className="fox-chat-selected-chip-emoji-glyph" aria-hidden>
                          {emoji}
                        </span>
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  });
                  answers.places.forEach((place) => {
                    chips.push(
                      <button
                        key={`selected-place-${place.id}`}
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-place"
                        onClick={() =>
                          setAnswers((prev) => ({ ...prev, places: prev.places.filter((p) => p.id !== place.id) }))
                        }
                        aria-label={`Remove ${place.name}`}
                        title={place.name}
                      >
                        <img
                          src={place.imageUrl}
                          alt=""
                          className="fox-chat-selected-chip-img fox-chat-selected-chip-img-cover"
                          aria-hidden
                          onError={fallbackToSourceImage(place)}
                        />
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  });
                  if (answers.spirit_gif) {
                    chips.push(
                      <button
                        key={`selected-gif-${answers.spirit_gif.id}`}
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-place"
                        onClick={() => setAnswers((prev) => ({ ...prev, spirit_gif: null, vibe_photo: null }))}
                        aria-label="Remove GIF"
                        title={answers.spirit_gif.title || 'GIF'}
                      >
                        <img
                          src={
                            answers.spirit_gif.stillUrl ||
                            answers.spirit_gif.gifUrl ||
                            answers.spirit_gif.fullUrl ||
                            ''
                          }
                          alt=""
                          className="fox-chat-selected-chip-img fox-chat-selected-chip-img-cover"
                          aria-hidden
                        />
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  }
                  if (posterFields.posterNickname) {
                    chips.push(
                      <button
                        key={`selected-nickname-${posterFields.posterNickname}`}
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-nickname"
                        onClick={() => {
                          setPosterFields((prev) => ({ ...prev, posterNickname: null }));
                          setNicknameFieldKey((k) => k + 1);
                        }}
                        aria-label={`Remove ${posterFields.posterNickname}`}
                        title={posterFields.posterNickname}
                      >
                        <span className="fox-chat-selected-chip-nickname-text" aria-hidden>
                          {`"${posterFields.posterNickname}"`}
                        </span>
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  }
                  if (posterFields.selfGender) {
                    const opt = GENDER_OPTIONS.find((g) => g.id === posterFields.selfGender);
                    const label = opt ? opt.label : posterFields.selfGender;
                    chips.push(
                      <button
                        key={`selected-gender-${posterFields.selfGender}`}
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-gender"
                        onClick={() => setPosterFields((prev) => ({ ...prev, selfGender: null }))}
                        aria-label={`Remove ${label}`}
                        title={label}
                      >
                        <span className="fox-chat-selected-chip-gender-text" aria-hidden>
                          {label}
                        </span>
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  }
                  if (posterFields.selfAge !== null) {
                    chips.push(
                      <button
                        key={`selected-age-${posterFields.selfAge}`}
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-age"
                        onClick={() => {
                          setPosterFields((prev) => ({ ...prev, selfAge: null }));
                          setAgeInput('');
                        }}
                        aria-label={`Remove age ${posterFields.selfAge}`}
                        title={`Age ${posterFields.selfAge}`}
                      >
                        <span className="fox-chat-selected-chip-gender-text" aria-hidden>
                          {`AGE ${posterFields.selfAge}`}
                        </span>
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  }
                  if (posterFields.friendLuv) {
                    chips.push(
                      <button
                        key="selected-luv-friend"
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-luv"
                        onClick={() => setPosterFields((prev) => ({ ...prev, friendLuv: false }))}
                        aria-label="Remove FRIENDS"
                        title="FRIENDS"
                      >
                        <img
                          src={friendsImage}
                          alt=""
                          className="fox-chat-selected-chip-img"
                          aria-hidden
                        />
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  }
                  posterFields.lookingForGenders.forEach((g) => {
                    const opt = GENDER_OPTIONS.find((o) => o.id === g);
                    const label = opt ? opt.label : g;
                    /* Compact chip label: MALE→M, FEMALE→F, NB stays NB. Keep aria/title using the
                       full label for accessibility. */
                    const shortLabel = g === 'male' ? 'M' : g === 'female' ? 'F' : label;
                    chips.push(
                      <button
                        key={`selected-romantic-looking-${g}`}
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-romantic-looking"
                        onClick={() =>
                          setPosterFields((prev) => ({
                            ...prev,
                            lookingForGenders: prev.lookingForGenders.filter((x) => x !== g),
                          }))
                        }
                        aria-label={`Remove romantic ${label}`}
                        title={`Romantic ${label}`}
                      >
                        <img
                          src={orheartImage}
                          alt=""
                          className="fox-chat-selected-chip-icon-prefix"
                          aria-hidden
                        />
                        <span className="fox-chat-selected-chip-gender-text" aria-hidden>
                          {shortLabel}
                        </span>
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  });
                  if (answers.user_photo) {
                    chips.push(
                      <button
                        key="selected-photo"
                        type="button"
                        className="fox-chat-selected-chip fox-chat-selected-chip-photo"
                        onClick={handlePhotoRetry}
                        aria-label={photoChipAriaLabel}
                        title={photoChipTitle}
                      >
                        <img
                          src={answers.user_photo}
                          alt=""
                          className="fox-chat-selected-chip-img fox-chat-selected-chip-img-cover"
                          aria-hidden
                        />
                        <span className="fox-chat-selected-chip-x" aria-hidden>×</span>
                      </button>,
                    );
                  }
                  return chips.reverse();
                })()}
              </div>
            )}
        </FoxChatPanelShell>
      )}

      {opsSendToOpsFlow && opsSendModalOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="feed-tile-detail-overlay feed-tile-detail-overlay--feed"
          onClick={closeOpsSendModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="fox-chat-ops-send-title"
        >
          <div
            className="feed-tile-detail-popup feed-tile-detail-popup--feed"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="feed-tile-detail-close" onClick={closeOpsSendModal} aria-label="Close">
              ×
            </button>
            <div className="shared-poster-apply-content fox-chat-ops-send-content">
              {opsSendModalPhase === 'email' ? (
                <>
                  <button
                    type="button"
                    className="feed-tile-detail-back"
                    onClick={() => {
                      setOpsSendModalPhase('menu');
                      setOpsReferEmail('');
                      setOpsReferEmailError('');
                      setOpsReferEmailSubmitted(false);
                    }}
                    aria-label="Back"
                  >
                    ‹ Back
                  </button>
                  <h2 id="fox-chat-ops-send-title" className="feed-tile-detail-title">
                    Enter your op&apos;s email
                  </h2>
                  <form
                    className="feed-tile-detail-apply-form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const email = (opsReferEmail || '').trim().toLowerCase();
                      const emailErr = referFriendEmailValidationError(email);
                      if (emailErr) {
                        setOpsReferEmailError(emailErr);
                        return;
                      }
                      setOpsReferEmailError('');
                      const referPosterId = savedPosterId;
                      try {
                        await addDoc(collection(db, 'referSubmissions'), {
                          posterId: referPosterId || null,
                          friendEmail: email,
                          createdAt: serverTimestamp(),
                          userId: currentUser?.uid || null,
                          source: 'foxchat_ops',
                        });
                        if (currentUser?.uid && referPosterId) {
                          try {
                            await appendCollageForFriendContact(db, currentUser.uid, referPosterId, { friendEmail: email });
                          } catch (cfErr) {
                            console.error('collageForFriend email append (foxchat_ops):', cfErr);
                          }
                        }
                        await persistOpsCollageShare({ channel: 'email', friendEmail: email });
                        setOpsReferEmailSubmitted(true);
                      } catch (err) {
                        console.error('Error saving ops refer submission:', err);
                        setOpsReferEmailError('Something went wrong. Please try again.');
                      }
                    }}
                  >
                    <input
                      type="email"
                      className="feed-tile-detail-apply-input"
                      placeholder="theirname@example.com"
                      value={opsReferEmail}
                      onChange={(e) => {
                        setOpsReferEmail(e.target.value);
                        setOpsReferEmailError('');
                      }}
                      aria-label="Op's email"
                      autoComplete="email"
                      disabled={opsReferEmailSubmitted}
                    />
                    {opsReferEmailError && (
                      <p className="feed-tile-detail-apply-error" role="alert">
                        {opsReferEmailError}
                      </p>
                    )}
                    <button type="submit" className="feed-tile-detail-btn feed-tile-detail-btn-apply" disabled={opsReferEmailSubmitted}>
                      {opsReferEmailSubmitted ? 'Received!' : 'Send'}
                    </button>
                  </form>
                  {opsReferEmailSubmitted && <p className="feed-tile-detail-thank-you">We&apos;ll send them an invite!</p>}
                </>
              ) : opsSendModalPhase === 'ig' ? (
                <>
                  <button
                    type="button"
                    className="feed-tile-detail-back"
                    onClick={() => {
                      setOpsSendModalPhase('menu');
                      setOpsReferIg('');
                      setOpsReferIgError('');
                      setOpsReferIgSubmitted(false);
                    }}
                    aria-label="Back"
                  >
                    ‹ Back
                  </button>
                  <h2 id="fox-chat-ops-send-title" className="feed-tile-detail-title">
                    Enter their IG handle
                  </h2>
                  <form
                    className="feed-tile-detail-apply-form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const handle = (opsReferIg || '').trim().replace(/^@/, '');
                      if (!handle) {
                        setOpsReferIgError('Please enter an IG handle.');
                        return;
                      }
                      setOpsReferIgError('');
                      const referPosterId = savedPosterId;
                      try {
                        await addDoc(collection(db, 'referSubmissions'), {
                          posterId: referPosterId || null,
                          friendIgHandle: handle,
                          createdAt: serverTimestamp(),
                          userId: currentUser?.uid || null,
                          source: 'foxchat_ops',
                        });
                        if (currentUser?.uid && referPosterId) {
                          try {
                            await appendCollageForFriendContact(db, currentUser.uid, referPosterId, { friendIgHandle: handle });
                          } catch (cfErr) {
                            console.error('collageForFriend IG append (foxchat_ops):', cfErr);
                          }
                        }
                        await persistOpsCollageShare({ channel: 'ig', friendIgHandle: handle });
                        setOpsReferIgSubmitted(true);
                      } catch (err) {
                        console.error('Error saving ops IG submission:', err);
                        setOpsReferIgError('Something went wrong. Please try again.');
                      }
                    }}
                  >
                    <input
                      type="text"
                      className="feed-tile-detail-apply-input"
                      placeholder="@username"
                      value={opsReferIg}
                      onChange={(e) => {
                        setOpsReferIg(e.target.value);
                        setOpsReferIgError('');
                      }}
                      aria-label="IG handle"
                      autoComplete="off"
                      disabled={opsReferIgSubmitted}
                    />
                    {opsReferIgError && (
                      <p className="feed-tile-detail-apply-error" role="alert">
                        {opsReferIgError}
                      </p>
                    )}
                    <button type="submit" className="feed-tile-detail-btn feed-tile-detail-btn-apply" disabled={opsReferIgSubmitted}>
                      {opsReferIgSubmitted ? 'Received!' : 'Send'}
                    </button>
                  </form>
                  {opsReferIgSubmitted && <p className="feed-tile-detail-thank-you">We&apos;ll reach out to them!</p>}
                </>
              ) : (
                <>
                  <h2 id="fox-chat-ops-send-title" className="feed-tile-detail-title">
                    SEND TO YOUR OPPS
                  </h2>
                  <p className="feed-tile-detail-apply-prompt fox-chat-ops-send-menu-prompt">
                    Pick how you want to reach them. We&apos;ll send them an invite to join.
                  </p>
                  <div className="shared-poster-refer-options">
                    <button
                      type="button"
                      className="feed-tile-detail-btn feed-tile-detail-btn-apply"
                      onClick={() => setOpsSendModalPhase('email')}
                    >
                      ENTER YOUR OPPS EMAIL
                    </button>
                    <button
                      type="button"
                      className="feed-tile-detail-btn feed-tile-detail-btn-ig"
                      onClick={() => setOpsSendModalPhase('ig')}
                    >
                      ENTER THEIR IG HANDLE
                    </button>
                    <button
                      type="button"
                      className="feed-tile-detail-btn feed-tile-detail-btn-refer"
                      onClick={async () => {
                        const referPosterId = savedPosterId;
                        if (!referPosterId) {
                          window.alert('Save your collage first to get a shareable link.');
                          return;
                        }
                        const link = `${window.location.origin}/poster/${referPosterId}`;
                        const copied = await copyTextToClipboard(link);
                        if (!copied) window.alert(`Copy this link: ${link}`);
                        else await persistOpsCollageShare({ channel: 'link_copy' });
                        setOpsShareLinkCopied(true);
                        window.setTimeout(() => setOpsShareLinkCopied(false), 2000);
                      }}
                    >
                      {opsShareLinkCopied ? 'Link copied!' : 'Share link'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

    </>
  );
};

function FoxChatPanelShell({
  embeddedInInbox, onClose, overlayBehindModal = false, panelRef, onPanelWheel, panelClassName, children,
}) {
  const panel = (
    <div
      ref={panelRef}
      className={panelClassName}
      tabIndex={-1}
      onClick={(e) => e.stopPropagation()}
      onWheel={onPanelWheel}
    >
      {children}
    </div>
  );
  if (embeddedInInbox) return panel;
  /* This overlay is portaled to document.body, so it lives in the root stacking context (above
     `.App`, which is z-index:2). Popups rendered inside `.App` (sign-up, school-email confirm)
     can't escape that stacking context. While one is open, drop this overlay below `.App`. */
  const overlay = (
    <div
      className={`messaging-overlay fox-chat-overlay${overlayBehindModal ? ' fox-chat-overlay--behind-modal' : ''}`}
      onClick={onClose}
    >
      {panel}
    </div>
  );
  if (typeof document !== 'undefined') {
    return createPortal(overlay, document.body);
  }
  return overlay;
}

export default FoxChat;
